import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Sparkles, Calendar as CalIcon, FileText, ClipboardCheck, Cable, Loader2, Copy, History, GitBranch, Mail, Download, UserCheck, Bell, BellOff, Send, Plus, Trash2, Expand, X, Headphones, ShieldCheck, AlertTriangle, MessageSquare, CheckCircle2, Wand2, Save, Calculator, Search, Euro, ChevronDown, ChevronUp, Lock, FilePenLine, User, XCircle, Clock, PhoneCall } from "lucide-react";
import api, { getUser, STATUS_LABELS, STATUS_COLORS, ONBOARDING_STAGE_LABELS, ONBOARDING_STAGE_COLORS, statusLabelForLead, downloadAuthedFile } from "@/lib/api";
import { canAccess, useSectionPermissions } from "@/lib/permissions";
import { parseLocalIso } from "@/lib/dt";
import { resolveOwnerPhase } from "@/lib/leadOwner";
import LeadTimeline from "@/components/LeadTimeline";
import LeadStatusHistory from "@/components/LeadStatusHistory";
import RTAnalysisPanel from "@/components/RTAnalysisPanel";
import PtFormalityHint from "@/components/PtFormalityHint";
import RTNotesBlock from "@/components/RTNotesBlock";
import RTSummaryPanel from "@/components/RTSummaryPanel";
import FollowUpForm from "@/components/FollowUpForm";
import FirstResponseModal from "@/components/FirstResponseModal";
import EstimateSimulator from "@/components/EstimateSimulator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
// Note (28/02/2026): TQAPanel foi removido. O fluxo TSC/TQA foi consolidado
// na Direção Técnica (DT), único aprovador do Plano de Cuidados via
// CarePlanBlocksEditor + portal público de token.
import ContractDataView from "@/components/ContractDataView";
import DepositPlanCard from "@/components/DepositPlanCard";
import RecruitmentPanel from "@/components/RecruitmentPanel";
import EnquadramentoPanel from "@/components/EnquadramentoPanel";
import { CallControls } from "@/components/CallControls";
// Note (28/02/2026): CarePlanApprovals.jsx foi removido — o painel de
// aprovações vive dentro do CarePlanBlocksEditor (apenas passo DT).
import CarePlanBlocksEditor from "@/components/CarePlanBlocksEditor";
import DTReviewLinkCard from "@/components/DTReviewLinkCard";
import FollowUpVisitPlanBlock from "@/components/FollowUpVisitPlanBlock";
import ZohoNotesBlock from "@/components/leadDetail/ZohoNotesBlock";
import CalendarFab from "@/components/leadDetail/CalendarFab";
import RTSummaryFab from "@/components/leadDetail/RTSummaryFab";
import PontualPricingBlock from "@/components/leadDetail/PontualPricingBlock";
import PontualCarePlanBlock from "@/components/leadDetail/PontualCarePlanBlock";
import CloseAtendimentoBlock from "@/components/leadDetail/CloseAtendimentoBlock";
import BonusCreditBlock from "@/components/leadDetail/BonusCreditBlock";
import NotesSection from "@/components/leadDetail/NotesSection";
import NotesTimeline from "@/components/leadDetail/NotesTimeline";
import LinkusCallsTimeline from "@/components/leadDetail/LinkusCallsTimeline";
import EstimatePreparerBadge from "@/components/leadDetail/EstimatePreparerBadge";

const Section = ({ title, children, action }) => (
  <div className="bg-card border border-border rounded-2xl p-4 lg:p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-ui text-base">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Kv = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</div>
    <div className="text-sm mt-1">{value || "—"}</div>
  </div>
);

// 25/02/2026 — Janela de 60 minutos para o painel "Desfecho da RT".
// Pedido do utilizador: o bloco "Desfecho da RT" deve aparecer só
// enquanto o Consultor está a fechar a RT (60 min após termino) e
// depois desaparece sozinho para não ocupar espaço na página.
// 05/02/2026 — Botão "Resumo RT" no toolbar força a abertura do painel
// mesmo depois de a janela de 60 min ter fechado (via prop ``force``).
// Wrapper isolado para evitar hooks-in-IIFE dentro de LeadDetail.
const RT_SUMMARY_WINDOW_MINUTES = 60;
function RTSummaryWindow({ lead, onSaved, force = false }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);
  const completedAt = lead?.technical?.completed_at;
  if (!completedAt) return null;
  if (!force) {
    const ageMin = (Date.now() - new Date(completedAt).getTime()) / 60000;
    if (ageMin > RT_SUMMARY_WINDOW_MINUTES) return null;
  }
  return <RTSummaryPanel lead={lead} onSaved={onSaved} />;
}



// 25/02/2026 — Configuração das transições manuais do funil (b1 do redesign
// do funil comercial): botões "Marcar Perdido na Estimativa", "Marcar
// Proposta Perdida", "Marcar Negócio Ganho" e "Marcar Negócio Perdido".
// Cada acção só aparece quando o status actual do lead o permite (evita
// saltar do princípio para o fim ou regredir estados). Os presets de
// motivo são apenas sugestões — o utilizador pode escrever livremente.
const STATUS_CHANGE_ACTIONS = [
  {
    key: "perdido_estimativa",
    label: "Marcar Perdido na Estimativa",
    tone: "danger",
    visibleWhen: (s) => [
      "estimativa_pronta", "estimativa_enviada",
      "qt_agendada", "qt_realizada", "qt_nao_realizada",
      "proposta_pronta",
    ].includes(s),
    presets: [
      "Preço demasiado alto",
      "Cliente escolheu outra empresa",
      "Cliente desistiu do serviço",
      "Sem resposta prolongada",
      "Situação clínica alterou-se",
    ],
  },
  {
    key: "proposta_perdida",
    label: "Marcar Proposta Perdida",
    tone: "danger",
    visibleWhen: (s) => ["proposta_enviada"].includes(s),
    presets: [
      "Sem resposta após envio",
      "Cliente adiou decisão indefinidamente",
      "Cliente escolheu outra empresa",
      "Cliente resolveu a situação",
      "Preço acima do orçamento do cliente",
    ],
  },
  {
    key: "negocio_ganho",
    label: "Marcar Negócio Ganho",
    tone: "success",
    visibleWhen: (s) => ["aceite", "proposta_enviada"].includes(s),
    presets: [
      "Caução mínima paga (arranca recrutamento)",
      "Caução total paga",
      "Assinatura formal",
      "Confirmação verbal firme",
    ],
  },
  {
    key: "negocio_perdido",
    label: "Marcar Negócio Perdido",
    tone: "danger",
    visibleWhen: (s) => [
      "aceite", "proposta_enviada", "proposta_pronta",
      "estimativa_enviada", "estimativa_pronta",
      "qt_realizada", "qt_agendada",
      "qualificado", "contactado", "tentativa_contacto",
      "qualificacao_inicial",
    ].includes(s),
    presets: [
      "Sem resposta após aceite",
      "Cliente cancelou após aceite",
      "Problema com o pagamento da caução",
      "Cliente escolheu outra opção",
      "Cliente desistiu",
    ],
  },
];


// 25/02/2026 — Devolve N próximos dias úteis (Seg–Sex) em formato ISO
// ``YYYY-MM-DD`` a começar em HOJE. Usa a data LOCAL (fuso do browser) para
// não sofrer o off-by-one de ``toISOString`` quando é madrugada em Lisboa.
// Sábados e Domingos são excluídos. Usada pelo painel "Slots livres em
// Outlook" da RT para mostrar sempre a semana à frente e nunca dias
// passados.
const getNextBusinessDays = (count = 5) => {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (out.length < count) {
    const dow = d.getDay(); // 0 = Sun, 6 = Sat
    if (dow !== 0 && dow !== 6) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      out.push(`${y}-${m}-${day}`);
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
};

// Human-readable relative time — "há 5min", "há 2h", "ontem", "há 3 dias".
// Falls back to a short PT locale date after 30 days.
const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "";
  const diff = Math.floor((Date.now() - then.getTime()) / 1000); // seconds
  if (diff < 60) return "agora mesmo";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `há ${h} h`;
  }
  if (diff < 172800) return "ontem";
  if (diff < 2592000) return `há ${Math.floor(diff / 86400)} dias`;
  return then.toLocaleDateString("pt-PT");
};

// Bloco dedicado aos emails recebidos do cliente. Encapsula:
//   * A secção rica com contador, timestamps relativos e corpo do email.
//   * Um empty-state pró-activo quando o lead tem email de contacto mas
//     ainda nada foi sincronizado — botão "Buscar emails na caixa"
//     (`POST /inbox-sync/reconcile/:id`) força uma busca no Microsoft 365
//     nos últimos 90 dias ignorando ``isRead``, útil quando o email foi
//     lido no Outlook antes do sync ou o lead foi criado depois do email.
function InboundEmailsBlock({ lead, onReconciled }) {
  const [busy, setBusy] = useState(false);
  // 20/02/2026 — Collapsed by default so the sidebar/main flow doesn't
  // get pushed down by a long email thread. Clicking the header expands
  // the block; the count badge stays visible in both states.
  const [expanded, setExpanded] = useState(false);
  const emails = Array.isArray(lead?.inbound_emails) ? lead.inbound_emails : [];
  const hasEmails = emails.length > 0;
  const contactEmail = ((lead?.qualification || {}).contact || {}).email || "";
  const canReconcile = !!contactEmail;

  const reconcile = async () => {
    if (!canReconcile || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/inbox-sync/reconcile/${lead.id}?days_back=90`);
      const data = r.data || {};
      if (data.ok === false) {
        toast.error(data.message || "Falhou a reconciliação");
      } else if (data.attached > 0) {
        toast.success(data.message || `${data.attached} email(s) anexado(s)`);
        onReconciled?.();
      } else {
        toast.info(data.message || "Nada de novo encontrado na caixa");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erro a buscar emails");
    } finally {
      setBusy(false);
    }
  };

  // Empty state — the lead has never received any email through the sync.
  if (!hasEmails) {
    return (
      <div id="inbound-emails" data-testid="inbound-emails-empty" className="bg-card border border-dashed border-border rounded-2xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center flex-shrink-0">
          <Mail className="w-4 h-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-ui text-foreground">Emails recebidos do cliente</div>
          <div className="text-[11.5px] text-muted-foreground mt-0.5 leading-relaxed">
            {canReconcile ? (
              <>Nenhum email sincronizado ainda para <b>{contactEmail}</b>. Use o botão para procurar em <b>leads@caring.pt</b> (últimos 90 dias).</>
            ) : (
              <>Sem <b>email de contacto</b> definido — adicione-o em "Contacto" para sincronizar futuras mensagens.</>
            )}
          </div>
          {canReconcile && (
            <button
              onClick={reconcile}
              disabled={busy}
              data-testid="btn-reconcile-empty"
              className="mt-2 inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border text-foreground rounded-full px-3 py-1.5 font-semibold disabled:opacity-60"
            >
              <Mail className="w-3.5 h-3.5" /> {busy ? "A procurar…" : "Buscar emails na caixa"}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div id="inbound-emails" data-testid="inbound-emails-section" className="bg-card border-2 border-[hsl(var(--success))]/25 rounded-2xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="inbound-emails-toggle"
        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-[hsl(var(--success-bg))]/60 transition-colors"
        aria-expanded={expanded}
      >
        <div className="w-9 h-9 rounded-full bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 flex items-center justify-center flex-shrink-0">
          <Mail className="w-4 h-4 text-[hsl(var(--success))]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-ui text-sm text-foreground flex items-center gap-2">
            Emails recebidos do cliente
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-600 text-primary-foreground text-[10px] font-bold" data-testid="inbound-emails-count">
              {emails.length}
            </span>
          </h3>
          {!expanded && (
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
              {lead.last_inbound_email_at ? (<>Último há <b className="text-[hsl(var(--success))]">{relativeTime(lead.last_inbound_email_at)}</b></>) : "Sincronizado de leads@caring.pt"}
            </div>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>
      {expanded && (
      <div className="p-6 lg:p-8 pt-2 border-t border-emerald-100">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="text-[11px] text-muted-foreground">
          Sincronizado automaticamente de <b>leads@caring.pt</b> (Microsoft 365)
        </div>
        <div className="flex items-center gap-3">
          {lead.last_inbound_email_at && (
            <div className="text-[11px] text-muted-foreground" data-testid="inbound-emails-last-at">
              Último: <b className="text-[hsl(var(--success))]">{relativeTime(lead.last_inbound_email_at)}</b>
            </div>
          )}
          {canReconcile && (
            <button
              onClick={reconcile}
              disabled={busy}
              data-testid="btn-reconcile"
              className="inline-flex items-center gap-1.5 text-[11px] bg-muted hover:bg-muted border border-border text-foreground rounded-full px-3 py-1.5 font-semibold disabled:opacity-60"
              title="Procurar na caixa leads@caring.pt por emails do cliente nos últimos 90 dias (ignora emails já lidos)"
            >
              {busy ? "A procurar…" : "Buscar mais"}
            </button>
          )}
        </div>
      </div>
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {emails
          .slice()
          .sort((a, b) => new Date(b.received_at || b.created_at) - new Date(a.received_at || a.created_at))
          .map((em) => (
            <div key={em.id || em.graph_message_id} data-testid="inbound-email" className="border border-border rounded-lg p-4 bg-muted/40 hover:bg-muted/40 transition-colors">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 rounded-full px-2 py-0.5">
                    ↓ Recebido
                  </span>
                  {/* 20/02/2026 — Origin badge: green for the primary
                      leads inbox, slate for the SAC-QI callback replies. */}
                  {em.mailbox && (() => {
                    const isSacQi = em.mailbox.toLowerCase().includes("sac.qi");
                    return (
                      <span
                        data-testid="inbound-email-origin"
                        className={
                          "inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-2 py-0.5 border " +
                          (isSacQi
                            ? "bg-muted border-border text-foreground"
                            : "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25 text-[hsl(var(--success))]")
                        }
                        title={`Chegou pela caixa ${em.mailbox}`}
                      >
                        {isSacQi ? "SAC.QI" : "LEADS@"}
                      </span>
                    );
                  })()}
                  <span className="text-xs text-muted-foreground">
                    <b className="text-foreground">{em.author_name || em.author}</b>
                    {em.author_name && em.author && <> · <span className="text-muted-foreground">{em.author}</span></>}
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground" title={em.received_at ? new Date(em.received_at).toLocaleString("pt-PT") : ""}>
                  {em.received_at ? (
                    <><b className="text-[hsl(var(--success))]">{relativeTime(em.received_at)}</b> · {new Date(em.received_at).toLocaleString("pt-PT")}</>
                  ) : ""}
                </span>
              </div>
              <div className="text-sm font-semibold text-foreground mb-1.5" data-testid="inbound-email-subject">{em.subject}</div>
              <div className="text-[13px] text-foreground/85 whitespace-pre-wrap leading-relaxed" data-testid="inbound-email-body">
                {(em.body || "").slice(0, 2500)}
                {em.body && em.body.length > 2500 && <span className="text-muted-foreground"> …</span>}
              </div>
            </div>
          ))}
      </div>
      </div>
      )}
    </div>
  );
}


// Notas do Zoho CRM associadas ao lead. Fetch on-demand via
// ``GET /api/leads/:id/zoho-notes`` — mostra o histórico completo de
// notas registadas no Zoho, independentemente de terem sido criadas
// pela Caring Estimativas ou manualmente por outros utilizadores no
// próprio Zoho CRM. Read-only — para criar/editar notas continue a
// usar o fluxo Zoho normal.

/**
 * Banner mostrado no topo de uma tab quando o utilizador só tem
 * permissão "view" (ie: canViewSection sim, canEditSection não) para
 * a secção correspondente. Ajuda a explicar porque é que os botões
 * de edição/envio estão bloqueados sem ter que passar por trial &
 * error. Configurado em `/settings/permissions`.
 */
function SectionReadOnlyBanner({ section, label }) {
  return (
    <div
      data-testid={`readonly-banner-${section}`}
      className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2 mb-4"
    >
      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-muted-foreground/80 bg-card border border-border rounded-full px-2 py-0.5">
        Só leitura
      </span>
      <span>
        O teu role tem permissão apenas de <b>ver</b> a secção <b>{label}</b>.
        Botões de edição/envio ficam desactivados. Um admin pode ajustar em&nbsp;
        <a href="/settings/permissions" className="underline hover:no-underline">
          /settings/permissions
        </a>.
      </span>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pontualFocus = searchParams.get("focus") === "pontual-price";
  const [lead, setLead] = useState(null);
  const [managers, setManagers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState({ date: "", time: "", manager_id: "" });

  // 26/02/2026 v2 — Interceptor de saída para o modal First Response.
  // O `useBlocker` do react-router não funciona com BrowserRouter
  // (data-router only), por isso interceptamos manualmente:
  //   * `beforeunload` → cobre refresh e tab close (browser prompt nativo)
  //   * `document.click` capture → intercepta cliques em <Link>/<a> que
  //     abandonem a rota actual e força o modal antes da navegação
  const needsFirstResponse = !!lead
    && !lead.first_contact_at
    && (lead.status === "tentativa_contacto" || lead.status === "qualificacao_inicial")
    && ["atendimento", "consultor", "gestor", "admin"].includes(getUser()?.role);
  const [pendingNavigation, setPendingNavigation] = useState(null); // string href
  useEffect(() => {
    if (!needsFirstResponse) return;
    const beforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    const clickCapture = (ev) => {
      // Find the nearest anchor / Link ancestor of the click target.
      let el = ev.target;
      while (el && el !== document.body) {
        if (el.tagName === "A" && el.getAttribute("href")) break;
        el = el.parentElement;
      }
      if (!el || el === document.body) return;
      const href = el.getAttribute("href");
      // Ignore modal buttons / anchors that don't navigate away.
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      const currentPath = window.location.pathname;
      // If the link points to the same lead (tabs, anchors), let it proceed.
      if (href === currentPath || href === `${currentPath}${window.location.search}`) return;
      // Modal FirstResponse handles its own outcome buttons — do not block.
      if (el.closest("[data-testid='first-response-modal']")) return;
      ev.preventDefault();
      ev.stopPropagation();
      setPendingNavigation(href);
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", clickCapture, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", clickCapture, true);
    };
  }, [needsFirstResponse]);

  // 25/02/2026 — Pré-preencher o formulário de agendamento com os dados
  // actuais da RT (data, hora, consultor atribuído) para que o Gestor veja
  // imediatamente com quem está agendada em vez de aparecer só o
  // placeholder "Consultor de Cuidados…" — pedido do utilizador.
  useEffect(() => {
    if (!lead) return;
    setSchedule((prev) => {
      // Só preenche se ainda estiver "vazio" (evita sobrescrever edições
      // em curso pelo utilizador).
      const isEmpty = !prev.date && !prev.time && !prev.manager_id;
      if (!isEmpty) return prev;
      const d = (lead.scheduled_qt_at || "").slice(0, 10);
      const t = (lead.scheduled_qt_at || "").slice(11, 16);
      // 25/02/2026 — Bug fix: antes o default era
      // `assigned_consultor_id || assigned_manager_id`, o que fazia com
      // que os slots livres viessem do calendário do Consultor (SAC).
      // A Reunião Técnica é conduzida pelo **Gestor de Cliente**, por
      // isso o pre-select passa a usar apenas `assigned_manager_id`.
      // Se não houver Gestor atribuído, o utilizador escolhe
      // explicitamente na dropdown — melhor forçar a decisão do que
      // ler slots do calendário errado.
      const mid = lead.assigned_manager_id || "";
      return { date: d, time: t, manager_id: mid };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?.scheduled_qt_at, lead?.assigned_manager_id]);

  // Review tracker (18/02/2026): the Gestor must download all three
  // documents (proposal PDF, care-plan client PDF, care-plan internal PDF)
  // before we allow "Enviar Proposta ao Cliente". Prevents shipping a
  // still-messy draft. Keyed by proposal version so a regeneration
  // resets the tracker automatically.
  const proposalVersionKey = lead?.proposal?.current_version || "";
  const [reviewedDocs, setReviewedDocs] = useState({ version: proposalVersionKey, proposal: false });
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  // 25/02/2026 — Modal de mudança de estado manual (Perdido na Estimativa,
  // Proposta Perdida, Negócio Ganho/Perdido). `null` quando fechado; caso
  // contrário guarda a acção seleccionada (com presets de motivo).
  const [statusChangeAction, setStatusChangeAction] = useState(null);
  const [statusChangeReason, setStatusChangeReason] = useState("");
  const [statusChangeBusy, setStatusChangeBusy] = useState(false);
  // 25/02/2026 — Estado do modal "RT recusada" (novo gate antes de enviar
  // estimativa quando `technical_meeting_accepted === false`).
  const [rtRefusalModal, setRtRefusalModal] = useState(false);
  const [rtRefusalReason, setRtRefusalReason] = useState("");
  const [rtRefusalCreateFu, setRtRefusalCreateFu] = useState(true);
  const [rtRefusalBusy, setRtRefusalBusy] = useState(false);
  useEffect(() => {
    // Reset tracker whenever the proposal version changes (regeneration).
    if (proposalVersionKey && reviewedDocs.version !== proposalVersionKey) {
      setReviewedDocs({ version: proposalVersionKey, proposal: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalVersionKey]);
  // Só a Proposta Final precisa de ser descarregada antes do envio ao
  // cliente — os PDFs do plano legado foram descontinuados a favor dos
  // `.docx` do Módulo Plano de Cuidados (opt-in via checkbox de anexar).
  const allReviewed = !!reviewedDocs.proposal;
  // Anexar `.docx` do Plano de Cuidados (vista cliente) ao email da proposta.
  // Persistido em localStorage para preservar a última escolha do Gestor
  // entre carregamentos da página (não é config global — é uma preferência UX).
  const [attachCarePlanDocx, setAttachCarePlanDocx] = useState(() => {
    try {
      return localStorage.getItem("attach_care_plan_docx_on_send") === "1";
    } catch (_e) { return false; }
  });
  // Zoho Bookings availability — fetched dynamically for the currently
  // selected (date, manager). Shows next available slots as clickable pills.
  // 25/02/2026 — Painel passou a mostrar os 5 próximos dias úteis (hoje + 4)
  // em vez de um único dia. Fetching é feito em paralelo para não bloquear
  // a UI. `availByDay` é um mapa `{ 'YYYY-MM-DD': ['09:00', ...] }`.
  const [availByDay, setAvailByDay] = useState({});
  const [availLoading, setAvailLoading] = useState(false);
  const [availError, setAvailError] = useState("");
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [followup, setFollowup] = useState(null);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [regeneratingSummary, setRegeneratingSummary] = useState(false);
  const [proposalTemplates, setProposalTemplates] = useState([]);
  const [pricingRows, setPricingRows] = useState([]);   // active rows from /pricing/config
  const [pricingMultipliers, setPricingMultipliers] = useState([]);  // active multipliers from /pricing/multipliers
  const [catalogSearch, setCatalogSearch] = useState(""); // filter for the catalog picker
  const [historyOpen, setHistoryOpen] = useState(false);   // estimate version history modal
  // Reunião Técnica — decision dialog (inline capture, avoids routing to /script)
  const [rtDialogOpen, setRtDialogOpen] = useState(false);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [rtDialogDraft, setRtDialogDraft] = useState({
    accepted: null, refuse_reason: "", date: "", time: "", manager_id: "",
  });
  const [rtDialogSaving, setRtDialogSaving] = useState(false);
  // Editar textos IA da proposta — per-lead override
  const [propEditOpen, setPropEditOpen] = useState(false);
  const [propEditDraft, setPropEditDraft] = useState({});
  const [propEditSaving, setPropEditSaving] = useState(false);
  const [propEditAiLoading, setPropEditAiLoading] = useState({});
  // Zoho CRM ``Lead_Status`` picklist — fetched on load, used by the
  // "Estado (Zoho CRM)" dropdown. Includes disqualification reasoning
  // modal to enforce a note when the user picks "Não qualificado".
  const [zohoStatuses, setZohoStatuses] = useState([]);
  const [zohoStatusesLoading, setZohoStatusesLoading] = useState(false);
  const [zohoStatusBusy, setZohoStatusBusy] = useState(false);
  const [disqualDialog, setDisqualDialog] = useState({ open: false, status: "", reason: "" });
  // 05/02/2026 — Força a abertura do painel "Resumo RT → ClickUp" mesmo
  // depois de expirada a janela de 60 min. Ligado pelo botão do toolbar.
  const [forceRTSummary, setForceRTSummary] = useState(false);
  // 25/02/2026 — Confirmação obrigatória antes de enviar a proposta ao
  // cliente. Substitui o gate anterior de "Validar valores (RT)" que
  // era exigido no backend. Agora o Consultor confirma no momento do
  // envio que os valores estão correctos.
  const [sendProposalConfirmOpen, setSendProposalConfirmOpen] = useState(false);
  // 25/02/2026 — Separadores de fase pedidos pelo utilizador: a página
  // ficava confusa com todas as fases empilhadas. Agora dividimos em
  // Atendimento / Reunião Técnica / Proposta & Onboarding. O deep-link
  // `?tab=qt` (histórico) continua a mapear para a Reunião Técnica.
  const initialTab = (() => {
    const q = (searchParams.get("tab") || "").toLowerCase();
    if (q === "qt" || q === "rt" || q === "reuniao_tecnica") return "rt";
    if (q === "proposta" || q === "proposal" || q === "onboarding") return "proposta";
    return "atendimento";
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  // 05/02/2026 — Section permissions matrix (role × section → level).
  // Usado para esconder tabs, RTSummaryFab e EnquadramentoPanel a
  // roles que não têm nível "ver" na secção correspondente.
  const { canView: canViewSection, canEdit: canEditSection, ready: permsReady } = useSectionPermissions();
  // Mapa tab → secção (para gate).
  const TAB_SECTION = { rt: "rt_consulta", proposta: "proposta", dt: "plano_cuidados" };
  const isTabAllowed = (key) => {
    const sec = TAB_SECTION[key];
    if (!sec) return true;             // atendimento nunca é gated
    if (!permsReady) return true;      // enquanto carrega, mostra tudo (avoid flicker)
    return canViewSection(sec);
  };
  // Se o utilizador estiver num tab que passou a estar bloqueado
  // (ex: um admin trocou a matriz enquanto ele estava com o lead
  // aberto), redirecciona para o primeiro tab permitido.
  useEffect(() => {
    if (!permsReady) return;
    if (!isTabAllowed(activeTab)) {
      const fallback = ["atendimento", "rt", "proposta", "dt"].find(isTabAllowed);
      if (fallback && fallback !== activeTab) setActiveTab(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsReady, activeTab]);
  // Notes + AI summary panel
  // Notes state moved into components/leadDetail/NotesSection.jsx (2026-02-20).

  const load = () => api.get(`/leads/${id}`).then((r) => setLead(r.data));
  const loadFollowup = () => api.get(`/leads/${id}/followup`).then((r) => setFollowup(r.data)).catch(()=>{});
  // 10/08/2026 — Old proposals may still contain the incorrect
  // "selecção e apresentação do Ajudante Familiar…" clause (which is
  // only valid for the Caring Select plan). Detect it once per lead
  // load and show a proactive toast with a one-click reformat CTA.
  useEffect(() => {
    if (!lead?.proposal?.commercial_terms) return;
    const ct = String(lead.proposal.commercial_terms || "");
    const badRe = /sele[çc]{1,2}[ãa]o e apresentação do Ajudante Familiar e concordância expressa/i;
    if (!badRe.test(ct)) return;
    const dismissKey = `cluj_ack_${id}`;
    if (window.sessionStorage.getItem(dismissKey) === "1") return;
    // Only consultores + gestores + admins can act.
    const role = (getUser()?.role || "").toLowerCase();
    if (!["admin", "consultor", "gestor"].includes(role)) return;
    const t = toast.info(
      "Esta proposta contém uma cláusula desactualizada (Ajudante Familiar). Reformate para corrigir.",
      {
        duration: 15000,
        action: {
          label: "Reformatar",
          onClick: async () => {
            try {
              const { data } = await api.post(`/leads/${id}/proposal/reformat-paragraphs`);
              if ((data.changed || []).length > 0) {
                toast.success(`Reformatado: ${data.changed.join(", ")}`);
                await load();
              } else {
                toast.info(data.message || "Nada a alterar.");
              }
              window.sessionStorage.setItem(dismissKey, "1");
            } catch (e) {
              toast.error(e.response?.data?.detail || "Erro a reformatar");
            }
          },
        },
        onDismiss: () => window.sessionStorage.setItem(dismissKey, "1"),
      },
    );
    return () => { try { toast.dismiss(t); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.proposal?.commercial_terms, id]);
  // Refresh when RTScheduleFab (or any other global widget) mutates this lead.
  useEffect(() => {
    const handler = (ev) => {
      if (!ev?.detail?.id || ev.detail.id === id) load();
    };
    window.addEventListener("lead-updated", handler);
    return () => window.removeEventListener("lead-updated", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // 05/02/2026 — RTSummaryFab (rail lateral) dispara este evento para
  // forçar a abertura do painel de Resumo da RT mesmo depois de a
  // janela auto-hide de 60 min ter fechado. Faz também scroll para o
  // anchor visível.
  useEffect(() => {
    const handler = (ev) => {
      if (ev?.detail?.id && ev.detail.id !== id) return;
      setForceRTSummary(true);
      setTimeout(() => {
        const el = document.getElementById("rt-summary-anchor");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    };
    window.addEventListener("open-rt-summary", handler);
    return () => window.removeEventListener("open-rt-summary", handler);
  }, [id]);
  useEffect(() => {
    // 08/02/2026 — Reset state ao navegar entre leads. Sem isto, quando
    // o utilizador salta de /leads/A para /leads/B, o estado ``lead``
    // ainda tem os dados do lead A durante a janela do fetch. Botões
    // como "Descarregar PDF" usam ``lead.estimate_token`` — enviariam
    // o token do lead A para o URL do lead B, resultando no PDF errado.
    // Utilizador reportou (08/02/2026) que ao descarregar do lead
    // "Carlos Farto" recebia o PDF de "César Rocha" — cross-lead leak
    // clássico de state stale em SPA.
    setLead(null);
    setFollowup(null);
    setVersions([]);
    load();
    loadFollowup();
    // 04/08/2026 — RT scheduling now picks a **Consultor de Cuidados**
    // (the person that evaluates needs + presents the proposal). The
    // list includes admins as a fallback for tenants that haven't set
    // up dedicated consultor users yet; ``gestor`` role is kept so
    // pre-restructure teams don't lose the dropdown until they add
    // consultores.
    // 26/02/2026 — Só consultores efectivos aparecem no dropdown de RTs.
    // `is_consultor_effective` é `true` quando o utilizador tem
    // `role="consultor"` OU quando `temporary_consultor_until` está no
    // futuro (feature de Consultor Temporário — só admin pode definir).
    // Utilizadores inactivos/soft-deleted são excluídos.
    api.get("/users").then((r) => setManagers(r.data.filter((u) => (
      u.is_consultor_effective === true && u.active !== false
    ))));
    api.get(`/leads/${id}/proposal-versions`).then((r)=>setVersions(r.data)).catch(()=>{});
    api.get("/proposal-templates").then((r) => setProposalTemplates(r.data.templates || [])).catch(()=>{});
    api.get("/pricing/config").then((r) => {
      const rows = (r.data?.rows || []).filter(x => x.active !== false);
      setPricingRows(rows);
      const mults = (r.data?.multipliers || []).filter(x => x.active !== false);
      setPricingMultipliers(mults);
    }).catch(()=>{});
    // Zoho Lead_Status picklist — cached server-side, safe to call on every LeadDetail load
    setZohoStatusesLoading(true);
    api.get("/zoho/leads/status-picklist")
      .then((r) => setZohoStatuses(r.data?.items || []))
      .catch(() => setZohoStatuses([]))
      .finally(() => setZohoStatusesLoading(false));
  }, [id]);

  const changeProposalTemplate = async (templateId) => {
    try {
      await api.post(`/leads/${id}/proposal-template`, {
        proposal_template_id: templateId || null,
      });
      await load();
      const t = proposalTemplates.find((x) => x.id === templateId);
      toast.success(templateId ? `Template "${t?.name || 'selecionado'}" associado` : "Template desassociado (usa default)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao mudar template");
    }
  };

  // Claim lock + heartbeat (every 45s while page is open); release on unmount
  const [handlingConflict, setHandlingConflict] = useState(null);
  // 25/02/2026 — Ownership conflict: lead already has a persistent
  // owner (first_contact_by_id) different from the current user. We
  // prompt for a transfer request instead of silently taking over.
  const [ownerConflict, setOwnerConflict] = useState(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferReason, setTransferReason] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [existingTransferReq, setExistingTransferReq] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  // 25/02/2026 — Modal de Diagnóstico das entregas do convite Teams.
  // Alimentado por GET /leads/{id}/teams-event/delivery-logs.
  const [deliveryLogsOpen, setDeliveryLogsOpen] = useState(false);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [sendDraft, setSendDraft] = useState({ subject: "", body_html: "", signature_html: "", link: "", expires_at: "", to: "", attach_pdf: true });
  const [sendLoading, setSendLoading] = useState(false);
  useEffect(() => {
    let mounted = true;
    // 20/02/2026 — Idle-release: releases the ``handling`` claim after
    // 5 min of no user interaction so the lead becomes pickup-able by
    // another SAC. Interaction resumes the claim automatically.
    const IDLE_MS = 5 * 60 * 1000;
    let lastActivity = Date.now();
    let released = false;
    const markActivity = () => { lastActivity = Date.now(); };
    const claim = async () => {
      try {
        const { data } = await api.post(`/leads/${id}/claim`);
        if (mounted) {
          setHandlingConflict(null);
          released = false;
          // 25/02/2026 — surface persistent owner conflict from the
          // backend so the modal can appear once.
          if (data?.owner_conflict) {
            setOwnerConflict(data.owner_conflict);
            setExistingTransferReq(data.owner_conflict.existing_request_id || null);
          } else {
            setOwnerConflict(null);
          }
        }
      } catch (e) {
        if (mounted && e.response?.status === 409) {
          setHandlingConflict(e.response.data?.detail || "Já em tratamento por outro utilizador");
          toast.warning(e.response.data?.detail || "Já em tratamento");
        }
      }
    };
    claim();
    const iv = setInterval(async () => {
      if (!mounted) return;
      const idle = Date.now() - lastActivity;
      if (idle >= IDLE_MS) {
        if (!released) {
          released = true;
          try { await api.post(`/leads/${id}/release`); } catch { /* ignore */ }
          if (mounted) {
            toast.info("Atendimento libertado por inactividade (5 min)");
            load?.();
          }
        }
        return; // skip claim renewal while idle
      }
      // Active: renew (or re-claim if we released earlier)
      claim();
    }, 45000);
    // Track user interaction on the whole page. Any of these events
    // resets the idle timer.
    const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));
    const onBeforeUnload = () => {
      // Best-effort release on tab close/refresh. sendBeacon doesn't allow
      // setting Authorization headers, so we pass the token as a query
      // param — the backend's release endpoint accepts _auth for this
      // exact scenario. Use the actual session key (caring_token), not
      // the legacy "token" key that never existed.
      try {
        const url = `${api.defaults.baseURL}/leads/${id}/release`;
        const token = localStorage.getItem("caring_token");
        if (!token) return;
        if (navigator.sendBeacon) {
          const headers = { type: "application/json" };
          navigator.sendBeacon(`${url}?_auth=${encodeURIComponent(token)}`, new Blob(["{}"], headers));
        }
      } catch {}
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      mounted = false;
      clearInterval(iv);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
      window.removeEventListener("beforeunload", onBeforeUnload);
      api.post(`/leads/${id}/release`).catch(()=>{});
    };
  }, [id]);

  // Fetch Microsoft Graph availability (Teams / Outlook) whenever the
  // manager changes. Since 25/02/2026 o painel deixou de estar acoplado ao
  // ``schedule.date`` — o Gestor vê SEMPRE os próximos 5 dias úteis (hoje +
  // 4) para não aparecerem slots do passado quando a RT já estava agendada
  // para um dia antigo. Chamadas ao endpoint são feitas em paralelo (uma
  // por dia) porque o backend só sabe responder para uma data de cada vez.
  useEffect(() => {
    if (!schedule.manager_id) {
      setAvailByDay({}); setAvailError(""); return;
    }
    let mounted = true;
    setAvailLoading(true); setAvailError("");
    const days = getNextBusinessDays(5);
    Promise.all(
      days.map((iso) =>
        api.get("/graph/calendar/available-slots", {
          params: { date: iso, manager_id: schedule.manager_id },
        })
          .then((r) => ({ iso, slots: r.data?.slots || [], err: null }))
          .catch((e) => ({ iso, slots: [], err: e.response?.data?.detail || "Não foi possível verificar disponibilidade" }))
      )
    ).then((results) => {
      if (!mounted) return;
      const map = {};
      let sharedErr = "";
      for (const { iso, slots, err } of results) {
        map[iso] = slots;
        if (err && !sharedErr) sharedErr = err;
      }
      setAvailByDay(map);
      setAvailError(sharedErr);
    }).finally(() => { if (mounted) setAvailLoading(false); });
    return () => { mounted = false; };
  }, [schedule.manager_id]);

  if (!lead) return <div className="p-10 text-muted-foreground">A carregar...</div>;

  const c = lead.qualification?.contact || {};
  const cp = lead.qualification?.cared_person || {};
  const loc = lead.qualification?.location || {};
  const sv = lead.qualification?.service || {};
  const est = lead.estimate;
  const currentUser = getUser();
  const isAtendimento = currentUser?.role === "atendimento";
  // 20/02/2026 — Consultor owns the RT flow (run_qt permission), so
  // Consultor/Gestor/Admin can validate/adjust QT values. Hoisted here
  // (was scoped inside an IIFE and referenced elsewhere → runtime
  // ``isGestorOrAdmin is not defined`` crash on the whole page).
  const isGestorOrAdmin = ["consultor", "gestor", "admin"].includes(currentUser?.role);
  const estimateBlockedByAlgarve = isAtendimento && lead.is_algarve;
  // Backend-computed reason. When populated, the estimate is blocked for
  // EVERY role (not just atendimento) — see ``_estimate_block_reason`` in
  // server.py. Two current triggers: (1) no regular public transport,
  // (2) district outside Viana do Castelo/Braga/Porto/Coimbra/Leiria/
  // Santarém/Faro.
  const estimateBlockedReason = lead.estimate_blocked_reason || null;
  const estimateBlocked = !!estimateBlockedReason || estimateBlockedByAlgarve;

  const backend = process.env.REACT_APP_BACKEND_URL;
  const estUrl = `${window.location.origin}/estimate/${lead.estimate_token}`;
  const propUrl = `${window.location.origin}/proposal/${lead.proposal_token}`;

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copiado"); };

  const doGenerateEstimate = async () => {
    setBusy(true);
    // Capture whether we're regenerating over a previously-sent estimate
    // so we can offer a one-click "Reenviar ao cliente" right after.
    const hadPrevSend = ((lead?.estimate_history || [])
      .some(h => (h.reason || "sent") === "sent"));
    try {
      await api.post(`/leads/${id}/estimate`);
      if (hadPrevSend) {
        toast.success("Estimativa regerada — reveja e reenvie ao cliente", {
          duration: 8000,
          action: {
            label: "Reenviar agora",
            onClick: () => openSendEstimateModal(),
          },
        });
      } else {
        toast.success("Estimativa gerada — reveja antes de enviar");
      }
      load();
    }
    catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };
  const setPrimaryOption = async (optionId) => {
    try {
      await api.patch(`/leads/${id}/estimate`, { primary_option_id: optionId });
      toast.success("Opção sugerida atualizada"); load();
    } catch(e){ toast.error("Erro"); }
  };
  const updateOptionField = async (optionId, patch) => {
    try {
      await api.patch(`/leads/${id}/estimate`, { options: [{ id: optionId, ...patch }] });
      load();
    } catch(e){ toast.error("Erro a atualizar valor"); }
  };
  const addOption = async () => {
    try {
      await api.post(`/leads/${id}/estimate/options`, { name: "Nova linha", days_per_week: 5, hours_per_day: 8, hourly_price: 0 });
      toast.success("Linha personalizada adicionada"); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); }
  };
  const addFromCatalog = async (row) => {
    if (!row) return;
    try {
      // Reuse the pricing_config row's rates so the operator only has to
      // adjust ``total_days`` (for pontual) or leave everything as-is.
      const est = lead?.estimate || {};
      const isPontual = (est.continuity_type || "continuidade") === "pontual";
      await api.post(`/leads/${id}/estimate/options`, {
        name: row.name,
        mode: isPontual ? "pontual" : "continuidade",
        hourly_price: Number(row.hourly_price) || 0,
        hours_per_day: Number(row.hours_per_day) || 0,
        days_per_week: Number(row.days_per_week) || 0,
        total_days: 0,
        extra_fee: 0,
      });
      toast.success(`Linha "${row.name}" adicionada do catálogo`);
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); }
  };

  // Sort catalog rows so those matching the lead's declared service_types
  // bubble to the top with a "Recomendada" badge. Mirrors the backend's
  // ``pricing_service.pick_primary_row`` heuristic (name substring match).
  const catalogSorted = (() => {
    const rows = pricingRows || [];
    if (!rows.length) return [];
    const svc = ((lead?.qualification || {}).service || {});
    const typesStr = (svc.service_types || []).join(" ").toLowerCase();
    const qiHpd = Number(svc.hours_per_day) || 0;
    const qiDpw = Number(svc.days_per_week) || 0;
    const usedNames = new Set(
      ((lead?.estimate?.options) || []).map(o => (o.name || "").toUpperCase().trim())
    );
    // 04/08/2026 — Recommend only rows whose hours_per_day is close to
    // what Atendimento captured on the QI. Was flagging ALL SEMANA
    // variants (1h → 24h) as "Recomendada" for any continuous lead,
    // which killed the value of the badge.
    const scoreOf = (r) => {
      const n = (r.name || "").toLowerCase();
      const rHpd = Number(r.hours_per_day) || 0;
      const rDpw = Number(r.days_per_week) || 0;
      // Only reward hpd-match when the QI actually provided a value.
      // Falls back to "any hours" match when Atendimento didn't fill it.
      const hpdMatch = qiHpd > 0 ? Math.abs(rHpd - qiHpd) < 0.5 : true;
      const dpwMatch = qiDpw > 0 ? Math.abs(rDpw - qiDpw) <= 1 : true;
      if (typesStr.includes("noite") && n.includes("noite")) {
        return hpdMatch ? 3 : 0;
      }
      if ((typesStr.includes("fim de semana") || typesStr.includes("fins de semana")
            || typesStr.includes("fim-de-semana")) && n.includes("fim")) {
        return hpdMatch ? 3 : 0;
      }
      if (n.includes("semana") && !n.includes("fim") &&
          (typesStr.includes("continuado") || typesStr.includes("continua"))) {
        // For SEMANA rows, prefer the one matching QI's hours_per_day
        // AND days_per_week — otherwise no badge (they still appear
        // in the list, just not marked as "Recomendada").
        return (hpdMatch && dpwMatch) ? 2 : 0;
      }
      return 0;
    };
    return rows
      .map(r => ({
        ...r,
        _score: scoreOf(r),
        _alreadyAdded: usedNames.has((r.name || "").toUpperCase().trim()),
      }))
      .sort((a, b) => b._score - a._score);
  })();
  // Narrow the catalog to the operator's search query (matches name or
  // "8h/dia"-style numeric segments; case-insensitive). Empty query =
  // returns the full sorted list unchanged.
  const catalogFiltered = (() => {
    const q = (catalogSearch || "").trim().toLowerCase();
    if (!q) return catalogSorted;
    return catalogSorted.filter(r => {
      const hay = [
        r.name || "",
        `${r.hours_per_day || 0}h/dia`,
        `${r.days_per_week || 0}d/sem`,
        r.note || "",
      ].join(" ").toLowerCase();
      // Split query into tokens so "semana 8" matches "SEMANA (8h/dia)"
      return q.split(/\s+/).every(tok => hay.includes(tok));
    });
  })();
  const removeOption = async (optionId) => {
    if (!window.confirm("Remover esta linha da estimativa?")) return;
    try {
      await api.delete(`/leads/${id}/estimate/options/${optionId}`);
      toast.success("Linha removida"); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); }
  };
  // ---- Multipliers ----
  const addMultiplierFromCatalog = async (mid) => {
    try {
      await api.post(`/leads/${id}/estimate/multipliers`, { multiplier_id: mid });
      toast.success("Factor adicionado"); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); }
  };
  const removeMultiplier = async (entryId) => {
    if (!window.confirm("Remover este factor da estimativa?")) return;
    try {
      await api.delete(`/leads/${id}/estimate/multipliers/${entryId}`);
      toast.success("Factor removido"); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); }
  };
  const updateAdditionalNotes = async (val) => {
    try { await api.patch(`/leads/${id}/estimate`, { additional_notes: val }); load(); }
    catch(e){ toast.error("Erro"); }
  };
  const updateEstimateSummary = async (val) => {
    try { await api.patch(`/leads/${id}/estimate`, { summary: val }); load(); }
    catch(e){ toast.error("Erro"); }
  };
  const regenerateSummary = async () => {
    setRegeneratingSummary(true);
    try {
      const r = await api.post(`/leads/${id}/estimate/regenerate-summary`);
      setSummaryDraft(r.data.summary || "");
      toast.success("Resumo regerado com IA");
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro ao regerar"); }
    finally { setRegeneratingSummary(false); }
  };
  const updateEstimateTerms = async (commercial_terms) => {
    try {
      await api.patch(`/leads/${id}/estimate`, { commercial_terms });
      toast.success("Condições atualizadas"); load();
    } catch(e){ toast.error("Erro"); }
  };
  const updateServiceWindow = async (patch) => {
    try {
      await api.patch(`/leads/${id}/estimate`, patch);
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); }
  };
  // 25/02/2026 — Gate reforçado antes de enviar a estimativa (pedido do
  // utilizador para o fluxo do SAC). Antes de abrir o modal de envio
  // verifica se: (a) o cliente aceitou a RT mas ainda não está agendada
  // → bloqueia e envia o utilizador para agendar; (b) o cliente recusou
  // a RT mas ainda não está registado o motivo → abre modal dedicado
  // para o SAC preencher o porquê e (opcionalmente) criar um follow-up.
  // Em qualquer caso, a estimativa segue sempre com o nome de quem a
  // GEROU (`estimate_prepared_by_name`) — o backend não altera isto no
  // envio (comportamento existente confirmado a 25/02/2026).
  const openSendEstimateModal = async () => {
    // Gate 1 — cliente aceitou RT mas ainda não agendou
    const accepted = lead?.technical_meeting_accepted;
    const leadIsPontual = (lead?.estimate?.continuity_type || "continuidade") === "pontual"
      || (lead?.estimate?.options || []).some(o => (o.mode || "continuidade") === "pontual");
    if (accepted === true && !lead?.scheduled_qt_at && !leadIsPontual) {
      toast.error(
        "O cliente aceitou a RT mas ainda não está agendada. Agende primeiro a RT antes de enviar a estimativa.",
        { duration: 8000 }
      );
      // Foca o painel de agendamento
      const el = document.querySelector('[data-testid="rt-when-label"]')
                 || document.querySelector('[data-testid="schedule-date"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Gate 2 — cliente recusou RT mas motivo não está registado
    if (accepted === false && !(lead?.rt_refusal_reason || "").trim()) {
      setRtRefusalReason("");
      setRtRefusalCreateFu(true);
      setRtRefusalModal(true);
      return;
    }
    // Fluxo normal
    setSendLoading(true);
    try {
      const r = await api.get(`/leads/${id}/estimate/email-draft`);
      setSendDraft({ ...r.data, attach_pdf: true });
      setSendModalOpen(true);
    } catch(e){ toast.error(e.response?.data?.detail || "Erro a gerar rascunho"); }
    finally { setSendLoading(false); }
  };

  // Submete o motivo de recusa da RT, opcionalmente cria um follow-up
  // e depois abre o modal de envio da estimativa (fluxo contínuo).
  const submitRtRefusalAndContinue = async () => {
    const reason = (rtRefusalReason || "").trim();
    if (!reason) {
      toast.error("Indique o motivo da recusa da RT.");
      return;
    }
    setRtRefusalBusy(true);
    try {
      await api.patch(`/leads/${id}`, { rt_refusal_reason: reason });
      if (rtRefusalCreateFu) {
        // Follow-up para reavaliar em 3 dias úteis. Se falhar não bloqueia
        // — a estimativa ainda pode seguir; apenas avisamos.
        try {
          const due = new Date();
          due.setDate(due.getDate() + 3);
          await api.post(`/leads/${id}/scheduled-followups`, {
            scheduled_for: due.toISOString(),
            reason: `Reavaliar RT após recusa: ${reason}`,
          });
        } catch (fuErr) {
          toast.warning("Motivo guardado, mas não foi possível criar o follow-up. Cria manualmente se necessário.");
        }
      }
      toast.success("Motivo da recusa registado.");
      setRtRefusalModal(false);
      // refresh e continua para o modal de envio
      const r = await api.get(`/leads/${id}`); setLead(r.data);
      setSendLoading(true);
      try {
        const draft = await api.get(`/leads/${id}/estimate/email-draft`);
        setSendDraft({ ...draft.data, attach_pdf: true });
        setSendModalOpen(true);
      } finally { setSendLoading(false); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registar o motivo.");
    } finally {
      setRtRefusalBusy(false);
    }
  };
  const confirmSendEstimate = async () => {
    if (!sendDraft.to) { toast.error("O lead não tem email de contacto"); return; }
    setSendLoading(true);
    try {
      const r = await api.post(`/leads/${id}/estimate/send`, {
        subject: sendDraft.subject,
        body_html: sendDraft.body_html,
        attach_pdf: sendDraft.attach_pdf,
      });
      const email = r.data?.email || {};
      const status = email.status || "sent";
      const expDate = (r.data?.expires_at || "").slice(0, 10);
      if (status === "sent") {
        toast.success(`Estimativa enviada ao cliente. Link válido até ${expDate}.`);
      } else if (status === "mocked") {
        toast.warning(
          `Estimativa marcada como enviada MAS o email NÃO saiu — remetente Office 365 sem sessão activa. Configure em /profile ou /settings/office365 e clique "Reenviar email".`
        );
      } else if (status === "error") {
        toast.error(
          `Estimativa registada mas o email FALHOU: ${email.error || "erro Office 365"}. Verifique /settings/office365 e clique "Reenviar email".`
        );
      } else {
        toast.success(`Estimativa enviada (status: ${status}). Link válido até ${expDate}.`);
      }
      setSendModalOpen(false);
      // Surface PDF build failures so the operator knows to retry —
      // the backend continues sending the email without the attachment
      // instead of blocking the whole flow, but the badge must remind
      // the operator that the client did NOT receive the PDF this time.
      if (email.pdf_error) {
        toast.warning(email.pdf_error + " · Corrija a estimativa e clique em Reenviar.", { duration: 10000 });
      }
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro ao enviar"); }
    finally { setSendLoading(false); }
  };
  const doSchedule = async () => {
    if (!schedule.date || !schedule.time) { toast.error("Indique data e hora"); return; }
    const mgr = managers.find(m => m.id === schedule.manager_id);
    // Some Zoho tenants return slot pills as 12h ("01:00 PM"). If the picker
    // ever passes that through, coerce it to strict 24h HH:MM before building
    // the ISO string — otherwise Python's fromisoformat blows up on the AM/PM.
    const to24h = (t) => {
      if (!t) return t;
      const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
      if (!m) return t;
      let h = parseInt(m[1], 10);
      const mm = m[2];
      const ap = (m[3] || "").toUpperCase();
      if (ap === "PM" && h < 12) h += 12;
      if (ap === "AM" && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${mm}`;
    };
    const time24 = to24h(schedule.time);
    setBusy(true);
    try {
      const r = await api.post(`/leads/${id}/schedule`, {
        scheduled_at: `${schedule.date}T${time24}:00`,
        manager_id: mgr?.id, manager_name: mgr?.name,
      });
      // Zoho Bookings creation is best-effort — if the appointment could
      // not be booked on the calendar, backend returns _booking_warning
      // on the lead. Surface it clearly so the gestor knows to follow up.
      const warn = r.data?._booking_warning;
      if (warn) {
        toast.success("Reunião registada no CRM");
        toast.error("Zoho Bookings: " + warn, { duration: 8000 });
      } else {
        toast.success("Reunião agendada");
      }
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro ao agendar"); } finally { setBusy(false); }
  };
  const doGenerateProposal = async () => {
    const isRegen = !!lead?.proposal;
    let motive = null;
    if (isRegen) {
      // Optional free-text motive — kept as a plain prompt to keep the change
      // small and unobtrusive. Empty string / cancel keeps motive === null.
      const answer = window.prompt(
        "Motivo desta regeneração? (opcional — vai no email à Direção Técnica)\n\n"
        + "Exemplos: 'Cliente pediu menos horas', 'Ajuste ao horário RT', "
        + "'Correção do valor da caução'.",
        "",
      );
      if (answer === null) return;  // user cancelled
      motive = (answer || "").trim() || null;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/proposal`, motive ? { motive } : {});
      toast.success(isRegen ? "Proposta regenerada — Direção Técnica notificada" : "Proposta gerada em rascunho");
      if (data?.proposal_draft_hint) {
        toast.info(data.proposal_draft_hint);
      }
      load();
      api.get(`/leads/${id}/proposal-versions`).then((r)=>setVersions(r.data)).catch(()=>{});
    }
    catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };
  // ---- Proposal AI-text editor ----
  const openProposalEditor = () => {
    const p = lead?.proposal || {};
    setPropEditDraft({
      personal_message: p.personal_message || "",
      executive_summary: p.executive_summary || "",
      needs_understanding: p.needs_understanding || "",
      recommended_service: p.recommended_service || "",
      commercial_terms: p.commercial_terms || "",
      additional_notes: p.additional_notes || "",
      caring_advantages: Array.isArray(p.caring_advantages) ? p.caring_advantages : [],
    });
    setPropEditOpen(true);
  };
  const setPropField = (k, v) => setPropEditDraft((d) => ({ ...d, [k]: v }));
  const improvePropField = async (field, aiContext) => {
    const text = (propEditDraft[field] || "").trim();
    if (!text) { toast.error("Nada para melhorar — escreva algo primeiro."); return; }
    setPropEditAiLoading((s) => ({ ...s, [field]: true }));
    try {
      const r = await api.post("/ai/improve-text", { text, context: aiContext || "", tone: "consultivo" });
      if (r.data?.text) {
        setPropField(field, r.data.text);
        toast.success("Texto melhorado");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao melhorar com IA");
    } finally {
      setPropEditAiLoading((s) => ({ ...s, [field]: false }));
    }
  };
  const saveProposalEdit = async () => {
    setPropEditSaving(true);
    try {
      const payload = { ...propEditDraft };
      // Normalize advantages: allow textarea input as newline-separated list
      if (typeof payload.caring_advantages === "string") {
        payload.caring_advantages = payload.caring_advantages
          .split("\n").map((s) => s.trim()).filter(Boolean);
      }
      await api.patch(`/leads/${id}/proposal`, payload);
      toast.success("Textos da proposta atualizados");
      setPropEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setPropEditSaving(false); }
  };
  const doSendProposalEmail = async () => {
    if (!allReviewed) {
      toast.error("Descarregue e reveja a proposta antes de enviar ao cliente");
      return;
    }
    // 25/02/2026 — Abre popup de confirmação em vez de enviar directamente.
    // O envio real acontece em ``confirmAndSendProposal`` depois do
    // Consultor confirmar que os valores estão correctos.
    setSendProposalConfirmOpen(true);
  };

  const confirmAndSendProposal = async () => {
    setSendProposalConfirmOpen(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/proposal/send-email`, {
        attach_care_plan_docx: attachCarePlanDocx,
      });
      if (data?.pending_prevalidation) {
        // 09/09/2026 — Pré-validação activa: em vez de enviar ao cliente,
        // enviámos ao validador (rui.pinto@caring.pt por defeito) que
        // decide se aprova, edita ou pede alterações.
        toast.success(
          data?.message
          || `Enviado para validação prévia por ${data?.prevalidation?.validator_email}.`,
          { duration: 6000 },
        );
      } else {
        toast.success(data?.email?.status === "sent"
          ? (attachCarePlanDocx
              ? "Proposta enviada ao cliente com Plano de Cuidados anexado (.docx)"
              : "Proposta enviada ao cliente")
          : "Proposta enviada (modo mock)");
      }
      load();
    }
    catch(e){ toast.error(e.response?.data?.detail || "Erro ao enviar"); }
    finally { setBusy(false); }
  };

  const doSendEstimateEmail = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/emails/send-estimate/${id}`); toast.success(data.status === "sent" ? "Email enviado" : "Email registado (mock)"); }
    catch(e){ toast.error("Erro"); } finally { setBusy(false); }
  };
  const doZohoSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/zoho/sync/${id}`);
      toast.success(`Zoho: ${data.operation === 'create_account' ? 'Conta criada' : 'Conta atualizada'}`);
      // refresh lead to pick up new zoho_account_id
      const r = await api.get(`/leads/${id}`); setLead(r.data);
    }
    catch(e){ toast.error(e.response?.data?.detail || "Erro ao sincronizar"); } finally { setBusy(false); }
  };

  // 25/02/2026 — Submit da mudança de estado manual (Perdido na Estimativa,
  // Proposta Perdida, Negócio Ganho/Perdido). Envia `status` + `loss_reason`
  // via PATCH `/leads/{id}` (para statuses de perda) OU
  // `status_change_reason` (para Negócio Ganho). Cria também uma nota
  // interna para auditoria — usa POST `/leads/{id}/notes` para ficar
  // visível na timeline sem sincronização Zoho.
  const submitStatusChange = async () => {
    if (!statusChangeAction) return;
    const reason = (statusChangeReason || "").trim();
    if (!reason) {
      toast.error("Indique o motivo da mudança de estado.");
      return;
    }
    setStatusChangeBusy(true);
    try {
      const isWin = statusChangeAction.key === "negocio_ganho";
      const payload = { status: statusChangeAction.key };
      if (isWin) {
        payload.status_change_reason = reason;
      } else {
        payload.loss_reason = reason;
      }
      await api.patch(`/leads/${id}`, payload);
      // Nota interna para auditoria (kind manual — não sincroniza para Zoho
      // porque o Zoho já regista o status próprio).
      try {
        await api.post(`/leads/${id}/notes`, {
          content: `Mudança manual de estado → ${STATUS_LABELS[statusChangeAction.key] || statusChangeAction.key}. Motivo: ${reason}`,
        });
      } catch { /* nota é bónus, não bloqueia o fluxo */ }
      toast.success(`Estado actualizado para "${STATUS_LABELS[statusChangeAction.key]}"`);
      const r = await api.get(`/leads/${id}`); setLead(r.data);
      setStatusChangeAction(null);
      setStatusChangeReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível actualizar o estado.");
    } finally {
      setStatusChangeBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/leads/${id}/proposal/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proposta-Caring-${id.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descarregado");
      // Mark the proposal PDF as reviewed
      setReviewedDocs((s) => ({ ...s, proposal: true }));
    } catch(e){ toast.error("Erro ao gerar PDF"); } finally { setBusy(false); }
  };

  const attachPdfToZoho = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/attach-to-zoho`);
      toast.success(`PDF anexado ao Zoho: ${data.filename}`);
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };

  const triggerFollowup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/followup/trigger`);
      if (data.action === "followup_created") toast.success(`Follow-up #${data.attempt} enviado ao Zoho`);
      else toast.info(`Sem acção: ${data.skipped || "—"}`);
      loadFollowup(); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };

  const cancelFollowup = async () => {
    if (!window.confirm("Cancelar os follow-ups automáticos para este lead?")) return;
    setBusy(true);
    try {
      await api.post(`/leads/${id}/followup/cancel`);
      toast.success("Follow-ups automáticos cancelados");
      loadFollowup();
    } catch(e){ toast.error("Erro"); } finally { setBusy(false); }
  };

  const qtConfirmEstimate = async () => {
    if (!window.confirm("Validar estes valores como finais após Reunião Técnica? O cliente NÃO é notificado automaticamente — depois clique em \"Enviar email\" quando estiver pronto.")) return;
    setBusy(true);
    try {
      await api.post(`/leads/${id}/qt/confirm-estimate`, {});
      toast.success("Valores validados. Cliente ainda não foi notificado — use \"Enviar email\" para o fazer.");
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };
  const qtAdjustEstimate = async () => {
    const reason = window.prompt("Justifique a alteração da estimativa (o cliente NÃO é notificado automaticamente):");
    if (!reason || !reason.trim()) { toast.error("Motivo obrigatório"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/leads/${id}/qt/adjust-estimate`, { reason: reason.trim() });
      if (r.data?.pending_approval) {
        toast.success("Redução submetida — a aguardar aprovação de Rui Pinto ou Daniela Monteiro.");
      } else {
        toast.success("Alteração registada. Cliente ainda não foi notificado — use \"Enviar email\" para o fazer.");
      }
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };
  const deleteLead = async () => {
    const name = (lead?.qualification?.contact?.name || "este lead");
    const hasZoho = !!lead.zoho_lead_id;
    const alsoZoho = hasZoho && window.confirm(
      `Eliminar ${name}.\n\nEste lead também existe no Zoho (ID ${lead.zoho_lead_id}).\n\nOK = eliminar aqui E no Zoho\nCancelar = eliminar só aqui`
    );
    if (!hasZoho && !window.confirm(`Eliminar definitivamente ${name}?`)) return;
    const confirm2 = window.prompt(`Escreva "ELIMINAR" para confirmar:`);
    if (confirm2 !== "ELIMINAR") { toast.error("Cancelado"); return; }
    setBusy(true);
    try {
      const { data } = await api.delete(`/leads/${id}`, { params: { also_zoho: alsoZoho } });
      if (data.zoho?.attempted) {
        if (data.zoho.ok) toast.success("Lead eliminado (aqui + Zoho)");
        else toast.warning(`Eliminado aqui, mas falhou no Zoho: ${data.zoho.error || "erro"}`);
      } else {
        toast.success("Lead eliminado");
      }
      window.location.href = "/leads";
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); setBusy(false); }
  };

  // Save the Reunião Técnica decision captured through the inline dialog.
  // Persists via PATCH /leads/{id} so ANY role that can edit the lead (admin,
  // gestor, atendimento) can register the decision — avoids depending on the
  // /leads/:id/script route which is gated by the ``lead-new`` permission.
  const saveRtDecision = async () => {
    if (rtDialogDraft.accepted == null) {
      toast.error("Escolha primeiro se o cliente aceitou ou não a Reunião Técnica.");
      return;
    }
    if (rtDialogDraft.accepted === false && !(rtDialogDraft.refuse_reason || "").trim()) {
      toast.error("Descreva brevemente o motivo/próximos passos do cliente.");
      return;
    }
    setRtDialogSaving(true);
    try {
      const patch = {
        technical_meeting_accepted: rtDialogDraft.accepted,
        technical_meeting_refuse_reason: rtDialogDraft.accepted === false
          ? (rtDialogDraft.refuse_reason || "").trim() : null,
      };
      if (rtDialogDraft.accepted === true && rtDialogDraft.date && rtDialogDraft.time) {
        // 04/08/2026 — Store as naive local Portugal time (no timezone
        // suffix), matching the /schedule route contract. Appending
        // "+00:00" here caused the RT date to be interpreted as UTC and
        // then shifted +1h when rendered in WEST (Portugal summer time).
        // Everywhere else in the codebase reads this string as
        // "YYYY-MM-DDTHH:MM[:SS]" in Lisbon local time.
        patch.scheduled_qt_at = `${rtDialogDraft.date}T${rtDialogDraft.time}:00`;
      }
      if (rtDialogDraft.accepted === true && rtDialogDraft.manager_id) {
        const mgr = managers.find(m => m.id === rtDialogDraft.manager_id);
        // 04/08/2026 — Post-restructure, the person picked at RT
        // scheduling is the Consultor de Cuidados (not the Gestor).
        // Store in the consultor slot so ``getLeadOwner`` returns the
        // correct role. Kept the ``manager_id`` variable name for
        // minimal frontend churn — it's just the picker id.
        patch.assigned_consultor_id = rtDialogDraft.manager_id;
        if (mgr?.name) patch.assigned_consultor_name = mgr.name;
      }
      await api.patch(`/leads/${id}`, patch);
      toast.success(
        rtDialogDraft.accepted
          ? "RT registada — Zoho vai receber a Task de reminder."
          : "Decisão registada — cliente marcado como \"aguarda retorno\"."
      );
      setRtDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setRtDialogSaving(false);
    }
  };

  return (
    <div className="p-4 lg:px-8 lg:py-5 lg:pr-20 max-w-[1600px] mx-auto" data-testid="lead-detail-root">
      {/* 26/02/2026 v2 — Modal First Response SÓ aparece quando o
          utilizador tenta sair do lead sem ter registado uma resposta
          (pendingNavigation != null). Cancelar mantém-no na página. */}
      {pendingNavigation && needsFirstResponse && (
        <FirstResponseModal
          leadId={lead.id}
          leadName={c.name}
          onDone={async () => {
            await load();
            const dest = pendingNavigation;
            setPendingNavigation(null);
            navigate(dest);
          }}
          onCancel={() => setPendingNavigation(null)}
        />
      )}
      <Link to="/leads" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-3"><ArrowLeft className="w-4 h-4" /> Voltar</Link>
      <div>
      <div className="space-y-4 min-w-0" data-testid="lead-detail-main">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-1">Lead {lead.is_algarve && <span className="ml-2 text-foreground">· Algarve</span>}</div>
          <h1 className="font-display text-2xl lg:text-3xl tracking-tight leading-tight">{c.name}</h1>
          <div className="text-muted-foreground text-xs mt-1">{c.phone} · {c.email}</div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 md:justify-end md:max-w-[60%]">
          <span className={`inline-flex text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border ${STATUS_COLORS[lead.status] || ''}`}
                title="Fase interna do workflow Caring"
                data-testid="lead-detail-status">{statusLabelForLead(lead)}</span>
          {/* 26/02/2026 — Botão manual "Marcar como Contactado". Só aparece
              enquanto o lead está no arranque (tentativa_contacto ou
              qualificacao_inicial legado). Chama o endpoint dedicado que
              carimba first_contact_at + escreve nota + auditoria. */}
          {(lead.status === "tentativa_contacto" || lead.status === "qualificacao_inicial")
              && ["atendimento", "consultor", "gestor", "admin"].includes(currentUser?.role) && (
            <button
              type="button"
              data-testid="btn-mark-contacted"
              onClick={async () => {
                try {
                  await api.post(`/leads/${lead.id}/mark-contacted`);
                  toast.success("Lead marcado como Contactado.");
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erro ao marcar como contactado.");
                }
              }}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] hover:border-[hsl(var(--success))] font-semibold"
              title="Força a transição para Contactado (usa isto se o lead já foi contactado mas o estado não avançou)"
            >
              <PhoneCall className="w-3 h-3" /> Marcar Contactado
            </button>
          )}
          {/* 09/09/2026 — Kill-switch manual "Parar emails ao cliente".
              Cliente reportou não parar de receber emails num lead
              marcado como negocio_perdido. Este botão desliga
              imediatamente todos os emails externos automáticos.
              Visível a quem gere o lead. Após stop, mostra badge
              "Emails desligados" e (só admin/direcao) botão "Reativar". */}
          {!lead.client_emails_stopped_at
              && ["atendimento", "consultor", "gestor", "admin", "direcao"].includes(currentUser?.role) && (
            <button
              type="button"
              data-testid="btn-stop-client-emails"
              onClick={async () => {
                if (!window.confirm(
                  "Desligar TODOS os emails automáticos ao cliente neste lead?\n\n"
                  + "Isto pára os lembretes de proposta, avisos de RT/caução e "
                  + "qualquer notificação externa. Continua a poder enviar emails "
                  + "manuais pelo Outlook — o cliente só deixa de receber os "
                  + "emails automáticos deste sistema.\n\n"
                  + "Fica registado em nota interna."
                )) return;
                try {
                  await api.post(`/leads/${lead.id}/client-emails/stop`);
                  toast.success("Emails ao cliente desligados.");
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erro ao desligar emails.");
                }
              }}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] hover:border-[hsl(var(--danger))] font-semibold"
              title="Corta imediatamente todos os emails automáticos externos que a plataforma envia ao cliente"
            >
              <BellOff className="w-3 h-3" /> Parar Emails
            </button>
          )}
          {lead.client_emails_stopped_at && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] font-semibold"
              title={`Emails automáticos ao cliente desligados${lead.client_emails_stopped_by_name ? ` por ${lead.client_emails_stopped_by_name}` : ""}. Envio manual via Outlook continua disponível.`}
              data-testid="badge-client-emails-stopped"
            >
              <BellOff className="w-3 h-3" /> Emails Desligados
            </span>
          )}
          {/* 09/09/2026 — Badge de estado da validação prévia da proposta.
              Só aparece se houver pré-validação em curso ou concluída. */}
          {lead.proposal_prevalidation?.status === "pending" && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-[hsl(var(--info))]/40 bg-[hsl(var(--info-bg))] text-[hsl(var(--info))] font-semibold"
              title={`Proposta em validação por ${lead.proposal_prevalidation.validator_email || "validador"}`}
              data-testid="badge-prevalidation-pending"
            >
              <ShieldCheck className="w-3 h-3" /> Aguarda validação
            </span>
          )}
          {lead.proposal_prevalidation?.status === "changes_requested" && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] font-semibold"
              title={`Alterações pedidas: ${lead.proposal_prevalidation.decision_reason || "sem motivo"}`}
              data-testid="badge-prevalidation-changes"
            >
              <ShieldCheck className="w-3 h-3" /> Alterações pedidas
            </span>
          )}
          {lead.client_emails_stopped_at
              && ["admin", "direcao"].includes(currentUser?.role) && (
            <button
              type="button"
              data-testid="btn-resume-client-emails"
              onClick={async () => {
                if (!window.confirm(
                  "Reactivar emails automáticos ao cliente?\n\n"
                  + "Os lembretes de proposta e outros avisos automáticos voltam "
                  + "a ser enviados. Use apenas se confirmou com o cliente que "
                  + "pode voltar a contactá-lo automaticamente."
                )) return;
                try {
                  await api.post(`/leads/${lead.id}/client-emails/resume`);
                  toast.success("Emails ao cliente reactivados.");
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erro ao reactivar emails.");
                }
              }}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-border bg-muted text-foreground hover:border-foreground font-semibold"
              title="Reactivar envio de emails automáticos ao cliente (admin/direcao only)"
            >
              <Bell className="w-3 h-3" /> Reativar
            </button>
          )}
          {/* 25/02/2026 — Badge "TESTE" quando o lead foi criado como
              ambiente de teste. Ajuda o utilizador a perceber que este
              lead não conta para painéis operacionais nem alertas. */}
          {lead.is_test && (
            <span
              className="inline-flex text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-dashed border-[hsl(var(--warning))] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))]/40"
              title="Lead de teste — excluído dos painéis operacionais; pode ser eliminado por qualquer utilizador"
              data-testid="lead-detail-test-badge"
            >
              Lead de Teste
            </span>
          )}
          {/* 25/02/2026 — Badge secundário do onboarding pós-adjudicação
              (b3 do redesign do funil). Só aparece quando existe um
              ``onboarding_stage`` — i.e. após o cliente aceitar a proposta. */}
          {lead.onboarding_stage && ONBOARDING_STAGE_LABELS[lead.onboarding_stage] && (
            <span
              className={`inline-flex text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border ${ONBOARDING_STAGE_COLORS[lead.onboarding_stage] || ''}`}
              title="Fase do onboarding pós-adjudicação (caução, PI, arranque)"
              data-testid="lead-detail-onboarding-stage"
            >
              {ONBOARDING_STAGE_LABELS[lead.onboarding_stage]}
            </span>
          )}
          {lead.zoho_lead_status && lead.zoho_lead_status.toLowerCase() !== (STATUS_LABELS[lead.status] || '').toLowerCase() && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border border-border bg-muted/40 text-muted-foreground"
                  title="Estado bruto sincronizado do Zoho CRM"
                  data-testid="lead-detail-zoho-status">
              <span className="text-muted-foreground">Zoho:</span> {lead.zoho_lead_status}
            </span>
          )}
          <Link data-testid="btn-lead-script" to={`/leads/${id}/script`} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"><Headphones className="w-3.5 h-3.5" /> Retomar Guião</Link>
          <Link data-testid="btn-lead-timeline" to={`/leads/${id}/timeline`} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"><Sparkles className="w-3.5 h-3.5" /> Timeline</Link>
          <CallControls lead={lead} />
          {Array.isArray(lead.inbound_emails) && lead.inbound_emails.length > 0 && (
            <a
              href="#inbound-emails"
              data-testid="btn-jump-emails"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("inbound-emails")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--success-bg))] hover:bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 text-[hsl(var(--success))] rounded-full px-3 py-1.5 font-semibold"
              title={`${lead.inbound_emails.length} email(s) recebido(s) do cliente`}
            >
              <Mail className="w-3.5 h-3.5" /> Emails do cliente ({lead.inbound_emails.length})
            </a>
          )}
          <Link data-testid="btn-edit-lead" to={`/leads/${id}/edit`} className="inline-flex items-center gap-1.5 text-xs bg-primary/10 border border-primary/30 text-foreground hover:bg-primary/20 rounded-full px-3 py-1.5"><ClipboardCheck className="w-3.5 h-3.5" /> Editar</Link>
          {/* 25/02/2026 — Dropdown "Mudar Estado" com as 4 transições
              manuais do novo funil. Cada item só aparece quando o status
              actual do lead o permite (definido em STATUS_CHANGE_ACTIONS
              via ``visibleWhen``). Motivo é obrigatório e é registado como
              nota interna para auditoria. */}
          {(() => {
            const applicable = STATUS_CHANGE_ACTIONS.filter(a => a.visibleWhen(lead.status));
            if (applicable.length === 0) return null;
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"
                    data-testid="btn-status-change"
                  >
                    <FilePenLine className="w-3.5 h-3.5" /> Mudar Estado <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Transições disponíveis
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {applicable.map((a) => (
                    <DropdownMenuItem
                      key={a.key}
                      onSelect={() => { setStatusChangeAction(a); setStatusChangeReason(""); }}
                      className={a.tone === "danger"
                        ? "text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]"
                        : a.tone === "success"
                        ? "text-[hsl(var(--success))] focus:text-[hsl(var(--success))]"
                        : ""}
                      data-testid={`status-change-option-${a.key}`}
                    >
                      {a.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
          <button data-testid="btn-zoho-sync" onClick={doZohoSync} disabled={busy} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"><Cable className="w-3.5 h-3.5" /> Sync Zoho</button>
          {lead.proposal && lead.zoho_lead_id && (
            <button data-testid="btn-attach-zoho" onClick={attachPdfToZoho} disabled={busy} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"><Cable className="w-3.5 h-3.5" /> Anexar PDF ao Zoho</button>
          )}
          {currentUser?.role === "admin" && (() => {
            // 18/02/2026: leads originados no Zoho não podem ser eliminados
            // só deste lado (Zoho continua a ser source of truth). Admin
            // pode escolher "also_zoho=true" no diálogo para eliminar em
            // ambos os sistemas.
            // 25/02/2026: leads MARCADOS COMO TESTE (`is_test=true`) podem
            // ser eliminados por qualquer utilizador, ignorando as
            // restrições de admin/Zoho.
            const isTestLead = !!lead.is_test;
            const isFromZoho = !isTestLead && (!!lead.created_by_zoho || (
              !!lead.zoho_lead_id && !lead.proposal && !lead.technical && !lead.created_by_id
            ));
            const disabled = busy || (
              !isTestLead && isFromZoho && currentUser?.role !== "admin"
            ) || (!isTestLead && currentUser?.role !== "admin");
            return (
              <button
                data-testid="btn-delete-lead"
                onClick={deleteLead}
                disabled={disabled}
                title={
                  isTestLead
                    ? "Lead de teste — pode ser eliminado por qualquer utilizador; apaga todo o histórico"
                    : isFromZoho
                    ? (currentUser?.role === "admin"
                        ? "Lead veio do Zoho — pode eliminar em ambos os lados no diálogo seguinte"
                        : "Lead veio do Zoho e só pode ser eliminado no Zoho")
                    : (currentUser?.role === "admin"
                        ? "Eliminar lead"
                        : "Só o administrador pode eliminar leads reais")
                }
                className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--danger-bg))] hover:bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 text-[hsl(var(--danger))] rounded-full px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-3.5 h-3.5" /> Eliminar
              </button>
            );
          })()}
        </div>
      </div>

      <FollowUpForm leadId={id} onSaved={load} />

      {/* Admin/direcao only — reatribuição manual do mérito do bónus.
          Sobrepõe first_contact_by_id no cálculo dos bónus por escritório.
          Ver /app/backend/routers/assignments.py > set_bonus_credit. */}
      <BonusCreditBlock lead={lead} currentUser={currentUser} onChanged={load} />

      {/* Banner de parceiro externo (escritório Lisboa) — leads que ficam
          read-only na app. Só aparece quando o escritório está em
          ``PARTNER_OFFICES``. Aparece antes de qualquer outro bloco para
          o utilizador perceber logo que este lead não é dele. */}
      {lead.office === "Lisboa" && (
        <div
          className="bg-muted border-2 border-slate-400 rounded-2xl p-5 flex items-start gap-4"
          data-testid="partner-lead-banner"
        >
          <div className="text-foreground text-2xl leading-none pt-0.5">🤝</div>
          <div className="flex-1">
            <div className="font-semibold text-foreground text-sm mb-1">
              Lead de parceiro externo — Lisboa (apenas leitura)
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              Este lead está localizado em Lisboa/Setúbal — zona coberta pelo
              nosso parceiro externo. É apresentado aqui apenas para consulta:
              <strong> não conta para SLA nem para as estatísticas do
              Relatório Diário</strong>, e todas as acções de edição estão
              bloqueadas. Se precisar de intervir neste lead, contacte
              directamente o parceiro.
              {currentUser?.role === "admin" && (
                <span className="block mt-1 text-muted-foreground">
                  Como admin, ainda podes editar em casos excepcionais
                  (ex: corrigir escritório mal atribuído).
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Escritório mal atribuído?
              </span>
              <select
                data-testid="partner-office-fix"
                defaultValue=""
                onChange={async (e) => {
                  const newOffice = e.target.value;
                  if (!newOffice) return;
                  if (!window.confirm(`Alterar o escritório deste lead para "${newOffice}"?`)) {
                    e.target.value = "";
                    return;
                  }
                  try {
                    await api.patch(`/leads/${id}`, { office: newOffice });
                    toast.success(`Lead movido para ${newOffice}`);
                    load();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erro a alterar escritório");
                    e.target.value = "";
                  }
                }}
                className="bg-card border border-border rounded-full px-3 py-1 text-xs text-foreground focus:outline-none focus:border-slate-500"
              >
                <option value="">— Mudar para… —</option>
                <option value="Porto">Porto</option>
                <option value="Braga">Braga</option>
                <option value="Leiria">Leiria</option>
                <option value="Algarve">Algarve</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {estimateBlockedReason && (
        <div
          className="bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 rounded-2xl p-5 flex items-start gap-4"
          data-testid="estimate-blocked-banner"
        >
          <div className="text-[hsl(var(--warning))] text-2xl leading-none pt-0.5">⚠</div>
          <div className="flex-1">
            <div className="font-semibold text-[hsl(var(--warning))] text-sm mb-1">
              Estimativa bloqueada — só pode agendar Reunião Técnica
            </div>
            <div className="text-xs text-[#78350F] leading-relaxed">
              {estimateBlockedReason}
            </div>
          </div>
        </div>
      )}

      {lead.is_algarve && (
        <div className="bg-primary/15 border border-primary/40 rounded-2xl p-5 flex items-start gap-4" data-testid="algarve-banner">
          <div className="text-foreground text-2xl leading-none pt-0.5">▲</div>
          <div className="flex-1">
            <div className="font-semibold text-foreground text-sm mb-1">Lead no Algarve — qualificação exclusiva do Gestor de Conta</div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              Por política interna, o Atendimento <strong>não pode gerar nem enviar valores</strong> para leads no Algarve. Agende a Reunião Técnica com um Gestor de Cliente para que ele avance com a estimativa.
            </div>
          </div>
        </div>
      )}

      {/* 25/02/2026 — RT summary panel: janela de 60 minutos.
          Só aparece quando a RT foi concluída (`technical.completed_at`)
          e estamos ainda dentro dos 60 minutos após a conclusão.
          Passado esse tempo desaparece automaticamente para não
          poluir a página. Só o Consultor/Gestor/Admin vê.
          05/02/2026 — O botão "Resumo RT" do toolbar força a reabertura
          (via prop ``force``) para reenvios tardios. */}
      {currentUser?.role !== "atendimento"
        && lead.technical?.completed_at
        && (!permsReady || canViewSection("rt_consulta")) && (
        <div id="rt-summary-anchor">
          <RTSummaryWindow lead={lead} onSaved={load} force={forceRTSummary} />
        </div>
      )}

      <LeadTimeline lead={lead} />

      {/* 25/02/2026 — Histórico de Estados para auditoria (pedido do
          utilizador). Colapsável para não poluir a página; expande sob
          click para mostrar quem, quando e porquê mudou cada status. */}
      <LeadStatusHistory lead={lead} />

      {/* Análise da Reunião Técnica — DESACTIVADA (25/02/2026 · pedido
          do utilizador). Feature escondida até nova ordem. O componente
          continua no repo (`RTAnalysisPanel.jsx`) para retomar quando
          for reactivada. */}
      {false && currentUser?.role !== "atendimento" && (
        <RTAnalysisPanel leadId={lead.id} onApplied={load} />
      )}

      {/* 20/02/2026 — RTNotesBlock is rendered later in the page (after
          the Zoho status & notes section) so the order on the lead
          screen is: Atendimento notes → Consultor notes. The block is
          placed near the RT / Care Plan section. */}

      {/* Emails recebidos do cliente (via sincronização leads@caring.pt).
          Colocado logo abaixo da timeline para máxima visibilidade — o utilizador
          precisa de ler o que o cliente escreveu antes de decidir o próximo passo.
          Um botão "Buscar emails na caixa" (reconciliação) força uma busca no
          Microsoft 365 pelos últimos 90 dias, ignorando ``isRead``. Útil quando
          o email foi lido no Outlook antes do sync ou o lead foi criado depois. */}
      <InboundEmailsBlock lead={lead} onReconciled={load} />

      {/* Pontual pricing gate — only visible for pontual services.
          Atendimento must request pricing from admin/consultor before
          the "Gerar Estimativa" button unblocks. See PontualPricingBlock
          for the state machine (request → priced). */}
      <PontualPricingBlock
        lead={lead}
        currentUser={currentUser}
        onChanged={load}
        autoFocus={pontualFocus}
      />

      {/* Plano de cuidados minimal para pontuais — só renderiza quando
          continuity="pontual" e a RT já está agendada/realizada
          (o próprio componente faz esses guards). Contexto:
          o care plan completo (care_plan_masters) tem 30+ blocos
          desenhados para continuidade; para pontuais criámos esta
          vista enxuta com checkboxes pré-preenchidos pela IA. */}
      <PontualCarePlanBlock lead={lead} onChanged={load} />

      {handlingConflict && (
        <div className="rounded-xl border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] px-4 py-3 text-sm flex items-center gap-2" data-testid="handling-conflict">
          <UserCheck className="w-4 h-4" /> <b>Atenção:</b> {handlingConflict}
        </div>
      )}

      {/* 25/02/2026 — Persistent ownership conflict banner. Fires when
          another SAC opens a lead already owned by a colleague. Admins
          bypass this (backend never sets owner_conflict for admins).
          09/02/2026 — Consultores de Cuidados podem assumir directamente
          (auto-approve) — o CTA fica gold "Assumir controlo" em vez de
          "Solicitar transferência". */}
      {ownerConflict && (
        <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] px-4 py-3 text-sm flex items-center gap-3 flex-wrap" data-testid="owner-conflict-banner">
          <UserCheck className="w-4 h-4" />
          <div className="flex-1 min-w-[200px]">
            <b>Lead atribuído a {ownerConflict.owner_name}.</b>{" "}
            {ownerConflict.can_auto_take
              ? "Como Consultor de Cuidados pode assumir o controlo directamente."
              : "Pode ver e adicionar notas, mas para assumir precisa de aprovação do admin."}
          </div>
          {existingTransferReq ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-card border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]" data-testid="owner-conflict-pending">
              Pedido pendente · aguarda admin
            </span>
          ) : (
            <button
              type="button"
              onClick={() => { setTransferReason(""); setTransferModalOpen(true); }}
              data-testid="owner-conflict-request-transfer"
              className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-colors ${
                ownerConflict.can_auto_take
                  ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                  : "bg-amber-600 hover:bg-amber-700 text-primary-foreground"
              }`}
            >
              {ownerConflict.can_auto_take ? "Assumir controlo" : "Solicitar transferência"}
            </button>
          )}
        </div>
      )}

      {/* 25/02/2026 — Modal "Mudar Estado" para as 4 transições manuais do
          novo funil. Motivo é obrigatório e é registado como nota interna
          para auditoria. Presets acelerando os motivos mais comuns. */}
      <Dialog open={!!statusChangeAction} onOpenChange={(o) => { if (!o) { setStatusChangeAction(null); setStatusChangeReason(""); } }}>
        <DialogContent className="max-w-lg" data-testid="status-change-modal">
          <DialogHeader>
            <DialogTitle>{statusChangeAction?.label || "Mudar estado"}</DialogTitle>
            <DialogDescription>
              Vais mover este lead para <b>{STATUS_LABELS[statusChangeAction?.key] || statusChangeAction?.key}</b>.
              O motivo é registado como nota interna para auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">
                Motivos frequentes
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="status-change-presets">
                {(statusChangeAction?.presets || []).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setStatusChangeReason(p)}
                    className={`text-[11px] rounded-full px-2.5 py-1 border transition ${statusChangeReason === p
                      ? "bg-foreground text-background border-foreground"
                      : "bg-muted text-foreground border-border hover:bg-muted/60"}`}
                    data-testid={`status-change-preset-${p.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold block mb-1">
                Motivo (obrigatório)
              </label>
              <textarea
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                placeholder="Escreve o motivo detalhado ou selecciona um preset acima"
                rows={4}
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm resize-y"
                data-testid="status-change-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => { setStatusChangeAction(null); setStatusChangeReason(""); }}
              className="text-xs px-4 py-2 rounded-full border border-border bg-muted hover:bg-muted/60"
              data-testid="status-change-cancel"
              disabled={statusChangeBusy}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submitStatusChange}
              disabled={statusChangeBusy || !(statusChangeReason || "").trim()}
              className={`text-xs px-4 py-2 rounded-full font-semibold text-primary-foreground disabled:opacity-50 ${statusChangeAction?.tone === "success"
                ? "bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90"
                : "bg-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/90"}`}
              data-testid="status-change-confirm"
            >
              {statusChangeBusy ? "A actualizar…" : "Confirmar mudança"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 25/02/2026 — RT Refusal modal. Aparece quando o SAC tenta enviar
          a estimativa mas o cliente recusou a RT e ainda não há motivo
          registado. Pedido do utilizador: registar motivo + follow-up
          opcional antes de enviar. */}
      <Dialog open={rtRefusalModal} onOpenChange={(o) => { if (!o && !rtRefusalBusy) setRtRefusalModal(false); }}>
        <DialogContent className="max-w-lg" data-testid="rt-refusal-modal">
          <DialogHeader>
            <DialogTitle>Cliente recusou a Reunião Técnica</DialogTitle>
            <DialogDescription>
              Antes de enviar a estimativa, regista <b>porque é que o cliente recusou</b>. Isto ajuda o Consultor a preparar próximos contactos. Opcionalmente cria um follow-up para reavaliares a decisão dentro de dias.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">
                Motivos frequentes
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="rt-refusal-presets">
                {[
                  "Cliente não quer marcar por agora",
                  "Cliente prefere ver primeiro a estimativa",
                  "Cliente diz que não é necessário RT",
                  "Cliente ainda a decidir entre opções",
                  "Cliente sem disponibilidade nos próximos dias",
                ].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setRtRefusalReason(p)}
                    className={`text-[11px] rounded-full px-2.5 py-1 border transition ${rtRefusalReason === p
                      ? "bg-foreground text-background border-foreground"
                      : "bg-muted text-foreground border-border hover:bg-muted/60"}`}
                    data-testid={`rt-refusal-preset-${p.slice(0, 18).replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold block mb-1">
                Motivo (obrigatório)
              </label>
              <textarea
                value={rtRefusalReason}
                onChange={(e) => setRtRefusalReason(e.target.value)}
                placeholder="Escreve o motivo específico ou selecciona um preset acima"
                rows={3}
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm resize-y"
                data-testid="rt-refusal-reason-input"
              />
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rtRefusalCreateFu}
                onChange={(e) => setRtRefusalCreateFu(e.target.checked)}
                className="mt-0.5"
                data-testid="rt-refusal-create-fu"
              />
              <span>
                <b>Criar follow-up daqui a 3 dias</b> para reavaliar a decisão do cliente. Aparece automaticamente na tua lista de tarefas.
              </span>
            </label>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setRtRefusalModal(false)}
              className="text-xs px-4 py-2 rounded-full border border-border bg-muted hover:bg-muted/60"
              data-testid="rt-refusal-cancel"
              disabled={rtRefusalBusy}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submitRtRefusalAndContinue}
              disabled={rtRefusalBusy || !(rtRefusalReason || "").trim()}
              className="text-xs px-4 py-2 rounded-full font-semibold bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
              data-testid="rt-refusal-confirm"
            >
              {rtRefusalBusy ? "A guardar…" : "Guardar e continuar para envio"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>




      {/* Transfer request modal */}
      <Dialog open={transferModalOpen} onOpenChange={setTransferModalOpen}>        <DialogContent data-testid="transfer-request-modal">
          <DialogHeader>
            <DialogTitle>
              {ownerConflict?.can_auto_take ? "Assumir o controlo do lead" : "Solicitar transferência do lead"}
            </DialogTitle>
            <DialogDescription>
              {ownerConflict?.can_auto_take ? (
                <>Este lead está actualmente com <b>{ownerConflict?.owner_name}</b>. Como Consultor de Cuidados, ao confirmar assume imediatamente o controlo — não precisa de aprovação. Uma nota curta serve apenas para o registo interno.</>
              ) : (
                <>Este lead está atribuído a <b>{ownerConflict?.owner_name}</b>. Explique em poucas palavras porque precisa de assumir — o pedido é enviado ao admin (Rui Pinto) para aprovar.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={transferReason}
            onChange={(e) => setTransferReason(e.target.value)}
            placeholder={ownerConflict?.can_auto_take
              ? "Ex.: Vou fazer a RT amanhã de manhã com o cliente."
              : "Ex.: Cliente ligou-me directamente pedindo continuidade do processo."}
            className="w-full min-h-[100px] border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary/50"
            data-testid="transfer-request-reason"
            maxLength={500}
          />
          <div className="text-[10px] text-muted-foreground text-right">{transferReason.length}/500</div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setTransferModalOpen(false)}
              className="px-4 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted"
              data-testid="transfer-request-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={transferBusy || transferReason.trim().length < 3}
              onClick={async () => {
                setTransferBusy(true);
                try {
                  const { data } = await api.post(`/leads/${id}/transfer-request`, { reason: transferReason.trim() });
                  if (data?.auto_approved) {
                    toast.success("Assumiu o controlo do lead");
                    // Reload the lead so the owner_conflict banner disappears
                    setOwnerConflict(null);
                    setExistingTransferReq(null);
                    await load();
                  } else if (data?.already_pending) {
                    toast.info("Já existe um pedido pendente para este lead.");
                  } else {
                    toast.success("Pedido enviado — o admin será notificado.");
                    setExistingTransferReq(data?.request?.id || null);
                  }
                  setTransferModalOpen(false);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erro ao enviar pedido");
                } finally { setTransferBusy(false); }
              }}
              className="px-4 py-2 rounded-md text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
              data-testid="transfer-request-submit"
            >
              {transferBusy ? "A enviar..." : (ownerConflict?.can_auto_take ? "Assumir agora" : "Enviar pedido")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 25/02/2026 — Confirmação antes de enviar a Proposta ao cliente.
          Substitui o gate anterior de "Validar valores (RT)". Mostra os
          valores-chave para o Consultor confirmar visualmente que estão
          correctos antes do envio. */}
      <Dialog open={sendProposalConfirmOpen} onOpenChange={(o) => { if (!busy) setSendProposalConfirmOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="send-proposal-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Confirmar envio da Proposta</DialogTitle>
            <DialogDescription>
              Reveja os valores abaixo antes de enviar ao cliente. Uma vez enviada, o cliente recebe imediatamente o email com o link da proposta.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const primary = (lead.estimate?.options || []).find(o => o.is_primary) || (lead.estimate?.options || [])[0] || {};
            const plan = lead.deposit?.plan || {};
            const isPontual = (primary.mode || "continuidade") === "pontual";
            const fmt = (n) => `${Number(n || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
            const items = [
              { label: "Opção principal", value: primary.name || "—" },
              isPontual
                ? { label: "Total pontual", value: fmt(primary.total_value) }
                : { label: "Valor mensal", value: fmt(primary.monthly_avg) },
              { label: "Total da caução", value: fmt(plan.total_amount) },
              { label: "Arranque de recrutamento", value: fmt(plan.kickstart_amount) },
              { label: "Início do serviço", value: plan.service_start_date ? new Date(plan.service_start_date).toLocaleDateString("pt-PT") : "—" },
              { label: "Cliente", value: lead.qualification?.contact?.name || "—" },
              { label: "Email de envio", value: lead.qualification?.contact?.email || "—" },
            ];
            return (
              <div className="space-y-2 my-3" data-testid="send-proposal-confirm-values">
                {items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-2 last:border-b-0">
                    <span className="text-muted-foreground">{it.label}</span>
                    <span className="font-medium text-foreground tabular-nums">{it.value}</span>
                  </div>
                ))}
              </div>
            );
          })()}
          <div className="rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-3 text-xs text-[hsl(var(--warning))]">
            Confirma que estes valores estão correctos antes de enviar? Depois de enviada, o cliente receberá imediatamente o email com o link para aceitar a proposta.
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setSendProposalConfirmOpen(false)}
              disabled={busy}
              data-testid="send-proposal-confirm-cancel"
              className="inline-flex items-center gap-2 bg-muted hover:bg-muted/80 border border-border rounded-full px-5 py-2 text-sm disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmAndSendProposal}
              disabled={busy}
              data-testid="send-proposal-confirm-send"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-full px-5 py-2 text-sm disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Valores correctos · Enviar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 25/02/2026 — Diagnóstico de entregas do convite Teams */}
      <Dialog open={deliveryLogsOpen} onOpenChange={setDeliveryLogsOpen}>
        <DialogContent className="max-w-3xl" data-testid="delivery-logs-modal">
          <DialogHeader>
            <DialogTitle>Histórico de entregas do convite Teams</DialogTitle>
            <DialogDescription>
              Cada tentativa de entrega ao cliente (create inicial + reenvios manuais). Útil para responder rapidamente a "porque é que X não recebeu?".
            </DialogDescription>
          </DialogHeader>
          {deliveryLogs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
              Sem tentativas registadas ainda.
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="delivery-logs-list">
              {deliveryLogs.map((log, idx) => {
                const badge = {
                  "forward": { bg: "bg-[hsl(var(--success-bg))]", border: "border-[hsl(var(--success))]/25", txt: "text-[hsl(var(--success))]", label: "✓ forward" },
                  "forward+ics": { bg: "bg-[hsl(var(--success-bg))]", border: "border-[hsl(var(--success))]/25", txt: "text-[hsl(var(--success))]", label: "✓ forward + ics" },
                  "ics": { bg: "bg-[hsl(var(--info-bg))]", border: "border-[hsl(var(--info))]/25", txt: "text-[hsl(var(--info))]", label: "✓ ics" },
                  "failed": { bg: "bg-[hsl(var(--danger-bg))]", border: "border-[hsl(var(--danger))]/25", txt: "text-[hsl(var(--danger))]", label: "✕ failed" },
                  "skipped": { bg: "bg-muted/40", border: "border-border", txt: "text-muted-foreground", label: "— skipped" },
                }[log.delivery] || { bg: "bg-muted/40", border: "border-border", txt: "text-muted-foreground", label: log.delivery || "?" };
                return (
                  <div key={idx} className={`${badge.bg} ${badge.border} border rounded-md p-2.5`} data-testid={`delivery-log-${idx}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-[0.1em] ${badge.txt}`}>{badge.label}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {log.created_at ? new Date(log.created_at).toLocaleString("pt-PT") : ""}
                        {" · "}{log.source || "?"}
                      </span>
                    </div>
                    <div className="text-[11px] text-foreground mb-1">{log.detail}</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground font-mono">
                      <div>to: <b className="text-foreground">{log.to}</b></div>
                      <div>external: <b>{log.external ? "sim" : "não"}</b></div>
                      <div>forward: <b>{log.forward_ok ? `${log.forward_status || "OK"} ✓` : (log.forward_status || "—")}</b></div>
                      <div>ics: <b>{log.ics_attempts ? `${log.ics_attempts}x → ${log.ics_status || "?"}` : "—"}</b></div>
                      {log.forward_error && <div className="col-span-2 text-[hsl(var(--danger))]">fwd err: {log.forward_error}</div>}
                      {log.ics_error && <div className="col-span-2 text-[hsl(var(--danger))]">ics err: {log.ics_error}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Zoho CRM status + Notas + Resumo IA */}
      <Section
        title="Estado no Zoho CRM & Notas"
        data-testid="zoho-status-notes-section"
        action={
          <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="zoho-status-picker">
            {lead.zoho_converted && (
              <span
                data-testid="zoho-converted-badge"
                className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-muted border border-border text-foreground font-semibold"
                title={lead.zoho_converted_at ? `Convertido em ${new Date(lead.zoho_converted_at).toLocaleString('pt-PT')}` : "Convertido no Zoho"}
              >
                Convertido no Zoho
              </span>
            )}
            <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Estado (Zoho):</label>
            <select
              data-testid="zoho-status-select"
              disabled={zohoStatusBusy || zohoStatusesLoading || lead.zoho_converted}
              value={lead.zoho_lead_status || ""}
              onChange={async (e) => {
                const newStatus = e.target.value;
                if (!newStatus) return;
                // Check if it's a disqualification-type status → open reason modal
                const disqualHints = ["não qualificado", "nao qualificado", "not qualified",
                                        "junk lead", "lost lead", "perdido"];
                const isDisqual = disqualHints.some(h => newStatus.toLowerCase().includes(h));
                if (isDisqual) {
                  setDisqualDialog({ open: true, status: newStatus, reason: "" });
                  return;
                }
                setZohoStatusBusy(true);
                try {
                  const r = await api.post(`/leads/${id}/zoho-status`, { zoho_status: newStatus });
                  setLead(r.data);
                  toast.success(`Estado Zoho atualizado: ${newStatus}`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Erro ao atualizar estado Zoho");
                } finally {
                  setZohoStatusBusy(false);
                }
              }}
              className={`text-xs bg-card border border-border rounded-full px-3 py-1.5 min-w-[180px] ${lead.zoho_converted ? "opacity-60 cursor-not-allowed" : ""}`}
              title={lead.zoho_converted ? "Lead convertido no Zoho — estado só editável no Zoho CRM" : ""}
            >
              <option value="">
                {zohoStatusesLoading ? "A carregar…" : (zohoStatuses.length === 0 ? "— sem picklist —" : "— escolher —")}
              </option>
              {zohoStatuses.map((s) => (
                <option key={s.actual_value} value={s.actual_value}>{s.display_value}</option>
              ))}
            </select>
            {lead.zoho_lead_status && (
              <span className="text-[10px] text-muted-foreground" data-testid="zoho-status-current">
                Actual: <b>{lead.zoho_lead_status}</b>
              </span>
            )}
          </div>
        }
      >
        {/* 20/02/2026 — Notes activity timeline visible to all roles so
            the Gestor can gauge the workflow state without opening
            Zoho or scrolling the note list. */}
        <div className="mb-4">
          <NotesTimeline lead={lead} />
        </div>

        {/* 20/02/2026 — Only the Atendimento sub-block is rendered
            here; the Consultor's notes are captured by RTNotesBlock
            (below) so the order on the page is Atendimento → Consultor. */}
        <NotesSection lead={lead} onChanged={load} />
      </Section>

      {/* Timeline PBX Linkus/Yeastar — chamadas centralinha associadas
          a este lead. Silenciosa quando não há histórico. */}
      <LinkusCallsTimeline leadId={lead?.id} />

      {/* Consultor's RT notepad — visible only in the Consultor phase
          or later. The block auto-hides for Atendimento to keep their
          inbox uncluttered. AI-powered: after "Melhorar com IA" the
          Consultor gets two apply buttons — internal → Zoho note,
          client → proposal.additional_notes.
          25/02/2026 — Movido para DENTRO do separador Reunião Técnica
          (pedido do utilizador: "isto deveria estar no separador do
          consultor"). O bloco fica agora renderizado só quando o tab
          activo é `rt`, evitando poluição visual nas outras fases. */}

      {/* Contexto para a IA — hint por lead injectado em todos os prompts */}
      <AIHintSection
        lead={lead}
        onSaved={(updated) => setLead(updated)}
      />

      {/* Escritório de origem — usado para a matriz do Relatório Diário */}
      <Section title="Escritório de origem" data-testid="office-section">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs text-muted-foreground">Atribuir a:</label>
          <select
            data-testid="office-select"
            value={lead.office || ""}
            onChange={async (e) => {
              const newOffice = e.target.value || null;
              try {
                const r = await api.patch(`/leads/${id}`, { office: newOffice });
                setLead(r.data);
                toast.success(newOffice ? `Escritório: ${newOffice}` : "Escritório removido");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Erro ao atualizar escritório");
              }
            }}
            className="text-sm bg-card border border-border rounded-full px-4 py-1.5 min-w-[180px]"
          >
            <option value="">— sem escritório —</option>
            <option value="Porto">Porto</option>
            <option value="Braga">Braga</option>
            <option value="Leiria">Leiria</option>
            <option value="Algarve">Algarve</option>
          </select>
          {lead.office && (
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/30 rounded-full px-3 py-1">
              Actual: <b>{lead.office}</b>
            </span>
          )}
          <span className="text-[11px] text-muted-foreground ml-auto">
            Leads sem escritório aparecem em "sem escritório" no Relatório Diário. Leads de Lisboa e Setúbal são automaticamente marcados "não qualificado".
          </span>
        </div>
      </Section>

      {/* Dialog: justificar desqualificação (Zoho status disqualifier) */}
      <Dialog open={disqualDialog.open} onOpenChange={(open) => setDisqualDialog(d => ({ ...d, open }))}>
        <DialogContent data-testid="disqual-dialog">
          <DialogHeader>
            <DialogTitle>Justificação — {disqualDialog.status}</DialogTitle>
            <DialogDescription>
              Antes de marcar este lead como <b>{disqualDialog.status}</b> é obrigatório indicar o motivo. Este texto vai ficar registado como <b>Nota no Zoho CRM</b> e visível na ficha do lead.
            </DialogDescription>
          </DialogHeader>
          <textarea
            data-testid="disqual-reason"
            autoFocus
            className="w-full mt-2 bg-card border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary resize-y min-h-[120px]"
            placeholder="Ex.: 'Cliente reside fora da área de cobertura' · 'Sem intenção de contratar' · 'Contactou outro fornecedor'…"
            value={disqualDialog.reason}
            onChange={(e) => setDisqualDialog(d => ({ ...d, reason: e.target.value }))}
          />
          <DialogFooter>
            <button
              onClick={() => setDisqualDialog({ open: false, status: "", reason: "" })}
              className="text-sm text-muted-foreground hover:text-foreground px-4 py-2"
              data-testid="disqual-cancel">
              Cancelar
            </button>
            <button
              disabled={zohoStatusBusy || !disqualDialog.reason.trim()}
              onClick={async () => {
                setZohoStatusBusy(true);
                try {
                  const r = await api.post(`/leads/${id}/zoho-status`, {
                    zoho_status: disqualDialog.status,
                    reason: disqualDialog.reason.trim(),
                  });
                  setLead(r.data);
                  toast.success(`Marcado como ${disqualDialog.status}. Motivo registado no Zoho.`);
                  setDisqualDialog({ open: false, status: "", reason: "" });
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erro ao registar desqualificação");
                } finally {
                  setZohoStatusBusy(false);
                }
              }}
              className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background font-semibold rounded-full px-6 py-2 text-sm disabled:opacity-50"
              data-testid="disqual-confirm">
              {zohoStatusBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Confirmar & registar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 25/02/2026 — Separadores de fase (pedido do utilizador para
          reduzir a poluição visual). Os blocos cross-cutting (Zoho notes,
          emails inbound, Linkus, AI hint, escritório) ficam sempre
          visíveis acima. A partir daqui a página divide-se em três
          fases operacionais claras. */}
      <div className="border-b border-border flex items-center gap-1 mb-6 overflow-x-auto -mx-1 px-1" data-testid="lead-phase-tabs">
        {[
          { key: "atendimento", label: "Fase Atendimento", sub: "SAC · Qualificação · Estimativa" },
          { key: "rt", label: "Reunião Técnica", sub: "Consultor · Validação · Care Plan" },
          { key: "proposta", label: "Proposta & Onboarding", sub: "Envio · Contrato · Caução · Recrutamento" },
          { key: "dt", label: "Direção Técnica", sub: "Plano de Cuidados · AF · Exports" },
        ].filter((t) => isTabAllowed(t.key)).map((t) => {
          const active = activeTab === t.key;
          const sec = TAB_SECTION[t.key];
          const readOnlyChip = sec && permsReady && !canEditSection(sec);
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              data-testid={`lead-phase-tab-${t.key}`}
              className={`relative shrink-0 text-left px-4 py-3 group transition-colors ${
                active ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"
              }`}
            >
              <div className="text-sm font-ui flex items-center gap-1.5">
                {t.label}
                {readOnlyChip && (
                  <span
                    data-testid={`tab-readonly-${t.key}`}
                    title={`Só leitura para o teu role — ajusta em /settings/permissions`}
                    className="inline-flex items-center gap-0.5 text-[8px] uppercase tracking-widest bg-muted text-muted-foreground border border-border rounded-full px-1.5 py-0.5 leading-none"
                  >
                    Só ler
                  </span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/80 mt-0.5">{t.sub}</div>
              {active && (
                <div className="absolute -bottom-px left-2 right-2 h-[2px] bg-foreground rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {activeTab === "atendimento" && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="phase-atendimento">
        <Section title="Contacto">
          <div className="grid grid-cols-2 gap-5">
            <Kv label="Nome" value={c.name} />
            <Kv label="Telefone" value={c.phone} />
            <Kv label="Email" value={c.email} />
            <Kv label="Relação" value={c.relationship} />
            <Kv label="Origem" value={c.source} />
            <Kv label="Idioma" value={c.language} />
          </div>
        </Section>
        <Section title={lead.qualification?.has_second_beneficiary ? "Pessoas cuidadas & Localização" : "Pessoa cuidada & Localização"}>
          <div className="grid grid-cols-2 gap-5">
            {lead.qualification?.has_second_beneficiary ? (
              <>
                <div className="col-span-2 grid grid-cols-2 gap-5 bg-muted/40 border border-primary/30 rounded-lg px-4 py-3">
                  <div className="col-span-2 text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold">Beneficiário 1</div>
                  <Kv label="Nome" value={cp.name} />
                  <Kv label="Idade" value={cp.age} />
                  <Kv label="Sexo" value={cp.sex} />
                </div>
                <div className="col-span-2 grid grid-cols-2 gap-5 bg-muted/40 border border-primary/30 rounded-lg px-4 py-3">
                  <div className="col-span-2 text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold">Beneficiário 2</div>
                  <Kv label="Nome" value={lead.qualification?.cared_person_2?.name} />
                  <Kv label="Idade" value={lead.qualification?.cared_person_2?.age} />
                  <Kv label="Sexo" value={lead.qualification?.cared_person_2?.sex} />
                </div>
              </>
            ) : (
              <>
                <Kv label="Nome" value={cp.name} />
                <Kv label="Idade" value={cp.age} />
                <Kv label="Sexo" value={cp.sex} />
              </>
            )}
            <Kv label="Concelho" value={loc.municipality} />
            <Kv label="Distrito" value={loc.district} />
            <Kv label="Código postal" value={loc.postal_code} />
          </div>
        </Section>
        <Section title="Serviço pretendido">
          <div className="grid grid-cols-2 gap-5">
            <Kv label="Tipos" value={(sv.service_types||[]).join(", ")} />
            <Kv label="Urgência" value={sv.urgency} />
            <Kv label="Pessoas" value={sv.people_count} />
            <Kv label="Cuidador atual" value={sv.has_current_caregiver === null ? "—" : sv.has_current_caregiver ? "Sim" : "Não"} />
            <Kv label="Data início" value={sv.start_date} />
            <Kv label="Horário" value={sv.schedule} />
            <Kv label="Dias/semana" value={sv.days_per_week} />
            <Kv label="Necessidades" value={(sv.needs||[]).join(", ")} />
          </div>
          {sv.notes && <div className="mt-4 text-sm text-muted-foreground">{sv.notes}</div>}

          {/* Transportes públicos regulares — controlo editável.
              Bloqueia a estimativa se marcado "Não". */}
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-4 flex-wrap" data-testid="transport-public-regular-toggle">
            <div className="text-xs text-muted-foreground">
              <b>Transportes públicos regulares</b> para o local do serviço?
              <span className="ml-2 text-muted-foreground">(marcar "Não" bloqueia estimativa)</span>
            </div>
            <div className="inline-flex items-center gap-2">
              {["yes", "no", ""].map((v) => {
                const label = v === "yes" ? "Sim" : v === "no" ? "Não" : "N/A";
                const current = (sv.transport_public_regular || "").toLowerCase();
                const active = current === v || (v === "" && !current);
                return (
                  <button
                    key={v || "na"}
                    type="button"
                    data-testid={`transport-public-${v || "na"}`}
                    onClick={async () => {
                      try {
                        const r = await api.patch(`/leads/${id}`, {
                          qualification: {
                            ...lead.qualification,
                            service: {
                              ...(lead.qualification?.service || {}),
                              transport_public_regular: v || null,
                            },
                          },
                        });
                        setLead(r.data);
                        if (v === "no") {
                          toast.info("Estimativa bloqueada — agende Reunião Técnica.");
                        } else {
                          toast.success("Actualizado.");
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Erro");
                      }
                    }}
                    className={`text-xs rounded-full px-3 py-1 border transition-colors ${
                      active
                        ? (v === "no"
                            ? "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))] font-semibold"
                            : v === "yes"
                              ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/40 text-[hsl(var(--success))] font-semibold"
                              : "bg-muted border-border text-muted-foreground")
                        : "bg-card border-border text-muted-foreground hover:bg-muted/40"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        {/* Resumo automático (IA) — ocupa toda a largura por baixo dos dados gerais */}
        <div className="lg:col-span-2" data-testid="est-summary-block">
          <Section
            title={<span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-foreground" /> Resumo automático (IA)</span>}
            action={
              (est?.summary || currentUser?.role === "admin" || currentUser?.role === "gestor" || currentUser?.role === "atendimento") && (
                <button
                  onClick={() => { setSummaryDraft(est?.summary || ""); setSummaryModalOpen(true); }}
                  disabled={!est}
                  className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="btn-open-summary-modal"
                >
                  <Expand className="w-3.5 h-3.5" /> Editar em janela ampla
                </button>
              )
            }
          >
            {!est ? (
              <div className="text-sm text-muted-foreground">O resumo é gerado ao criar a estimativa a partir dos dados da qualificação inicial.</div>
            ) : est.summary ? (
              <div className="text-sm text-muted-foreground italic leading-relaxed whitespace-pre-wrap bg-muted/40 border border-primary/40 rounded-lg p-4" data-testid="est-summary-preview">
                {est.summary}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic bg-muted/40 border border-dashed border-border rounded-lg p-4">Sem resumo. Carrega em "Editar em janela ampla" para adicionar.</div>
            )}
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">Gerado a partir da qualificação inicial · editável antes de enviar</div>
          </Section>
        </div>

        {lead.qualification?.custom_data && Object.keys(lead.qualification.custom_data).length > 0 && (
          <Section title="Campos adicionais">
            <div className="grid grid-cols-2 gap-5">
              {Object.entries(lead.qualification.custom_data).map(([k, v]) => {
                let disp = v;
                if (Array.isArray(v)) disp = v.join(", ");
                else if (v && typeof v === "object" && v.name) disp = `📎 ${v.name}`;
                else if (typeof v === "boolean") disp = v ? "Sim" : "Não";
                else if (v === null || v === undefined || v === "") disp = "—";
                return <Kv key={k} label={k} value={String(disp)} />;
              })}
            </div>
          </Section>
        )}

        <div className="lg:col-span-2">
        <Section title="Estimativa" action={
          <div className="flex items-center gap-2">
            {est && <EstimatePreparerBadge lead={lead} currentUser={currentUser} onChanged={load} />}
            {est && !["estimativa_enviada","qt_agendada","qt_realizada","proposta_enviada","aceite","recusada"].includes(lead.status) && (
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-amber-500/10 text-[hsl(var(--warning))] border border-amber-500/30" data-testid="estimate-draft-badge">Rascunho</span>
            )}
            {est && ["estimativa_enviada","qt_agendada","qt_realizada","proposta_enviada","aceite"].includes(lead.status) && (
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-emerald-500/10 text-[hsl(var(--success))] border border-emerald-500/30">Enviada</span>
            )}
            {!estimateBlocked && (() => {
              // 06/08/2026 — Regerar fica INATIVO depois de o Consultor
              // valorizar um serviço pontual. Os valores da estimativa
              // passam a ser autoritativos (`pontual_pricing.total_value`
              // → `hourly_price` derivado) e uma "regeração" iria
              // recalcular sobre o catálogo, apagando o preço fixado.
              //
              // 10/08/2026 — Bug fix: o bloqueio só se aplica à
              // REGERAÇÃO (est existe). A primeira "Gerar" DEVE ser
              // permitida — é precisamente essa a geração inicial que
              // pega no ``pontual_pricing.total_value`` para preencher
              // a estimativa. Antes o botão ficava disabled para
              // pontuais valorizados sem estimativa ainda, contradizendo
              // o objectivo do próprio fluxo de "definir preço".
              const pontualPriced = Boolean(lead?.pontual_pricing?.priced_at);
              const lockRegen = Boolean(est) && pontualPriced;
              const disabled = busy || lockRegen;
              const title = lockRegen
                ? "Serviço pontual já valorizado pelo Consultor — regerar inactivo"
                : undefined;
              return (
                <button
                  data-testid="btn-generate-estimate"
                  onClick={doGenerateEstimate}
                  disabled={disabled}
                  title={title}
                  className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border ${lockRegen ? "bg-muted/40 border-border text-muted-foreground cursor-not-allowed" : "bg-muted hover:bg-muted border-border"} disabled:opacity-60`}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5" />}
                  {est ? "Regerar" : "Gerar"}
                  {lockRegen && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] opacity-70">
                      <Lock className="w-3 h-3" />
                    </span>
                  )}
                </button>
              );
            })()}
            {est && (currentUser?.role === "gestor" || currentUser?.role === "admin") && (
              <button data-testid="btn-open-simulator"
                onClick={() => setSimulatorOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs bg-primary hover:bg-primary/90 text-primary-foreground border border-primary rounded-full px-3 py-1.5 font-heading tracking-tight">
                <Calculator className="w-3.5 h-3.5" /> Simulador
                {est.margin_validated_at && (
                  <span className="ml-1 inline-flex items-center gap-0.5 text-[10px]">
                    <CheckCircle2 className="w-3 h-3" />
                  </span>
                )}
              </button>
            )}
          </div>
        }>
          {estimateBlocked && !est ? (
            <div className="text-sm text-[#78350F] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 rounded-lg p-4" data-testid="estimate-blocked-note">
              {estimateBlockedReason || "Este lead é do Algarve. Não é possível gerar valores no Atendimento — agende a Reunião Técnica com um Gestor de Conta para que ele apresente a estimativa."}
            </div>
          ) : (!est ? <div className="text-sm text-muted-foreground">Ainda não foi gerada estimativa.</div> : (() => {
            // 20/02/2026 — isGestorOrAdmin hoisted to the component
            // scope so downstream JSX (send-proposal button etc) can
            // reference it. Legacy comment kept here for context —
            // Consultor de Cuidados owns the RT flow (run_qt).
            const inPreSend = !["estimativa_enviada","qt_agendada","qt_realizada","proposta_enviada","aceite","recusada"].includes(lead.status);
            const inQTPhase = ["estimativa_enviada","qt_agendada"].includes(lead.status);
            // 04/08/2026 — Atendimento can keep iterating on the estimate
            // even after the first send until the QT is confirmed. This
            // lets them prepare V2/V3 when the client asks for a revised
            // proposal. History is preserved automatically at each send
            // (see estimate_history in server.py::send_estimate).
            const canAtendimentoStillEdit = isAtendimento
              && inQTPhase
              && !est.qt_confirmed_at;
            const canEdit = !estimateBlocked && (
              inPreSend
              || (inQTPhase && isGestorOrAdmin)
              || (!est.qt_confirmed_at && isGestorOrAdmin)
              || canAtendimentoStillEdit
            );
            const inputCls = "bg-card border border-border rounded px-2 py-1 text-xs text-right disabled:opacity-70";
            const options = est.options || [];
            const isPontual = (est.continuity_type || "") === "pontual";
            const fmt = (n) => (Number(n) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return (
              <div className="space-y-5" data-testid="estimate-editor">
                {!est.qt_confirmed_at && isGestorOrAdmin && options.length > 0 && (() => {
                  const primary = options.find(o => o.is_primary) || options[0];
                  const isPontualP = (primary.mode || est.continuity_type || "continuidade") === "pontual";
                  return (
                    <div className="bg-gradient-to-br from-muted/40 to-muted/40 border border-primary/50 rounded-xl p-5" data-testid="qt-validation-callout">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/25 border border-primary/40 flex items-center justify-center flex-shrink-0">
                          <ClipboardCheck className="w-5 h-5 text-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">Ação necessária</div>
                          <div className="text-[15px] text-foreground font-medium mt-0.5">Validar os valores para gerar a proposta</div>
                          <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed">
                            Reveja os valores da opção principal (<b>{primary.name || "—"}</b>) e confirme na <b>Reunião Técnica</b>. Só depois de validado é que a Proposta formal pode ser gerada.
                          </p>
                          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                            <div className="bg-card/60 border border-primary/30 rounded-lg px-3 py-2">
                              <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
                                {isPontualP ? "Total do serviço" : "Média mensal"}
                              </div>
                              <div className="font-display text-lg text-foreground">
                                {fmt(isPontualP ? primary.total_value : primary.monthly_avg)} €
                              </div>
                            </div>
                            <div className="bg-card/60 border border-primary/30 rounded-lg px-3 py-2">
                              <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
                                {isPontualP ? "Total horas" : "Horas/mês"}
                              </div>
                              <div className="font-display text-lg text-foreground">
                                {(isPontualP ? primary.total_hours : primary.monthly_hours || 0).toFixed(1)}h
                              </div>
                            </div>
                            {!isPontualP && (
                              <div className="bg-card/60 border border-primary/30 rounded-lg px-3 py-2">
                                <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Frequência</div>
                                <div className="font-display text-lg text-foreground">
                                  {primary.days_per_week || 0}d × {primary.hours_per_day || 0}h
                                </div>
                              </div>
                            )}
                            {isPontualP && (
                              <div className="bg-card/60 border border-primary/30 rounded-lg px-3 py-2">
                                <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Dias/horas</div>
                                <div className="font-display text-lg text-foreground">
                                  {primary.total_days || 0}d × {primary.hours_per_day || 0}h
                                </div>
                              </div>
                            )}
                            <div className="bg-card/60 border border-primary/30 rounded-lg px-3 py-2">
                              <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Preço/hora</div>
                              <div className="font-display text-lg text-foreground">{fmt(primary.hourly_price || 0)} €</div>
                            </div>
                          </div>
                          <p className="text-[11px] text-foreground mt-3 italic">
                            💡 Se precisar editar valores, faça-o diretamente nas linhas abaixo antes de validar. Depois clique em <b>“Validar valores (RT)”</b> ou <b>“Ajustar valores”</b> (se alterou algo).
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded-full bg-primary/15 border border-primary/40">
                    Modo: {isPontual ? "Pontual (dias efetivos)" : "Continuidade (dias/semana)"}
                  </span>
                  <span className="text-muted-foreground normal-case tracking-normal font-normal">Preço/hora vem da tabela — restantes valores editáveis por lead.</span>
                </div>
                {isPontual && (
                  <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-4 space-y-3" data-testid="est-service-window-card">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold flex items-center gap-2">
                      Período do serviço pontual
                      <span className="text-foreground normal-case tracking-normal font-normal">(obrigatório antes de enviar ao cliente)</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Data de início</span>
                        <input type="date" disabled={!canEdit}
                                defaultValue={est.service_start_date || ""}
                                onBlur={(e) => {
                                  const v = e.target.value || null;
                                  if ((est.service_start_date || null) !== v) updateServiceWindow({ service_start_date: v });
                                }}
                                className={`${inputCls} w-full`}
                                data-testid="est-service-start-date" />
                      </label>
                      <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Data de fim</span>
                        <input type="date" disabled={!canEdit}
                                defaultValue={est.service_end_date || ""}
                                min={est.service_start_date || undefined}
                                onBlur={(e) => {
                                  const v = e.target.value || null;
                                  if ((est.service_end_date || null) !== v) updateServiceWindow({ service_end_date: v });
                                }}
                                className={`${inputCls} w-full`}
                                data-testid="est-service-end-date" />
                      </label>
                      <div className="text-sm">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Total de dias</div>
                        <div className="font-mono text-foreground text-lg" data-testid="est-service-window-days">
                          {(() => {
                            const s = est.service_start_date, e = est.service_end_date;
                            if (!s || !e) return "—";
                            const ds = new Date(s), de = new Date(e);
                            if (isNaN(ds) || isNaN(de) || de < ds) return "—";
                            const d = Math.round((de - ds) / 86400000) + 1;
                            return `${d} dia(s)`;
                          })()}
                        </div>
                      </div>
                    </div>
                    {(!est.service_start_date || !est.service_end_date) && (
                      <div className="text-[12px] text-primary bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/50 rounded-md px-3 py-2" data-testid="est-service-window-warning">
                        Preencha as datas de início e fim antes de enviar a estimativa ao cliente. O total de dias efetivos é recalculado automaticamente em todas as opções pontuais.
                      </div>
                    )}
                  </div>
                )}
                {options.length === 0 && (
                  <div className="text-xs text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded p-3">
                    Nenhuma linha ativa na tabela de preços. Configure em <a href="/settings/pricing" className="underline">/settings/pricing</a>.
                  </div>
                )}
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                        <th className="text-left py-2 px-2 w-6"></th>
                        <th className="text-left py-2 px-2">Serviço</th>
                        {isPontual ? (<>
                          <th className="text-right py-2 px-2 w-20">Dias efetivos</th>
                          <th className="text-right py-2 px-2 w-14">H/Dia</th>
                          <th className="text-right py-2 px-2 w-16">Total H</th>
                          <th className="text-right py-2 px-2 w-20">P.Venda/H</th>
                          <th className="text-right py-2 px-2 w-24">Extra (€)</th>
                          <th className="text-right py-2 px-2 w-24">Total</th>
                        </>) : (<>
                          <th className="text-right py-2 px-2 w-16">Dias/Sem</th>
                          <th className="text-right py-2 px-2 w-14">H/Dia</th>
                          <th className="text-right py-2 px-2 w-14">H/Sem</th>
                          <th className="text-right py-2 px-2 w-20">P.Venda/H</th>
                          <th className="text-right py-2 px-2 w-24">Valor Semana</th>
                          <th className="text-right py-2 px-2 w-24">Média Mensal</th>
                        </>)}
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {options.map((o) => (
                        <tr key={o.id} className={`border-b border-border/60 ${o.is_primary ? "bg-primary/10" : ""}`} data-testid={`opt-${o.id}`}>
                          <td className="py-2 px-2">
                            <input type="radio" name="primary" checked={!!o.is_primary} disabled={!canEdit} onChange={()=>setPrimaryOption(o.id)} data-testid={`opt-primary-${o.id}`} className="accent-[#1D4ED8]" />
                          </td>
                          <td className="py-2 px-2 font-semibold text-foreground">
                            {canEdit ? (
                              <input
                                type="text"
                                defaultValue={o.name || ""}
                                onBlur={(e)=>{
                                  const v = (e.target.value || "").trim();
                                  if (!v) { e.target.value = o.name || ""; return; }
                                  if (v !== (o.name || "")) updateOptionField(o.id, { name: v });
                                }}
                                onKeyDown={(e)=>{ if (e.key === "Enter") e.target.blur(); }}
                                className="bg-transparent border border-transparent hover:border-border focus:border-primary focus:bg-card rounded px-1.5 py-0.5 text-sm font-semibold text-foreground w-40 sm:w-56 transition-colors"
                                data-testid={`opt-name-${o.id}`}
                                title="Editar nome da linha"
                              />
                            ) : (
                              <span>{o.name}</span>
                            )}
                            {o.is_primary && <span className="ml-1 text-[9px] uppercase tracking-[0.15em] text-foreground">sugerida</span>}
                          </td>
                          {isPontual ? (<>
                            <td className="py-2 px-2 text-right">
                              <input type="number" step="1" disabled={!canEdit} defaultValue={o.total_days ?? 0} onBlur={(e)=>{const v=parseFloat(e.target.value||0); if(v!==o.total_days) updateOptionField(o.id,{total_days:v});}} className={`${inputCls} w-20`} data-testid={`opt-days-${o.id}`} />
                            </td>
                            <td className="py-2 px-2 text-right">
                              <input type="number" step="0.5" disabled={!canEdit} defaultValue={o.hours_per_day} onBlur={(e)=>{const v=parseFloat(e.target.value||0); if(v!==o.hours_per_day) updateOptionField(o.id,{hours_per_day:v});}} className={`${inputCls} w-14`} data-testid={`opt-hpd-${o.id}`} />
                            </td>
                            <td className="py-2 px-2 text-right text-xs text-muted-foreground font-mono">{fmt(o.total_hours)}</td>
                            <td className="py-2 px-2 text-right">
                              <input type="number" step="0.01" disabled={!canEdit} defaultValue={o.hourly_price} onBlur={(e)=>{const v=parseFloat(e.target.value||0); if(v!==o.hourly_price) updateOptionField(o.id,{hourly_price:v});}} className={`${inputCls} w-20`} data-testid={`opt-price-${o.id}`} />
                            </td>
                            <td className="py-2 px-2 text-right">
                              <input type="number" step="1" disabled={!canEdit} defaultValue={o.extra_fee ?? 0} onBlur={(e)=>{const v=parseFloat(e.target.value||0); if(v!==o.extra_fee) updateOptionField(o.id,{extra_fee:v});}} className={`${inputCls} w-24`} data-testid={`opt-extra-${o.id}`} />
                            </td>
                            <td className={`py-2 px-2 text-right font-mono ${o.is_primary ? "text-foreground font-semibold" : "text-muted-foreground"}`}>{fmt(o.total_value)} €</td>
                          </>) : (<>
                            <td className="py-2 px-2 text-right">
                              <input type="number" step="0.5" disabled={!canEdit} defaultValue={o.days_per_week} onBlur={(e)=>{const v=parseFloat(e.target.value||0); if(v!==o.days_per_week) updateOptionField(o.id,{days_per_week:v});}} className={`${inputCls} w-16`} data-testid={`opt-dpw-${o.id}`} />
                            </td>
                            <td className="py-2 px-2 text-right">
                              <input type="number" step="0.5" disabled={!canEdit} defaultValue={o.hours_per_day} onBlur={(e)=>{const v=parseFloat(e.target.value||0); if(v!==o.hours_per_day) updateOptionField(o.id,{hours_per_day:v});}} className={`${inputCls} w-14`} data-testid={`opt-hpd-${o.id}`} />
                            </td>
                            <td className="py-2 px-2 text-right text-xs text-muted-foreground font-mono">{fmt(o.weekly_hours)}</td>
                            <td className="py-2 px-2 text-right">
                              <input type="number" step="0.01" disabled={!canEdit} defaultValue={o.hourly_price} onBlur={(e)=>{const v=parseFloat(e.target.value||0); if(v!==o.hourly_price) updateOptionField(o.id,{hourly_price:v});}} className={`${inputCls} w-20`} data-testid={`opt-price-${o.id}`} />
                            </td>
                            <td className="py-2 px-2 text-right text-xs text-muted-foreground font-mono">{fmt(o.weekly_value)} €</td>
                            <td className={`py-2 px-2 text-right font-mono ${o.is_primary ? "text-foreground font-semibold" : "text-muted-foreground"}`}>{fmt(o.monthly_avg)} €</td>
                          </>)}
                          <td className="py-2 px-2 text-right">
                            {canEdit && (
                              <button onClick={()=>removeOption(o.id)} className="text-muted-foreground/70 hover:text-rose-500 p-1" title="Remover linha" data-testid={`del-opt-${o.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {/* Multiplier factor lines (nocturno, feriado, etc.). Only
                          shown when continuidade mode + at least one attached
                          to this estimate. Contribution = sum(services) * pct/100. */}
                      {!isPontual && (est.multipliers_applied || []).length > 0 && (() => {
                        const servicesSubtotal = options.reduce((acc, o) => acc + (Number(o.monthly_avg) || 0), 0);
                        return (est.multipliers_applied || []).map((m) => {
                          const contribution = servicesSubtotal * (Number(m.percentage) || 0) / 100;
                          return (
                            <tr key={m.id} className="border-b border-border/60 bg-[hsl(var(--warning-bg))]/30" data-testid={`mult-${m.id}`}>
                              <td className="py-2 px-2"></td>
                              <td className="py-2 px-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/40 font-heading font-semibold">factor</span>
                                  <div>
                                    <div className="font-semibold text-foreground">{m.name}</div>
                                    {m.note && <div className="text-[11px] text-muted-foreground">{m.note}</div>}
                                  </div>
                                </div>
                              </td>
                              {/* Empty numeric cells (days/hours/etc. do not apply) */}
                              <td className="py-2 px-2"></td>
                              <td className="py-2 px-2"></td>
                              <td className="py-2 px-2"></td>
                              <td className="py-2 px-2 text-right font-mono text-muted-foreground text-xs">+{Number(m.percentage).toFixed(1)}%</td>
                              <td className="py-2 px-2 text-right text-xs text-muted-foreground font-mono">—</td>
                              <td className="py-2 px-2 text-right font-mono text-[hsl(var(--warning))] font-semibold" data-testid={`mult-value-${m.id}`}>+ {fmt(contribution)} €</td>
                              <td className="py-2 px-2 text-right">
                                {canEdit && (
                                  <button onClick={()=>removeMultiplier(m.id)} className="text-muted-foreground/70 hover:text-rose-500 p-1" title="Remover factor" data-testid={`del-mult-${m.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                {canEdit && (
                  <div className="pt-1 flex flex-wrap items-center gap-2">
                    {catalogSorted.length > 0 && (
                      <div className="relative group" data-testid="catalog-picker-wrapper">
                        <button type="button"
                                className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-[hsl(var(--warning-bg))] border border-primary text-[hsl(var(--warning))] rounded-full px-3 py-1.5"
                                data-testid="btn-add-from-catalog">
                          <Plus className="w-3.5 h-3.5" /> Adicionar do catálogo
                          <span className="ml-1 text-[10px] text-[#9C7A45]">({catalogSorted.length})</span>
                          <span className="ml-1 text-[10px] text-[#9C7A45]">▾</span>
                        </button>
                        <div className="absolute z-30 mt-1 left-0 hidden group-hover:block group-focus-within:block bg-card border border-border rounded-xl shadow-2xl w-[720px] max-w-[92vw] max-h-[70vh] overflow-y-auto"
                              data-testid="catalog-picker-menu">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold px-4 pt-3 pb-1 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
                            <span>Catálogo (tabela de valores)</span>
                            {catalogSorted.some(r => r._score > 0) && (
                              <span className="text-[9px] tracking-[0.15em] text-foreground">
                                🌟 recomendadas por tipo de serviço
                              </span>
                            )}
                          </div>
                          {/* Search bar — filters by name or hours/day */}
                          <div className="px-3 py-2 border-b border-border/60 bg-muted/40 sticky top-[34px] z-10">
                            <div className="relative">
                              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                              <input
                                type="text"
                                value={catalogSearch}
                                onChange={(e)=>setCatalogSearch(e.target.value)}
                                placeholder="Pesquisar (ex: SEMANA, 8h, noites)"
                                className="w-full pl-7 pr-7 py-1.5 text-xs bg-card border border-border rounded-md focus:outline-none focus:border-primary"
                                data-testid="catalog-search-input"
                                autoComplete="off"
                              />
                              {catalogSearch && (
                                <button
                                  type="button"
                                  onClick={()=>setCatalogSearch("")}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                  data-testid="catalog-search-clear"
                                  title="Limpar"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          <ul className="divide-y divide-[#F1F5F9]" data-testid="catalog-picker-list">
                            {catalogFiltered.length === 0 && (
                              <li className="px-4 py-6 text-center text-[12px] text-muted-foreground" data-testid="catalog-empty">
                                Sem resultados para "{catalogSearch}"
                              </li>
                            )}
                            {catalogFiltered.map((row) => (
                              <li key={row.id}>
                                <button type="button"
                                        onClick={() => addFromCatalog(row)}
                                        className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-3 transition-colors ${
                                          row._alreadyAdded
                                            ? "bg-muted/40 hover:bg-muted/40"
                                            : "hover:bg-muted/40"
                                        }`}
                                        data-testid={`catalog-row-${row.id}`}>
                                  <span className="flex flex-col gap-1 flex-1 min-w-0">
                                    <span className="font-medium text-foreground truncate">{row.name}</span>
                                    <span className="flex items-center gap-1.5 flex-wrap">
                                      {row._score > 0 && (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--warning))] bg-muted border border-primary rounded-full px-1.5 py-0.5"
                                               data-testid={`catalog-badge-recommended-${row.id}`}>
                                          🌟 Recomendada
                                        </span>
                                      )}
                                      {row._alreadyAdded && (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.15em] font-semibold text-muted-foreground bg-muted border border-border rounded-full px-1.5 py-0.5"
                                               data-testid={`catalog-badge-added-${row.id}`}>
                                          já adicionada
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                  <span className="text-[11px] text-muted-foreground tabular-nums shrink-0 self-center">
                                    {Number(row.hourly_price).toFixed(2)}€/h · {row.hours_per_day}h/dia · {row.days_per_week}d/sem
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    <button data-testid="btn-add-option" onClick={addOption} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5">
                      <Plus className="w-3.5 h-3.5" /> Linha personalizada
                    </button>
                    {/* Multiplier picker — only for continuidade estimates */}
                    {!isPontual && pricingMultipliers.length > 0 && (
                      <div className="relative group" data-testid="mult-picker-wrapper">
                        <button type="button"
                                className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--warning-bg))] hover:bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] rounded-full px-3 py-1.5"
                                data-testid="btn-add-multiplier">
                          <Plus className="w-3.5 h-3.5" /> Adicionar factor
                          <span className="ml-1 text-[10px] text-[hsl(var(--warning))]">({pricingMultipliers.length})</span>
                          <span className="ml-1 text-[10px] text-[hsl(var(--warning))]">▾</span>
                        </button>
                        <div className="absolute z-30 mt-1 left-0 hidden group-hover:block group-focus-within:block bg-card border border-border rounded-xl shadow-lg overflow-hidden min-w-[380px] max-h-80 overflow-y-auto"
                              data-testid="mult-picker-menu">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold px-4 pt-3 pb-1 border-b border-border">
                            Factores multiplicadores
                          </div>
                          <ul className="divide-y divide-[#F1F5F9]" data-testid="mult-picker-list">
                            {pricingMultipliers.map((m) => {
                              const alreadyAdded = (est.multipliers_applied || []).some(x => x.source_id === m.id);
                              return (
                                <li key={m.id}>
                                  <button type="button"
                                          onClick={()=>addMultiplierFromCatalog(m.id)}
                                          disabled={alreadyAdded}
                                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-3 transition-colors ${alreadyAdded ? "bg-muted/40 text-muted-foreground cursor-not-allowed" : "hover:bg-muted/40"}`}
                                          data-testid={`mult-row-${m.id}`}>
                                    <div className="flex-1">
                                      <div className="font-semibold text-foreground">{m.name}</div>
                                      {m.note && <div className="text-[11px] text-muted-foreground">{m.note}</div>}
                                    </div>
                                    <div className="text-right shrink-0">
                                      <div className="font-mono text-[hsl(var(--warning))] font-semibold">+{Number(m.percentage).toFixed(1)}%</div>
                                      {alreadyAdded && <div className="text-[10px] text-muted-foreground">já adicionado</div>}
                                    </div>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Notas adicionais (texto livre visível ao cliente)</div>
                  <textarea disabled={!canEdit} rows={3} defaultValue={est.additional_notes || ""} onBlur={(e)=>{ if(e.target.value !== (est.additional_notes||"")) updateAdditionalNotes(e.target.value); }} placeholder="Ex: horários preferenciais, observações sobre o serviço, informações extra que queira comunicar." className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm w-full disabled:opacity-70" data-testid="est-additional-notes" />
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2 flex items-center gap-2">
                    Condições comerciais
                    <span className="text-[9px] tracking-[0.15em] px-1.5 py-0.5 rounded-full bg-primary/15 border border-primary/40 text-foreground">
                      {est.continuity_type === "pontual" ? "Pontual" : "Continuidade"}
                    </span>
                  </div>
                  <textarea disabled={!canEdit} rows={4} defaultValue={est.commercial_terms || ""} onBlur={(e)=>{ if(e.target.value !== est.commercial_terms) updateEstimateTerms(e.target.value); }} className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm w-full disabled:opacity-70" data-testid="est-terms" />
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
                  {(() => {
                    // 06/08/2026 — Nova versão pronta para reenviar:
                    // aparece um banner permanente sempre que já houve
                    // pelo menos um envio ("sent") e depois disso houve
                    // uma regeração/ajuste. Complementa o toast fleeting
                    // do ``doGenerateEstimate`` — pedido explícito do
                    // utilizador para não perder a acção se o toast
                    // desaparecer.
                    const hist = lead.estimate_history || [];
                    if (hist.length < 2) return null;
                    const lastSentIdx = (() => {
                      for (let i = hist.length - 1; i >= 0; i--) {
                        if ((hist[i].reason || "sent") === "sent") return i;
                      }
                      return -1;
                    })();
                    if (lastSentIdx < 0) return null; // nunca foi enviada
                    const hasNewerRevision = hist.slice(lastSentIdx + 1)
                      .some(h => (h.reason || "sent") !== "sent");
                    if (!hasNewerRevision) return null;
                    if (!canEdit) return null;
                    return (
                      <div
                        className="w-full flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] px-4 py-3 mb-2"
                        data-testid="estimate-new-version-banner"
                      >
                        <div className="flex-1">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold mb-1 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> Nova versão pronta
                          </div>
                          <div className="text-sm text-[hsl(var(--success))]">
                            A estimativa foi <b>alterada desde o último envio</b>. Reveja os valores e reenvie a nova versão ao cliente.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={openSendEstimateModal}
                          disabled={busy || options.length === 0 || sendLoading}
                          className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-4 py-2 font-semibold shrink-0 disabled:opacity-50"
                          data-testid="estimate-resend-now-btn"
                        >
                          {sendLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          Reenviar agora
                        </button>
                      </div>
                    );
                  })()}
                  {(() => {
                    // 10/08/2026 — Send gate. Pontuais use a different rule:
                    // instead of the RT decision, the Consultor must have
                    // either placed a technical call OR written a note.
                    // Continuidade keeps the classic RT gate.
                    const pgate = lead?.pontual_send_gate || null;
                    const isPontual = Boolean(pgate?.requires);
                    const pontualUnmet = isPontual && !pgate?.satisfied;
                    const rtDecisionMissing = !isPontual && lead?.technical_meeting_accepted == null;
                    if (canEdit && pontualUnmet) {
                      return (
                        <div className="w-full flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] px-4 py-3 mb-2" data-testid="pontual-gate-warning">
                          <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] font-semibold mb-1">Contacto técnico em falta</div>
                            <div className="text-sm text-[hsl(var(--warning))]">
                              Serviço pontual: antes de enviar a estimativa, registe pelo menos <b>uma chamada técnica</b> ao cliente (via PBX Linkus) <b>OU</b> uma <b>nota no bloco "Notas do Consultor"</b> a documentar o alinhamento.
                            </div>
                            <div className="text-[11px] text-[hsl(var(--warning))]/80 mt-1 flex flex-wrap gap-3">
                              <span data-testid="pontual-gate-note-flag">
                                {pgate?.has_consultor_note ? "✓" : "○"} Nota do Consultor
                              </span>
                              <span data-testid="pontual-gate-call-flag">
                                {pgate?.has_consultor_call ? "✓" : "○"} Chamada técnica registada
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    if (canEdit && rtDecisionMissing) {
                      return (
                        <div className="w-full flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] px-4 py-3 mb-2" data-testid="rt-gate-warning">
                          <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] font-semibold mb-1">Reunião Técnica em falta</div>
                            <div className="text-sm text-[hsl(var(--warning))]">
                              Antes de enviar a estimativa, registe no guião se o cliente <b>aceitou</b> a Reunião Técnica (idealmente já com data marcada) ou se <b>ficou de decidir</b>. É o passo com maior taxa de conversão.
                            </div>
                          </div>
                          <button type="button"
                                   onClick={() => {
                                     setRtDialogDraft({
                                       accepted: lead?.technical_meeting_accepted ?? null,
                                       refuse_reason: lead?.technical_meeting_refuse_reason || "",
                                       date: (lead?.scheduled_qt_at || "").slice(0, 10),
                                       time: (lead?.scheduled_qt_at || "").slice(11, 16),
                                       // Pre-select the consultor already
                                       // assigned; fall back to the legacy
                                       // manager slot for pre-restructure
                                       // leads.
                                       manager_id: (lead?.assigned_consultor_id
                                                     || lead?.assigned_manager_id
                                                     || ""),
                                     });
                                     setRtDialogOpen(true);
                                   }}
                                   className="inline-flex items-center gap-1.5 text-xs bg-amber-500 hover:bg-amber-600 text-primary-foreground rounded-full px-3 py-1.5 font-semibold shrink-0"
                                   data-testid="rt-gate-goto-guiao">
                            <ClipboardCheck className="w-3.5 h-3.5" /> Registar decisão RT
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {canEdit && (() => {
                    // Send disabled: pontual → until gate satisfied.
                    //                continuidade → until RT decision recorded.
                    const pgate = lead?.pontual_send_gate || null;
                    const isPontual = Boolean(pgate?.requires);
                    const gateBlock = isPontual
                      ? !pgate?.satisfied
                      : lead?.technical_meeting_accepted == null;
                    const gateTitle = isPontual
                      ? "Serviço pontual: registe primeiro uma chamada técnica (Linkus) ou uma nota do Consultor."
                      : "Registe primeiro a decisão sobre a Reunião Técnica no guião";
                    const disabled = busy || options.length === 0 || sendLoading || gateBlock;
                    return (
                      <button data-testid="btn-send-estimate"
                               onClick={openSendEstimateModal}
                               disabled={disabled}
                               title={gateBlock ? gateTitle : ""}
                               className="inline-flex items-center gap-1.5 text-xs bg-primary/25 hover:bg-primary/40 border border-primary/40 text-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                        {sendLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {(lead.estimate_history || []).length > 0 ? "Reenviar ao cliente" : "Enviar ao cliente"}
                      </button>
                    );
                  })()}
                  {/* 04/08/2026 — Estimate version history badge.
                      Shows total versions (sent + regen snapshots) and
                      opens a modal with the audit trail (values + who
                      + when + reason). */}
                  {(lead.estimate_history || []).length > 0 && (() => {
                    const hist = lead.estimate_history || [];
                    const sentCount = hist.filter(h => (h.reason || "sent") === "sent").length;
                    const label = sentCount > 0
                      ? `Versões: ${hist.length} · ${sentCount} enviada${sentCount === 1 ? "" : "s"}`
                      : `Versões: ${hist.length}`;
                    return (
                      <button
                        type="button"
                        data-testid="btn-estimate-history"
                        onClick={() => setHistoryOpen(true)}
                        title="Ver histórico de versões (envios + regerações)"
                        className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--warning-bg))] hover:bg-amber-200 border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] rounded-full px-3 py-1.5 font-semibold"
                      >
                        <History className="w-3.5 h-3.5" />
                        {label}
                      </button>
                    );
                  })()}
                  {!est.qt_confirmed_at && isGestorOrAdmin && options.length > 0 && (
                    <>
                      <button data-testid="btn-qt-confirm" onClick={qtConfirmEstimate} disabled={busy} className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-[hsl(var(--success))] rounded-full px-3 py-1.5 font-semibold">
                        <ClipboardCheck className="w-3.5 h-3.5" /> Validar valores (RT)
                      </button>
                      <button data-testid="btn-qt-adjust" onClick={qtAdjustEstimate} disabled={busy} className="inline-flex items-center gap-1.5 text-xs bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-[hsl(var(--warning))] rounded-full px-3 py-1.5 font-semibold">
                        <GitBranch className="w-3.5 h-3.5" /> Ajustar valores (justificar)
                      </button>
                    </>
                  )}
                  <button data-testid="copy-estimate" onClick={()=>copy(estUrl)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Copy className="w-3 h-3" /> Copiar link</button>
                  <a href={estUrl} target="_blank" rel="noreferrer" className="text-xs text-foreground flex items-center gap-1.5" data-testid="open-estimate"><ExternalLink className="w-3 h-3" /> Abrir</a>
                  <a
                    // 08/02/2026 — Defesa contra cross-lead PDF leak:
                    // só permite descarregar quando `lead.id` bate com
                    // o `id` do URL E o `estimate_token` está definido.
                    // Caso contrário desactiva o link.
                    href={lead?.id === id && lead?.estimate_token
                      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/estimate/${lead.estimate_token}/pdf`
                      : undefined}
                    onClick={(e) => {
                      if (!(lead?.id === id && lead?.estimate_token)) {
                        e.preventDefault();
                        toast.error("Aguarde — dados do lead ainda a carregar.");
                      }
                    }}
                    target="_blank" rel="noreferrer"
                    data-testid="download-estimate-pdf"
                    className={`text-xs flex items-center gap-1.5 ${lead?.id === id && lead?.estimate_token ? "text-foreground hover:text-foreground cursor-pointer" : "text-muted-foreground/70 cursor-not-allowed"}`}
                    title="Descarregar PDF da estimativa (regerado a cada mudança)"
                  >
                    <Download className="w-3 h-3" /> Descarregar PDF
                  </a>
                  {!canEdit && (
                    <button data-testid="send-estimate-email" onClick={doSendEstimateEmail} disabled={busy} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Mail className="w-3 h-3" /> Reenviar email</button>
                  )}
                </div>
                {/* Pending discount-approval banner (Rui/Daniela) */}
                {(() => {
                  const approvals = (est.discount_approvals || []).filter((a) => a.status === "pending");
                  if (!approvals.length) return null;
                  const req = approvals[approvals.length - 1];
                  const fmt = (v) => (Number(v) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  const suffix = req.mode === "pontual" ? " total" : " /mês";
                  const expires = req.expires_at ? new Date(req.expires_at).toLocaleString("pt-PT") : "—";
                  return (
                    <div className="rounded-lg p-3 text-xs border bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]" data-testid="discount-approval-pending">
                      <div className="font-semibold mb-1 flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" /> Redução aguarda aprovação (Rui Pinto ou Daniela Monteiro)
                      </div>
                      <div>
                        {fmt(req.current_value)}€{suffix} → <b>{fmt(req.requested_value)}€{suffix}</b>
                        {" · "}delta {fmt(req.delta)}€
                      </div>
                      <div className="mt-1">Motivo: {req.reason || "—"}</div>
                      <div className="mt-1 text-[hsl(var(--warning))]/80">Caduca em {expires}</div>
                    </div>
                  );
                })()}
                {est.qt_confirmed_at && (
                  <div className={`rounded-lg p-3 text-xs border ${est.qt_adjusted ? "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25 text-[hsl(var(--warning))]" : "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25 text-[hsl(var(--success))]"}`} data-testid="qt-confirmed-note">
                    {est.qt_adjusted ? (
                      <>
                        <div className="font-semibold mb-1">Valores ajustados na Reunião Técnica por {est.qt_confirmed_by_name || "—"}</div>
                        <div>Motivo: {est.qt_adjustment_reason}</div>
                      </>
                    ) : (
                      <div>Valores validados na Reunião Técnica por {est.qt_confirmed_by_name || "—"}.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })())}
        </Section>
        </div>
      </div>

      {followup && lead.status !== "perdida" && (followup.followup_count > 0 || followup.next_followup_at || followup.followup_status !== "disabled") && (
        <Section title="Follow-up automático" action={
          <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border border-primary/30 bg-primary/10 text-foreground" data-testid="followup-badge">
            {followup.followup_status === "active" && "Ativo"}
            {followup.followup_status === "pending" && "Pendente"}
            {followup.followup_status === "done" && "Concluído"}
            {followup.followup_status === "disabled" && "Desativado"}
            {followup.followup_status === "lost" && "Perdido"}
          </span>
        }>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="followup-card">
            <Kv label="Sequência" value={`${followup.followup_count} / ${followup.schedule_hours.length}`} />
            <Kv label="Último follow-up" value={followup.last_followup_at ? new Date(followup.last_followup_at).toLocaleString('pt-PT') : "—"} />
            <Kv label="Próximo follow-up" value={followup.next_followup_at ? new Date(followup.next_followup_at).toLocaleString('pt-PT') : "—"} />
          </div>
          {followup.followup_history?.length > 0 && (
            <div className="mt-4 border-t border-border pt-3 space-y-1.5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Histórico</div>
              {followup.followup_history.map((h, i) => (
                <div key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="font-mono text-foreground">#{h.n}</span>
                  <span>{new Date(h.at).toLocaleString('pt-PT')}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">{h.channel === "zoho_task" ? `Tarefa Zoho${h.zoho_task_id ? ` #${h.zoho_task_id}`:""}` : "Registo interno"}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-center gap-2">
            <button data-testid="btn-trigger-followup" onClick={triggerFollowup} disabled={busy || followup.followup_status === "disabled" || followup.followup_count >= followup.schedule_hours.length}
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 disabled:opacity-40">
              <Send className="w-3.5 h-3.5" /> Disparar agora
            </button>
            <button data-testid="btn-cancel-followup" onClick={cancelFollowup} disabled={busy || followup.followup_status === "disabled"}
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 disabled:opacity-40">
              <BellOff className="w-3.5 h-3.5" /> Cancelar follow-ups
            </button>
            <span className="text-[10px] text-muted-foreground ml-auto flex items-center gap-1"><Bell className="w-3 h-3" /> {followup.schedule_hours.join("h · ")}h</span>
          </div>
        </Section>
      )}
      </>
      )}

      {activeTab === "rt" && (
      <div className="space-y-6" data-testid="phase-rt">
      {permsReady && !canEditSection("rt_consulta") && (
        <SectionReadOnlyBanner section="rt_consulta" label="RT / Consulta Técnica" />
      )}
      {/* 25/02/2026 — Bloco de notas do Consultor movido para dentro do
          separador RT. Contexto operacional específico da Reunião
          Técnica não faz sentido estar visível na Fase Atendimento. */}
      <RTNotesBlock
        lead={lead}
        currentUser={currentUser}
        ownerPhase={resolveOwnerPhase(lead)}
        onCarePlanChanged={load}
      />

      <Section title="Agendar Reunião Técnica" action={
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {lead.scheduled_qt_at && (() => {
            const dt = parseLocalIso(lead.scheduled_qt_at);
            const pretty = dt ? dt.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : lead.scheduled_qt_at;
            return <span className="text-xs text-muted-foreground" data-testid="rt-when-label">Agendada para {pretty}</span>;
          })()}
          {/* 25/02/2026 — Consultor associado + estado de aceitação do
              cliente. Pedido do utilizador para dar visibilidade imediata
              a "com quem" está agendada e "se o cliente já aceitou". */}
          {(() => {
            // Nome do consultor associado (fallback em cadeia):
            // 1) assigned_consultor_name (novo schema)
            // 2) manager na lista pelo id
            // 3) assigned_manager_name (leads legado pre-consultor)
            const consName = lead.assigned_consultor_name
              || (managers.find(m => m.id === (lead.assigned_consultor_id || lead.assigned_manager_id))?.name)
              || lead.assigned_manager_name
              || null;
            if (!consName) return null;
            return (
              <span
                className="inline-flex items-center gap-1 text-[10px] bg-primary/10 border border-primary/30 text-primary rounded-full px-2 py-0.5 font-semibold"
                title="Consultor de Cuidados atribuído à Reunião Técnica"
                data-testid="rt-consultor-badge"
              >
                <User className="w-3 h-3" /> Consultor: {consName}
              </span>
            );
          })()}
          {(() => {
            const acc = lead.technical_meeting_accepted;
            if (acc === true) {
              return (
                <span
                  className="inline-flex items-center gap-1 text-[10px] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 text-[hsl(var(--success))] rounded-full px-2 py-0.5 font-semibold"
                  title="O cliente aceitou realizar a Reunião Técnica"
                  data-testid="rt-accept-badge-yes"
                >
                  <CheckCircle2 className="w-3 h-3" /> Aceite pelo cliente
                </span>
              );
            }
            if (acc === false) {
              return (
                <span
                  className="inline-flex items-center gap-1 text-[10px] bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 text-[hsl(var(--danger))] rounded-full px-2 py-0.5 font-semibold"
                  title="O cliente recusou realizar a Reunião Técnica"
                  data-testid="rt-accept-badge-no"
                >
                  <XCircle className="w-3 h-3" /> Recusada pelo cliente
                </span>
              );
            }
            // null/undefined → aguarda decisão
            return (
              <span
                className="inline-flex items-center gap-1 text-[10px] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 text-[hsl(var(--warning))] rounded-full px-2 py-0.5 font-semibold"
                title="Ainda não há decisão do cliente sobre a Reunião Técnica"
                data-testid="rt-accept-badge-pending"
              >
                <Clock className="w-3 h-3" /> Aguarda decisão do cliente
              </span>
            );
          })()}
          {lead.teams_event_id && lead.teams_join_url && (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 text-[hsl(var(--success))] rounded-full px-2 py-0.5 font-semibold" title={`Teams event: ${lead.teams_event_id}`} data-testid="teams-event-badge">
                <CheckCircle2 className="w-3 h-3" /> Sincronizado com Microsoft Teams
              </span>
              <a href={lead.teams_join_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-[10px] bg-[hsl(var(--info))] hover:bg-[hsl(var(--info))]/85 text-primary-foreground rounded-full px-3 py-1 font-semibold"
                 data-testid="teams-join-btn">
                Abrir Teams <ExternalLink className="w-3 h-3" />
              </a>
              {/* 06/08/2026 — Reenvio manual do convite ao cliente
                  (por vezes o cliente perde o email do Outlook ou o
                  forward do Graph falha silenciosamente). Botão
                  disponível para consultor/gestor/admin — não requer
                  reagendar. */}
              {["admin","gestor","consultor"].includes(currentUser?.role) && (
                <button
                  data-testid="teams-resend-invite-btn"
                  disabled={busy}
                  onClick={async () => {
                    try {
                      setBusy(true);
                      const r = await api.post(`/leads/${id}/teams-event/resend-invite`);
                      const d = r.data || {};
                      if (d.delivery === "forward") {
                        toast.success("Convite reencaminhado ao cliente (Outlook nativo)");
                      } else if (d.delivery === "forward+ics") {
                        toast.success(
                          `Convite enviado (Outlook + .ics anexo) · ${d.ics_attempts || 1} tentativas`
                        );
                      } else if (d.delivery === "ics") {
                        toast.success(
                          `Convite reenviado (.ics anexado) · ${d.ics_attempts || 1} tentativas`
                        );
                      } else if (d.delivery === "failed") {
                        toast.error(`Falha ao reenviar convite: ${d.detail || "erro"}`);
                      } else {
                        toast.info(d.detail || "Sem alterações");
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Falhou ao reenviar convite");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  title="Reenviar convite ao cliente sem reagendar"
                  className="inline-flex items-center gap-1 text-[10px] bg-card hover:bg-muted/40 border border-border text-foreground rounded-full px-3 py-1 font-semibold disabled:opacity-50"
                >
                  <Mail className="w-3 h-3" /> Reenviar convite
                </button>
              )}
              {/* 25/02/2026 — Diagnóstico de entregas: mostra o
                  histórico de tentativas registadas em email_logs. Só
                  admin para não sobrecarregar a UI. */}
              {currentUser?.role === "admin" && (
                <button
                  data-testid="teams-delivery-logs-btn"
                  onClick={async () => {
                    try {
                      const r = await api.get(`/leads/${id}/teams-event/delivery-logs`);
                      setDeliveryLogs(r.data?.items || []);
                      setDeliveryLogsOpen(true);
                    } catch (e) {
                      toast.error("Não foi possível carregar histórico");
                    }
                  }}
                  title="Ver histórico de entregas do convite"
                  className="inline-flex items-center gap-1 text-[10px] bg-muted/40 hover:bg-muted border border-border text-muted-foreground rounded-full px-2.5 py-1 font-semibold"
                >
                  <span className="text-[10px]">Diagnóstico</span>
                </button>
              )}
            </>
          )}
          {lead.scheduled_qt_at && !lead.teams_event_id && (
            <span
              className="inline-flex items-center gap-1 text-[10px] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 text-[hsl(var(--warning))] rounded-full px-2 py-0.5 font-semibold"
              title={lead.teams_event_error
                ? `Erro Graph: ${lead.teams_event_error}`
                : "Reunião marcada localmente mas evento Teams não foi criado — verifique o UPN do gestor em /profile → Office 365"}
              data-testid="teams-event-badge-warn">
              <AlertTriangle className="w-3 h-3" /> Não sincronizado com Teams{lead.teams_event_error ? " · ver detalhes" : ""}
            </span>
          )}
          {lead.teams_event_error && lead.scheduled_qt_at && !lead.teams_event_id && (
            <div className="basis-full mt-1 flex items-start gap-2" data-testid="teams-event-error-detail">
              <div className="flex-1 text-[10px] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-md px-2 py-1">
                <b>Erro Microsoft Graph:</b> {lead.teams_event_error}
              </div>
              <button
                data-testid="teams-retry-btn"
                onClick={async () => {
                  try {
                    setBusy(true);
                    const r = await api.post(`/leads/${id}/teams-event/retry`);
                    toast.success("Evento Teams criado com sucesso");
                    setLead(r.data);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Retry falhou");
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                className="text-[10px] bg-foreground hover:bg-foreground/90 text-background rounded-md px-3 py-1 font-semibold whitespace-nowrap disabled:opacity-50">
                Retry Teams
              </button>
            </div>
          )}
        </div>
      }>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input data-testid="schedule-date" type="date" value={schedule.date} onChange={(e)=>setSchedule({...schedule, date:e.target.value})} className="bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="schedule-time" type="time" value={schedule.time} onChange={(e)=>setSchedule({...schedule, time:e.target.value})} className="bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm" />
          <select data-testid="schedule-manager" value={schedule.manager_id} onChange={(e)=>setSchedule({...schedule, manager_id:e.target.value})} className="bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm">
            <option value="">Consultor de Cuidados…</option>
            {/* 26/02/2026 — Post-restructure a Reunião Técnica é conduzida
                pelo Consultor de Cuidados. O filtro anterior (`role !==
                "consultor"`) datava do pre-restructure e mostrava
                admins/gestores. Passamos a usar `is_consultor_effective`
                (backend-computed) que cobre `role="consultor"` +
                Consultor Temporário (definido em
                `temporary_consultor_until` — só admin). */}
            {managers.filter(m => m.is_consultor_effective === true).map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button data-testid="schedule-btn" onClick={doSchedule} disabled={busy} className="bg-muted hover:bg-muted border border-border rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2"><CalIcon className="w-4 h-4" /> Agendar</button>
        </div>
        {/* Availability slots picker (Microsoft Graph — real free/busy from
            the manager's Outlook calendar). Since 25/02/2026 mostra HOJE +
            4 próximos dias úteis (excluindo fins-de-semana) num painel de 5
            colunas, para o Gestor deixar de ver slots do passado quando a
            RT já estava agendada para uma data antiga. */}
        {schedule.manager_id && (
          <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4" data-testid="avail-slots-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">
                Slots livres em Outlook para {managers.find(m=>m.id===schedule.manager_id)?.name || "gestor"} · próximos 5 dias úteis
              </div>
              {availLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-foreground" />}
            </div>
            {availError && (
              <div className="text-[11px] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5 mb-3" data-testid="avail-slots-error">
                <AlertTriangle className="w-3 h-3" /> {availError}
              </div>
            )}
            {!availLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="avail-slots-days">
                {getNextBusinessDays(5).map((iso) => {
                  const slots = availByDay[iso] || [];
                  // Etiqueta pt-PT compacta: "seg, 11/08" — cabe em coluna estreita
                  const d = new Date(`${iso}T00:00:00`);
                  const weekday = d.toLocaleDateString("pt-PT", { weekday: "short" }).replace(".", "");
                  const dayMonth = d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });
                  const isSelectedDay = schedule.date === iso;
                  return (
                    <div key={iso}
                          className={`rounded-lg border p-2.5 ${isSelectedDay ? "border-primary/60 bg-primary/5" : "border-border bg-card"}`}
                          data-testid={`avail-slots-day-${iso}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        {weekday} · {dayMonth}
                      </div>
                      {slots.length === 0 ? (
                        <div className="text-[10px] text-muted-foreground/60 italic">Sem slots</div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {slots.map((s) => {
                            const isSelectedSlot = isSelectedDay && schedule.time === s;
                            return (
                              <button key={`${iso}-${s}`} type="button"
                                       onClick={() => setSchedule({ ...schedule, date: iso, time: s })}
                                       className={`text-[11px] rounded-full px-2 py-1 border transition ${isSelectedSlot
                                         ? "bg-foreground text-background border-foreground"
                                         : "bg-muted text-foreground border-border hover:bg-muted/60"}`}
                                       data-testid={`avail-slot-${iso}-${s}`}>
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Section>

      <Section title="Reunião Técnica" action={
        <Link data-testid="btn-qt" to={`/leads/${id}/technical`} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"><ClipboardCheck className="w-3.5 h-3.5" /> {lead.technical ? "Editar" : "Iniciar"}</Link>
      }>
        {lead.technical?.completed_at ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Kv label="Probabilidade" value={`${lead.technical.close_probability}%`} />
            <Kv label="Próxima ação" value={lead.technical.next_action} />
            <Kv label="Objeções" value={(lead.technical.objections||[]).join(", ")} />
            <Kv label="Concluída" value={new Date(lead.technical.completed_at).toLocaleDateString('pt-PT')} />
          </div>
        ) : <div className="text-sm text-muted-foreground">Ainda não realizada.</div>}
      </Section>
      </div>
      )}

      {activeTab === "proposta" && (
      <div className="space-y-6" data-testid="phase-proposta">
      {permsReady && !canEditSection("proposta") && (
        <SectionReadOnlyBanner section="proposta" label="Proposta" />
      )}
      <Section title="Proposta Final" action={
        <div className="flex items-center gap-2">
          {proposalTemplates.length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="lead-proposal-template-select-container">
              <span className="text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold">Template</span>
              <select
                value={lead.proposal_template_id || ""}
                onChange={(e) => changeProposalTemplate(e.target.value)}
                className="text-xs bg-card border border-border rounded-full px-3 py-1.5 focus:outline-none focus:border-[hsl(var(--warning))] max-w-[240px]"
                data-testid="lead-proposal-template-select"
              >
                <option value="">{`Default (${(proposalTemplates.find(t=>t.is_default) || {}).name || "Standard"})`}</option>
                {proposalTemplates.filter(t => !t.is_default).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
          {lead.proposal && versions.length > 1 && (
            <button data-testid="btn-versions" onClick={()=>setShowVersions(!showVersions)} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5">
              <History className="w-3.5 h-3.5" /> {versions.length} versões
            </button>
          )}
          {lead.proposal && (
            <button data-testid="btn-edit-proposal-text"
                     onClick={() => openProposalEditor()}
                     className="inline-flex items-center gap-1.5 text-xs bg-card hover:bg-muted border border-border text-muted-foreground rounded-full px-3 py-1.5"
                     title="Editar textos gerados por IA">
              <Sparkles className="w-3.5 h-3.5" /> Editar textos
            </button>
          )}
          <button data-testid="btn-generate-proposal" onClick={doGenerateProposal} disabled={busy || !lead.technical} className="inline-flex items-center gap-1.5 text-xs bg-primary/10 border border-primary/30 text-foreground hover:bg-primary/20 rounded-full px-3 py-1.5 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <FileText className="w-3.5 h-3.5" />} {lead.proposal ? "Regerar Proposta" : "Gerar Proposta"}
          </button>
          {lead.proposal && (
            <button
              data-testid="btn-reformat-paragraphs"
              onClick={async () => {
                if (!window.confirm("Reformatar os textos da proposta em parágrafos inteligentes?")) return;
                try {
                  setBusy(true);
                  const { data } = await api.post(`/leads/${lead.id}/proposal/reformat-paragraphs`);
                  if ((data.changed || []).length === 0) {
                    toast.info(data.message || "Já formatado.");
                  } else {
                    toast.success(`Reformatado: ${data.changed.join(", ")}`);
                    await load();
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erro a reformatar");
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs bg-muted/40 hover:bg-muted/70 border border-border text-muted-foreground rounded-full px-3 py-1.5 disabled:opacity-40"
              title="Dividir os textos longos da proposta em parágrafos inteligentes sem regenerar o conteúdo AI."
            >
              <FilePenLine className="w-3.5 h-3.5" /> Reformatar parágrafos
            </button>
          )}
        </div>
      }>
        {lead.proposal ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-foreground">{lead.proposal.current_version}</span>
              {!lead.proposal_last_sent_at && !lead.acceptance?.accepted && !lead.decline?.decision && (
                <span data-testid="proposal-draft-badge" className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] font-semibold inline-flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Rascunho — reveja antes de enviar
                </span>
              )}
              {lead.proposal_last_sent_at && (
                <span data-testid="proposal-sent-badge" className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 text-[hsl(var(--success))] font-semibold inline-flex items-center gap-1">
                  <Send className="w-3 h-3" /> Enviada ao cliente
                </span>
              )}
              <span className="text-xs text-muted-foreground">gerada em {new Date(lead.proposal.generated_at).toLocaleString('pt-PT')}</span>
            </div>
            <div className="text-sm text-muted-foreground">{lead.proposal.executive_summary}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kv label="€/mês proposto" value={
                lead.proposal.monthly_price_min === lead.proposal.monthly_price_max
                  ? `${Number(lead.proposal.monthly_price_max).toLocaleString('pt-PT', {minimumFractionDigits: 2})}€`
                  : `${Number(lead.proposal.monthly_price_min).toLocaleString('pt-PT', {minimumFractionDigits: 2})}€ – ${Number(lead.proposal.monthly_price_max).toLocaleString('pt-PT', {minimumFractionDigits: 2})}€`
              } />
              <Kv label="Caução"
                  value={`${Number((lead.deposit?.plan?.total_amount) || lead.proposal.caucao || 0).toLocaleString('pt-PT', {minimumFractionDigits: 2})}€`} />
              <Kv label="Arranque recrutamento"
                  value={`${Number((lead.deposit?.plan?.kickstart_amount) || lead.proposal.valor_inicial || 0).toLocaleString('pt-PT', {minimumFractionDigits: 2})}€`} />
              <Kv label="Início do serviço"
                  value={lead.deposit?.plan?.service_start_date
                          ? new Date(lead.deposit.plan.service_start_date).toLocaleDateString('pt-PT', {day: '2-digit', month: 'short', year: 'numeric'})
                          : `${lead.proposal.validity_days} dias`} />
            </div>
            {/* 27/02/2026 — Cartão INTERNO com custo AFs + margem.
                Nunca aparece no PDF nem no email do cliente. */}
            {(() => {
              const afCost = Number(lead.estimate?.af_monthly_cost || 0);
              const revenue = Number(lead.proposal.monthly_price_max || 0);
              const marginAbs = revenue - afCost;
              const marginPct = revenue > 0 ? (marginAbs / revenue) * 100 : 0;
              const hasCost = afCost > 0;
              const marginTone = !hasCost
                ? "text-muted-foreground"
                : marginPct >= 36
                  ? "text-[hsl(var(--success))]"
                  : marginPct >= 15
                    ? "text-[hsl(var(--warning))]"
                    : "text-[hsl(var(--danger))]";
              const eur = (v) => `${Number(v || 0).toLocaleString('pt-PT', {minimumFractionDigits: 2})}€`;
              return (
                <div
                  data-testid="proposal-margin-card"
                  className={`rounded-lg border p-4 ${hasCost ? "bg-muted/40 border-border" : "bg-[hsl(var(--warning-bg))]/50 border-[hsl(var(--warning))]/40"}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-heading font-semibold flex items-center gap-1.5">
                        <ShieldCheck className="w-3 h-3" /> Margem interna
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        Só visível internamente — não vai na proposta enviada ao cliente.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSimulatorOpen(true)}
                      data-testid="btn-open-margin-simulator"
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-foreground text-background hover:bg-[#1E293B] rounded-full px-3 py-1.5"
                    >
                      <Calculator className="w-3.5 h-3.5" />
                      {hasCost ? "Rever custo AFs" : "Indicar custo AFs"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Receita mensal</div>
                      <div className="text-[15px] font-heading tabular-nums text-foreground">{eur(revenue)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Custo AFs autorizado</div>
                      <div
                        data-testid="proposal-margin-afcost"
                        className={`text-[15px] font-heading tabular-nums ${hasCost ? "text-foreground" : "text-[hsl(var(--warning))] font-bold"}`}
                      >
                        {hasCost ? eur(afCost) : "por indicar"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Margem €/mês</div>
                      <div className={`text-[15px] font-heading tabular-nums ${marginTone}`}>
                        {hasCost ? eur(marginAbs) : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Margem %</div>
                      <div
                        data-testid="proposal-margin-pct"
                        className={`text-[15px] font-heading tabular-nums font-bold ${marginTone}`}
                      >
                        {hasCost ? `${marginPct.toFixed(1).replace(".", ",")}%` : "—"}
                      </div>
                    </div>
                  </div>
                  {!hasCost && (
                    <div className="mt-3 text-[12px] text-[hsl(var(--warning))] flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        Indica o custo mensal dos AFs para desbloquear o botão <b>Enviar Proposta</b>.
                        Este valor é o tecto autorizado para o recrutamento — se for excedido, o pedido
                        vai à aprovação de <b>rui.pinto@caring.pt</b>.
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button data-testid="copy-proposal" onClick={()=>copy(propUrl)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Copy className="w-3 h-3" /> Copiar link</button>
              <a href={propUrl} target="_blank" rel="noreferrer" data-testid="open-proposal" className="text-xs text-foreground flex items-center gap-1.5"><ExternalLink className="w-3 h-3" /> Abrir proposta</a>
              <button data-testid="download-pdf" onClick={downloadPdf} disabled={busy} className={`text-xs flex items-center gap-1.5 ${reviewedDocs.proposal ? "text-[hsl(var(--success))]" : "text-muted-foreground hover:text-foreground"}`}>
                {reviewedDocs.proposal ? <span className="text-[hsl(var(--success))]" data-testid="proposal-reviewed-check">✓</span> : <Download className="w-3 h-3" />} Descarregar PDF
              </button>
              <button
                data-testid="preview-pdf"
                onClick={async () => {
                  try {
                    setBusy(true);
                    const res = await api.get(`/leads/${id}/proposal/pdf`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    setPdfPreviewUrl(url);
                    setReviewedDocs((s) => ({ ...s, proposal: true }));
                  } catch (e) {
                    toast.error("Erro a gerar preview");
                  } finally { setBusy(false); }
                }}
                disabled={busy}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                title="Ver PDF em pré-visualização (marca como revisto automaticamente)"
              >
                <Expand className="w-3 h-3" /> Pré-visualizar
              </button>
              {/* Nota (18/02/2026): os botões "Plano (cliente rascunho)" e
                  "Plano (interno)" foram removidos daqui. O Módulo Plano de
                  Cuidados oferece agora `.docx` editáveis em 3 vistas
                  (Equipa Interna · Ajudante Familiar · Cliente/Família) via
                  `CarePlanApprovals.jsx` e o toggle "Anexar Plano (.docx cliente)"
                  ao lado envia-o ao cliente junto com a proposta. */}
              <label
                data-testid="attach-care-plan-docx-toggle"
                title="Se ativo, anexa o Plano de Cuidados em Word (vista cliente — sem dados clínicos sensíveis) ao email da proposta."
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2 py-1 border cursor-pointer select-none ${attachCarePlanDocx ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:bg-muted"}`}
              >
                <input
                  type="checkbox"
                  checked={attachCarePlanDocx}
                  data-testid="attach-care-plan-docx-checkbox"
                  onChange={(e) => {
                    const v = e.target.checked;
                    setAttachCarePlanDocx(v);
                    try { localStorage.setItem("attach_care_plan_docx_on_send", v ? "1" : "0"); } catch (_er) {}
                  }}
                  className="w-3 h-3 accent-primary"
                />
                Anexar Plano (.docx cliente)
              </label>
              {(() => {
                if (!isGestorOrAdmin) return null;
                const plan = lead.deposit?.plan || {};
                const cautionOk = plan.total_amount > 0 && plan.kickstart_amount > 0;
                const hasEmail = !!(lead.qualification?.contact?.email);
                const already = !!lead.proposal_last_sent_at;
                const afCost = Number(lead.estimate?.af_monthly_cost || 0);
                const afCostOk = afCost > 0;
                const canSend = cautionOk && hasEmail && afCostOk && !lead.acceptance?.accepted && lead.decline?.decision !== "declined";
                const missing = [];
                if (!cautionOk) missing.push("caução (total + arranque)");
                if (!afCostOk) missing.push("custo AFs (Simulador de Margem)");
                if (!hasEmail) missing.push("email do cliente");
                const title = canSend ? "" : `Falta: ${missing.join(", ")}`;
                return (
                  <button
                    data-testid="btn-send-proposal-email"
                    onClick={doSendProposalEmail}
                    disabled={busy || !canSend}
                    title={title}
                    className="inline-flex items-center gap-1.5 text-xs bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {already ? "Reenviar Proposta ao Cliente" : "Enviar Proposta ao Cliente"}
                  </button>
                );
              })()}
              {lead.proposal_last_sent_at && (
                <span className="text-[11px] text-muted-foreground" data-testid="proposal-last-sent">
                  Enviada {new Date(lead.proposal_last_sent_at).toLocaleString("pt-PT")}
                </span>
              )}
              {lead.acceptance?.accepted && <span className="text-xs text-emerald-400">✓ Aceite por {lead.acceptance.accepted_by_name}</span>}
              {lead.decline?.decision === "declined" && <span className="text-xs text-[hsl(var(--danger))]">✗ Recusado por {lead.decline.submitted_by_name || "cliente"}</span>}
              {lead.decline?.decision === "postpone" && <span className="text-xs text-[hsl(var(--warning))]">⏳ Cliente adiou decisão</span>}
            </div>
            {(() => {
              // Contextual banner when the "Enviar" button is disabled — tells
              // the Gestor exactly what needs to happen next.
              const plan = lead.deposit?.plan || {};
              const cautionOk = plan.total_amount > 0 && plan.kickstart_amount > 0;
              const hasEmail = !!(lead.qualification?.contact?.email);
              const already = !!lead.proposal_last_sent_at;
              const afCost = Number(lead.estimate?.af_monthly_cost || 0);
              const afCostOk = afCost > 0;
              const closed = lead.acceptance?.accepted || lead.decline?.decision === "declined";
              if (already || closed) return null;
              const need = [];
              if (!cautionOk) need.push("definir a caução (total + arranque de recrutamento) no bloco DAF abaixo");
              if (!afCostOk) need.push("indicar o custo mensal dos AFs no Simulador de Margem para congelar a margem interna");
              if (!hasEmail) need.push("guardar o email do cliente na Qualificação");
              if (!allReviewed) need.push("descarregar e rever a proposta antes de enviar");
              if (need.length === 0) {
                // Ready to send — nudge the Gestor to actually click the button.
                return (
                  <div className="mt-3 rounded-lg border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-3 text-xs text-[hsl(var(--success))] flex items-start gap-2" data-testid="proposal-ready-to-send">
                    <span className="text-base leading-none">✅</span>
                    <div>
                      <b>Tudo revisto.</b> Pode clicar em <b>Enviar Proposta ao Cliente</b>.
                    </div>
                  </div>
                );
              }
              return (
                <div className="mt-3 rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-3 text-xs text-[hsl(var(--warning))] flex items-start gap-2" data-testid="proposal-send-blockers">
                  <span className="text-base leading-none">💡</span>
                  <div>
                    <b>Para enviar a proposta ao cliente</b>, ainda falta: {need.join(" · ")}.
                  </div>
                </div>
              );
            })()}
            {lead.decline && (
              <div className={`mt-3 rounded-lg p-3 border ${lead.decline.decision === "declined" ? "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25" : "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25"}`} data-testid="lead-decline-block">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{color: lead.decline.decision === "declined" ? "#DC2626" : "#B45309"}}>
                  {lead.decline.decision === "declined" ? "Proposta recusada pelo cliente" : "Cliente pediu para adiar"}
                </div>
                <div className="text-[11px] text-muted-foreground mb-2">
                  {new Date(lead.decline.submitted_at).toLocaleString("pt-PT")}
                  {lead.decline.submitted_by_name && <> · por <b>{lead.decline.submitted_by_name}</b></>}
                </div>
                {lead.decline.reason && (
                  <div className="text-sm text-foreground italic border-l-2 pl-3" style={{borderColor: lead.decline.decision === "declined" ? "#DC2626" : "#F59E0B"}}>
                    "{lead.decline.reason}"
                  </div>
                )}
              </div>
            )}

            {showVersions && (
              <div className="mt-4 border-t border-border pt-4 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-2"><GitBranch className="w-3.5 h-3.5" /> Histórico de versões</div>
                {versions.map((v, i) => (
                  <div key={i} className="text-xs bg-muted/40 border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-foreground">{v.version}</span>
                      <span className="text-muted-foreground">{v.created_at ? new Date(v.created_at).toLocaleString('pt-PT') : "—"}</span>
                    </div>
                    {v.reason && <div className="text-muted-foreground">{v.reason}</div>}
                    {v.snapshot?.monthly_price_max && <div className="text-muted-foreground mt-1">Valor máx: {v.snapshot.monthly_price_max}€/mês</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Client observations left on the public proposal page */}
            {(lead.proposal.client_observations || []).length > 0 && (
              <div className="mt-4 border-t border-border pt-4 space-y-2" data-testid="client-observations-list">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" /> Observações do cliente ({lead.proposal.client_observations.length})
                </div>
                {lead.proposal.client_observations.map((obs, i) => (
                  <div key={i} className="text-xs bg-muted/40 border border-primary/40 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-[0.15em] text-foreground">
                      <span>{obs.submitted_by_name || "Cliente"}</span>
                      <span>{obs.submitted_at ? new Date(obs.submitted_at).toLocaleString("pt-PT") : ""}</span>
                    </div>
                    <div className="text-foreground whitespace-pre-wrap">{obs.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2" data-testid="proposal-blockers">
            <div className="text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold">Pré-requisitos para gerar a proposta</div>
            <ul className="text-[13px] space-y-1.5">
              <li className="flex items-center gap-2">
                {lead.technical ? <span className="text-[hsl(var(--success))]">✓</span> : <span className="text-[hsl(var(--danger))]">✗</span>}
                <span className={lead.technical ? "text-muted-foreground line-through" : "text-foreground"}>
                  Preencher a <b>Reunião Técnica</b> (estado clínico, plano de cuidados)
                </span>
                {!lead.technical && (
                  <Link to={`/leads/${id}/technical`} className="text-xs text-foreground hover:underline ml-auto flex-shrink-0" data-testid="link-technical">Ir para RT →</Link>
                )}
              </li>
              <li className="flex items-center gap-2">
                {lead.estimate ? <span className="text-[hsl(var(--success))]">✓</span> : <span className="text-[hsl(var(--danger))]">✗</span>}
                <span className={lead.estimate ? "text-muted-foreground line-through" : "text-foreground"}>
                  Ter a <b>Estimativa</b> gerada (Fase 1)
                </span>
                {!lead.estimate && (
                  <span className="text-xs text-[hsl(var(--danger))] ml-auto flex-shrink-0">Estimativa ainda não gerada</span>
                )}
              </li>
            </ul>
          </div>
        )}
      </Section>

      {/* Módulo Plano de Cuidados — foi movido para a aba dedicada
          "Direção Técnica" (27/02/2026). Ver bloco ``activeTab === "dt"``
          abaixo. Apenas Direção Técnica (roles: dt/direcao/admin) pode
          preencher tarefas diárias com horas para o AF. */}

      {/* Plano de Visitas de Acompanhamento — segue anexo ao export. */}
      <FollowUpVisitPlanBlock lead={lead} onChange={load} />

      {/* Contract data — visible once the proposal exists */}
      {lead.proposal && (
        <ContractDataView lead={lead} proposalToken={lead.proposal_token} currentUser={currentUser} onChanged={load} />
      )}

      {/* Deposit plan (Gestor defines total + kickstart + service start) */}
      <DepositPlanCard lead={lead} onChange={load} />

      {/* Recruitment requisition — visible once the client has accepted */}
      <RecruitmentPanel lead={lead} onChange={load} />

      {/* Enquadramento Global do Serviço — atribui DT + envia briefing.
          05/02/2026: pós-kickstart-pago, a consultora escolhe a DT e a
          mesma passa a assinar o PIC. */}
      <EnquadramentoPanel lead={lead} onChange={load} />
      </div>
      )}

      {/* ============= Aba DIREÇÃO TÉCNICA (27/02/2026) =============
          Contém o formulário completo do Plano de Cuidados (com
          tarefas diárias + horas). Apenas a DT (roles: dt/direcao/admin)
          preenche esta aba. Outros roles (gestor/consultor) veem em
          modo leitura para acompanhamento.
      */}
      {activeTab === "dt" && (
      <div className="space-y-6" data-testid="phase-dt">
        {permsReady && !canEditSection("plano_cuidados") && (
          <SectionReadOnlyBanner section="plano_cuidados" label="Plano de Cuidados" />
        )}
        {(lead.estimate || lead.technical?.care_plan) && canAccess("care-plans", currentUser) && (
          <DTReviewLinkCard lead={lead} onSent={load} />
        )}
        {!(lead.estimate || lead.technical?.care_plan) ? (
          <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="dt-empty-state">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Direção Técnica · Plano de Cuidados
            </div>
            <div className="rounded-xl border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
              <div className="font-semibold mb-1">Plano de Cuidados ainda não iniciado</div>
              <div className="leading-snug">
                A estimativa ainda não foi gerada. Depois da <b>Reunião Técnica</b>,
                a Direção Técnica preenche aqui o formulário completo com tarefas
                diárias e horas do Ajudante Familiar.
              </div>
            </div>
          </div>
        ) : canAccess("care-plans", currentUser) ? (
          <CarePlanBlocksEditor leadId={lead.id} lead={lead} initialCarePlan={lead.technical?.care_plan} onLeadRefresh={load} />
        ) : (
          <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="dt-forbidden">
            <div className="text-sm text-muted-foreground">
              Não tens permissão para ver o Plano de Cuidados detalhado.
            </div>
          </div>
        )}
      </div>
      )}
      </div> {/* /lead-detail-main */}

      {/* Sidebar content — CloseAtendimento floating tab + ZohoNotes drawer.
          Both use position:fixed so they don't need a reserved column. */}
      {(() => {
        const isAdmin = currentUser?.role === "admin";
        const startedByMe = !!(
          lead.first_contact_by_id
          && lead.first_contact_by_id === currentUser?.id
        );
        if (!isAdmin && !startedByMe) return null;
        return (
          <CloseAtendimentoBlock
            lead={lead}
            onChanged={load}
            zohoStatuses={zohoStatuses}
            zohoStatusesLoading={zohoStatusesLoading}
            onZohoStatusSaved={(updated) => setLead(updated)}
            variant="floating"
          />
        );
      })()}
      <ZohoNotesBlock lead={lead} />
      <CalendarFab leadConsultorId={lead?.assigned_consultor_id || lead?.assigned_manager_id} />
      {/* 05/02/2026 — Botão do rail para reabrir o Resumo RT → ClickUp
          mesmo depois da janela auto-hide de 60 min. Só aparece após a
          RT concluída e está escondido de Atendimento. */}
      {/* Só aparece com permissão de VER a secção rt_consulta. */}
      {currentUser?.role !== "atendimento"
        && lead.technical?.completed_at
        && (!permsReady || canViewSection("rt_consulta")) && (
        <RTSummaryFab lead={lead} />
      )}

      {/* PDF preview modal — open via "Pré-visualizar" in the proposal section */}
      {pdfPreviewUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => { window.URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null); }}
          data-testid="pdf-preview-modal"
        >
          <div
            className="bg-card border border-border rounded-2xl w-full max-w-4xl h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pré-visualização</div>
                <div className="font-ui text-base">Proposta em PDF</div>
              </div>
              <button
                data-testid="pdf-preview-close"
                onClick={() => { window.URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null); }}
                className="text-muted-foreground hover:text-foreground p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              src={pdfPreviewUrl}
              title="Preview PDF"
              className="flex-1 w-full bg-white"
              data-testid="pdf-preview-iframe"
            />
          </div>
        </div>
      )}
      </div> {/* /root */}

      {/* Modal — Enviar estimativa ao cliente */}
      <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
        <DialogContent className="max-w-6xl bg-card" data-testid="send-estimate-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
              <Send className="w-5 h-5 text-foreground" /> Enviar estimativa ao cliente
            </DialogTitle>
            <DialogDescription>
              Revê o texto que será enviado. O link expira a {(sendDraft.expires_at || "").slice(0,10)}. A tua assinatura é adicionada automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 max-h-[75vh] overflow-hidden">
            <div className="space-y-3 overflow-y-auto pr-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Para</div>
              <input value={sendDraft.to} readOnly className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-muted-foreground" data-testid="send-to" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Assunto</div>
              <input value={sendDraft.subject} onChange={e => setSendDraft({...sendDraft, subject: e.target.value})} className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="send-subject" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Corpo do email (HTML)</div>
              <textarea rows={10} value={sendDraft.body_html} onChange={e => setSendDraft({...sendDraft, body_html: e.target.value})} className="w-full bg-card border border-border rounded-lg px-3 py-2 text-xs font-mono text-foreground leading-relaxed resize-y min-h-[200px]" data-testid="send-body" />
              <div className="text-[10px] text-muted-foreground mt-1">Podes editar. Suporta HTML simples (&lt;p&gt;, &lt;b&gt;, &lt;a&gt;, &lt;br/&gt;).</div>
            </div>
            {sendDraft.signature_html && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Assinatura (auto)</div>
                <div className="bg-muted/40 border border-primary/40 rounded-lg px-3 py-2 text-xs text-muted-foreground" dangerouslySetInnerHTML={{__html: sendDraft.signature_html}} data-testid="send-signature-preview" />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={sendDraft.attach_pdf} onChange={e => setSendDraft({...sendDraft, attach_pdf: e.target.checked})} data-testid="send-attach-pdf" />
              <span>Anexar PDF da estimativa</span>
            </label>
            </div>

            {/* --- PDF mini preview --- */}
            <div className="min-h-[400px] hidden lg:flex flex-col border border-border rounded-lg overflow-hidden bg-muted/20">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                  Pré-visualização PDF
                </div>
                {sendDraft.pdf_preview_url ? (
                  <a
                    href={sendDraft.pdf_preview_url}
                    target="_blank" rel="noreferrer"
                    className="text-[10px] text-primary hover:underline"
                  >Abrir em separador</a>
                ) : null}
              </div>
              {sendDraft.pdf_preview_url ? (
                <iframe
                  src={sendDraft.pdf_preview_url}
                  title="Preview estimativa PDF"
                  className="flex-1 w-full bg-white"
                  data-testid="send-modal-pdf-preview"
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
                  {sendDraft.pdf_loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> A gerar preview…</>
                  ) : (
                    <>Sem preview disponível.</>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <button onClick={() => setSendModalOpen(false)} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted" data-testid="send-cancel">Cancelar</button>
            <button
              onClick={confirmSendEstimate}
              disabled={sendLoading || !sendDraft.to}
              className="px-5 py-2 rounded-lg bg-foreground hover:bg-slate-800 text-background text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="send-confirm"
            >
              {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar agora
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal amplo para edição do Resumo automático (IA) */}
      <Dialog open={summaryModalOpen} onOpenChange={setSummaryModalOpen}>
        <DialogContent className="max-w-3xl bg-card" data-testid="summary-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-foreground" /> Resumo automático (IA)
            </DialogTitle>
            <DialogDescription>
              Texto gerado a partir da qualificação inicial. Podes ajustar antes de enviar ao cliente — este resumo aparece na estimativa e nas notas Zoho.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={summaryDraft}
            onChange={(e) => setSummaryDraft(e.target.value)}
            rows={14}
            className="w-full bg-muted/40 border border-primary/40 rounded-lg px-4 py-3 text-sm text-foreground leading-relaxed resize-y focus:outline-none focus:border-primary/60 min-h-[300px]"
            placeholder="Resumo empático e profissional do pedido (3-6 frases)."
            data-testid="summary-modal-textarea"
            autoFocus
          />
          <div className="text-xs text-muted-foreground mt-1">{summaryDraft.length} caracteres</div>
          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={regenerateSummary}
                disabled={regeneratingSummary}
                className="px-4 py-2 rounded-lg border border-primary/60 text-sm text-foreground bg-muted/40 hover:bg-muted inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="summary-modal-regenerate"
              >
                {regeneratingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Regerar do zero
              </button>
              <button
                onClick={async () => {
                  if (!(summaryDraft || "").trim()) {
                    toast.error("Nada para melhorar — texto vazio.");
                    return;
                  }
                  setRegeneratingSummary(true);
                  try {
                    const r = await api.post("/ai/improve-text", {
                      text: summaryDraft,
                      context: "Resumo empático da qualificação inicial que abre a estimativa enviada ao cliente. 3-6 frases.",
                      tone: "consultivo",
                    });
                    if (r.data?.text) {
                      setSummaryDraft(r.data.text);
                      toast.success("Resumo refinado pela IA");
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Falha ao melhorar com IA");
                  } finally {
                    setRegeneratingSummary(false);
                  }
                }}
                disabled={regeneratingSummary || !(summaryDraft || "").trim()}
                className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground bg-card hover:bg-muted inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="summary-modal-improve"
              >
                <Sparkles className="w-4 h-4" /> Melhorar com IA
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSummaryModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted"
                data-testid="summary-modal-cancel"
              >Cancelar</button>
              <button
                onClick={async () => {
                  await updateEstimateSummary(summaryDraft);
                  setSummaryModalOpen(false);
                  toast.success("Resumo atualizado");
                }}
                className="px-5 py-2 rounded-lg bg-foreground hover:bg-slate-800 text-background text-sm font-semibold inline-flex items-center gap-2"
                data-testid="summary-modal-save"
              >
                <Send className="w-4 h-4" /> Guardar
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Reunião Técnica · inline decision dialog -------- */}
      <Dialog open={rtDialogOpen} onOpenChange={setRtDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="rt-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Decisão da Reunião Técnica</DialogTitle>
            <DialogDescription>
              Registe se o cliente aceitou (idealmente com dia e hora) ou ficou de decidir. Quando aceita, é criada automaticamente uma Task de reminder no Zoho CRM.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const rtInp = "bg-card border border-border rounded-md px-3 py-2 text-sm";
            return (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 gap-2">
              <button type="button"
                       onClick={() => setRtDialogDraft(d => ({ ...d, accepted: true }))}
                       className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                         rtDialogDraft.accepted === true
                           ? "border-[#059669] bg-[#059669]/10 text-[#265F55]"
                           : "border-border bg-card text-muted-foreground hover:border-[#059669]/50"
                       }`}
                       data-testid="rt-decision-accept">
                Cliente aceitou
              </button>
              <button type="button"
                       onClick={() => setRtDialogDraft(d => ({ ...d, accepted: false }))}
                       className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                         rtDialogDraft.accepted === false
                           ? "border-[#A94442] bg-[#A94442]/10 text-[#A94442]"
                           : "border-border bg-card text-muted-foreground hover:border-[#A94442]/50"
                       }`}
                       data-testid="rt-decision-refuse">
                Ficou de decidir
              </button>
            </div>

            {rtDialogDraft.accepted === true && (
              <div className="space-y-3 rounded-xl border border-border bg-muted/40 p-4" data-testid="rt-accept-details">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">Detalhes da RT (opcional se ainda não agendada)</div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold mb-1">Data</span>
                    <input type="date"
                            value={rtDialogDraft.date}
                            onChange={e => setRtDialogDraft(d => ({ ...d, date: e.target.value }))}
                            className={`${rtInp} w-full`}
                            data-testid="rt-date" />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold mb-1">Hora</span>
                    <input type="time"
                            value={rtDialogDraft.time}
                            onChange={e => setRtDialogDraft(d => ({ ...d, time: e.target.value }))}
                            className={`${rtInp} w-full`}
                            data-testid="rt-time" />
                  </label>
                </div>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold mb-1">Consultor de Cuidados atribuído</span>
                  <select value={rtDialogDraft.manager_id}
                           onChange={e => setRtDialogDraft(d => ({ ...d, manager_id: e.target.value }))}
                           className={`${rtInp} w-full`}
                           data-testid="rt-manager">
                    <option value="">— nenhum —</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                  </select>
                </label>
                <div className="text-[11px] text-foreground">
                  Se ainda não sabe data/hora, deixe em branco — o Zoho recebe um Task para o Consultor agendar em 2 dias.
                </div>
              </div>
            )}

            {rtDialogDraft.accepted === false && (
              <div className="space-y-2 rounded-xl border border-[hsl(var(--danger))]/25 bg-[hsl(var(--danger-bg))] p-4" data-testid="rt-refuse-details">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--danger))] font-semibold mb-1">Motivo / próximos passos</span>
                  <textarea rows={3}
                             value={rtDialogDraft.refuse_reason}
                             onChange={e => setRtDialogDraft(d => ({ ...d, refuse_reason: e.target.value }))}
                             placeholder="Ex: cliente vai analisar em família e retomar contacto na próxima semana."
                             className={`${rtInp} w-full`}
                             data-testid="rt-refuse-reason" />
                </label>
                <div className="text-[11px] text-[hsl(var(--danger))]">
                  O lead fica marcado como <b>Aguarda Retorno</b> e o motivo é adicionado como nota no Zoho.
                </div>
              </div>
            )}
          </div>
          );
          })()}
          <DialogFooter>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => setRtDialogOpen(false)}
                       className="px-4 py-2 text-sm rounded-full bg-muted hover:bg-muted border border-border text-muted-foreground"
                       data-testid="rt-dialog-cancel">
                Cancelar
              </button>
              <button type="button" onClick={saveRtDecision} disabled={rtDialogSaving || rtDialogDraft.accepted == null}
                       className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full bg-foreground hover:bg-[#1E293B] text-background font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                       data-testid="rt-dialog-save">
                {rtDialogSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                Guardar decisão
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Editor de textos IA da proposta --- */}
      <Dialog open={propEditOpen} onOpenChange={setPropEditOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="proposal-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar textos da proposta</DialogTitle>
            <DialogDescription>
              Estes textos foram gerados por IA e podem ser ajustados manualmente. As alterações refletem-se de imediato na proposta pública e no PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {[
              { key: "personal_message", label: "Mensagem pessoal (hero)", rows: 3,
                aiContext: "Mensagem inicial curta e calorosa para a família cliente, incluída no hero da proposta." },
              { key: "executive_summary", label: "Resumo executivo", rows: 4,
                aiContext: "Resumo executivo da proposta Caring (2-4 frases, consultivo)." },
              { key: "needs_understanding", label: "Compreensão das necessidades", rows: 4,
                aiContext: "Parágrafo que demonstra que compreendemos as necessidades específicas do cliente." },
              { key: "recommended_service", label: "Serviço recomendado", rows: 3,
                aiContext: "Descrição breve do serviço recomendado para este cliente." },
              { key: "commercial_terms", label: "Condições comerciais (mostrado antes da aceitação)", rows: 8,
                hint: "IMPORTANTE: Se a Caring está isenta de IVA, garanta que não menciona taxas de IVA aqui.",
                aiContext: "Condições comerciais da proposta Caring. Não mencionar IVA se a empresa está isenta." },
              { key: "additional_notes", label: "Notas adicionais", rows: 3,
                aiContext: "Notas ou avisos complementares para o cliente." },
            ].map((f) => (
              <div key={f.key} className="space-y-1.5" data-testid={`prop-edit-row-${f.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                    {f.label}
                  </label>
                  <button type="button"
                           onClick={() => improvePropField(f.key, f.aiContext)}
                           disabled={propEditAiLoading[f.key] || !(propEditDraft[f.key] || "").trim()}
                           className="inline-flex items-center gap-1.5 text-[11px] rounded-full bg-primary/15 hover:bg-primary/25 border border-primary/40 text-[hsl(var(--warning))] px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                           data-testid={`prop-edit-improve-${f.key}`}>
                    {propEditAiLoading[f.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Melhorar com IA
                  </button>
                </div>
                <textarea
                  value={propEditDraft[f.key] || ""}
                  onChange={(e) => setPropField(f.key, e.target.value)}
                  rows={f.rows}
                  className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 resize-y"
                  data-testid={`prop-edit-input-${f.key}`}
                />
                <PtFormalityHint
                  text={propEditDraft[f.key]}
                  onFix={(fixed) => setPropField(f.key, fixed)}
                />
                {f.hint && <div className="text-[11px] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 rounded px-2 py-1">{f.hint}</div>}
              </div>
            ))}
            {/* Vantagens Caring — lista */}
            <div className="space-y-1.5" data-testid="prop-edit-row-caring_advantages">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                Vantagens Caring (uma por linha)
              </label>
              <textarea
                value={Array.isArray(propEditDraft.caring_advantages) ? propEditDraft.caring_advantages.join("\n") : (propEditDraft.caring_advantages || "")}
                onChange={(e) => setPropField("caring_advantages", e.target.value)}
                rows={6}
                placeholder="Uma vantagem por linha..."
                className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 resize-y"
                data-testid="prop-edit-input-caring_advantages"
              />
              <div className="text-[11px] text-muted-foreground">
                Cada linha vira uma vantagem no bloco "Porque a Caring".
              </div>
            </div>
          </div>
          <DialogFooter>
            <div className="flex items-center justify-end gap-2 w-full">
              <button onClick={() => setPropEditOpen(false)}
                       className="text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2"
                       data-testid="prop-edit-cancel">Cancelar</button>
              <button onClick={saveProposalEdit} disabled={propEditSaving}
                       className="inline-flex items-center gap-1.5 text-xs bg-foreground hover:bg-[#1E293B] text-background font-semibold rounded-full px-4 py-2 disabled:opacity-40"
                       data-testid="prop-edit-save">
                {propEditSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Guardar textos
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EstimateSimulator
        open={simulatorOpen}
        onClose={() => setSimulatorOpen(false)}
        lead={lead}
        onValidated={load}
      />
      {/* 04/08/2026 — Estimate version history modal */}
      {historyOpen && (
        <EstimateHistoryModal
          history={lead.estimate_history || []}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Modal listing all estimate versions dispatched to the client, chronological
 * (most recent first). Each row shows the primary option's monthly value,
 * hours/day × days/week, who sent it and when.
 */
function EstimateHistoryModal({ history, onClose }) {
  const fmt = (n) => (Number(n) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sorted = [...history].sort((a, b) => (b.sent_at || "").localeCompare(a.sent_at || ""));
  // Highlight class for a cell whose value differs from the previous
  // chronological version. Amarelo suave para não competir com o card
  // verde da versão actual (`bg-[hsl(var(--success-bg))]`).
  const chg = "bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] rounded px-1.5 py-0.5";
  // Given `sorted` (desc), the previous chronological version is at
  // idx+1. On the oldest entry we compare against nothing → no diff.
  const changed = (curr, prev, key) => {
    if (!prev) return false;
    const a = curr?.primary?.[key];
    const b = prev?.primary?.[key];
    return a !== b && !(a == null && b == null);
  };
  return (
    <div className="fixed inset-0 z-[80] bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="estimate-history-modal">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-muted/40 to-card">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">Histórico de estimativas</div>
            <div className="font-display text-lg text-foreground mt-0.5">
              {(() => {
                const sentCount = history.filter(h => (h.reason || "sent") === "sent").length;
                const regenCount = history.filter(h => (h.reason || "sent") === "regen").length;
                const parts = [];
                if (sentCount > 0) parts.push(`${sentCount} ${sentCount === 1 ? "envio" : "envios"} ao cliente`);
                if (regenCount > 0) parts.push(`${regenCount} ${regenCount === 1 ? "regeração" : "regerações"}`);
                return parts.length ? parts.join(" · ") : `${history.length} versão(ões)`;
              })()}
            </div>
            {history.length > 1 && (
              <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5">
                <span className={chg}>&nbsp;</span>
                <span>a amarelo: valores que mudaram vs a versão anterior</span>
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="history-close-btn" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5 space-y-3">
          {sorted.map((h, idx) => {
            const isLatest = idx === 0;
            const isPontual = (h.primary?.mode || h.continuity_type) === "pontual";
            const prev = sorted[idx + 1] || null;
            // Diff cell key used both for the big money value (monthly
            // ou total) and the smaller stats row.
            const moneyKey = isPontual ? "total_value" : "monthly_avg";
            const changedMoney = changed(h, prev, moneyKey);
            const changedHpd   = changed(h, prev, "hours_per_day");
            const changedDpw   = changed(h, prev, "days_per_week");
            const changedPrice = changed(h, prev, "hourly_price");
            const changedOpts  = prev && (h.options || []).length !== (prev.options || []).length;
            const changedName  = prev && (h.primary?.name || "") !== (prev.primary?.name || "");
            return (
              <div
                key={h.id || idx}
                className={`rounded-xl border p-4 ${isLatest ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-muted/40 border-border"}`}
                data-testid={`history-entry-${idx}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-heading text-muted-foreground">
                        Versão {sorted.length - idx}{isLatest ? " · actual" : ""}
                      </span>
                      {isLatest && <span className="text-[9px] bg-emerald-600 text-primary-foreground px-1.5 py-0.5 rounded-full font-heading uppercase tracking-wider">Mais recente</span>}
                      {(() => {
                        const reason = h.reason || "sent";
                        if (reason === "regen") {
                          return (
                            <span className="text-[9px] bg-muted text-foreground px-1.5 py-0.5 rounded-full font-heading uppercase tracking-wider" data-testid={`history-badge-regen-${idx}`}>
                              Regerada · não enviada
                            </span>
                          );
                        }
                        if (reason === "adjusted") {
                          return (
                            <span className="text-[9px] bg-amber-200 text-[hsl(var(--warning))] px-1.5 py-0.5 rounded-full font-heading uppercase tracking-wider" data-testid={`history-badge-adjusted-${idx}`}>
                              Ajustada (RT)
                            </span>
                          );
                        }
                        return (
                          <span className="text-[9px] bg-[hsl(var(--info-bg))] text-[hsl(var(--info))] px-1.5 py-0.5 rounded-full font-heading uppercase tracking-wider" data-testid={`history-badge-sent-${idx}`}>
                            Enviada ao cliente
                          </span>
                        );
                      })()}
                    </div>
                    <div className="font-display text-sm text-foreground mt-1" data-testid={changedName ? `history-changed-name-${idx}` : undefined}>
                      <span className={changedName ? chg : ""}>{h.primary?.name || "—"}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className="font-display text-2xl text-foreground tabular-nums inline-block"
                      data-testid={changedMoney ? `history-changed-money-${idx}` : undefined}
                    >
                      <span className={changedMoney ? chg : ""}>
                        {fmt(h.primary?.[moneyKey])} €
                      </span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading">
                      {isPontual ? "Total do serviço" : "Média mensal"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-muted-foreground">
                  <div data-testid={changedHpd ? `history-changed-hpd-${idx}` : undefined}>
                    <b className={`text-foreground tabular-nums ${changedHpd ? chg : ""}`}>{h.primary?.hours_per_day ?? "—"}</b> h/dia
                  </div>
                  <div data-testid={changedDpw ? `history-changed-dpw-${idx}` : undefined}>
                    <b className={`text-foreground tabular-nums ${changedDpw ? chg : ""}`}>{h.primary?.days_per_week ?? "—"}</b> dias/sem
                  </div>
                  <div data-testid={changedPrice ? `history-changed-price-${idx}` : undefined}>
                    <b className={`text-foreground tabular-nums ${changedPrice ? chg : ""}`}>{fmt(h.primary?.hourly_price)}</b> €/h
                  </div>
                  <div data-testid={changedOpts ? `history-changed-opts-${idx}` : undefined}>
                    <b className={`text-foreground tabular-nums ${changedOpts ? chg : ""}`}>{(h.options || []).length}</b> opções
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground flex items-center justify-between gap-2 flex-wrap">
                  <span>Enviado por <b className="text-foreground">{h.sent_by_user_email || "—"}</b></span>
                  <span>Para <b className="text-foreground">{h.sent_to_email || "—"}</b></span>
                  <span className="tabular-nums">{h.sent_at ? new Date(h.sent_at).toLocaleString("pt-PT") : "—"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}



const AI_HINT_EXAMPLES = [
  "Cliente é engenheiro reformado — usar registo formal e tecnicidade calibrada.",
  "Filha vive no estrangeiro e é o principal decisor — priorizar comunicação assíncrona.",
  "Já teve má experiência com outro fornecedor — valorizar acompanhamento próximo.",
  "Cliente tem restrições religiosas alimentares — evitar exemplos alimentares.",
];

/**
 * Section that lets the manager persist a free-text hint used by every
 * AI generation for this lead (proposta, plano de cuidados, emails,
 * resumo para Zoho). Auto-saves 800ms after the user stops typing.
 */
function AIHintSection({ lead, onSaved }) {
  const [value, setValue] = useState(lead?.ai_hint || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValue(lead?.ai_hint || "");
    setDirty(false);
  }, [lead?.id, lead?.ai_hint]);

  // Debounced auto-save.
  useEffect(() => {
    if (!dirty) return;
    const handle = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.post(`/leads/${lead.id}/ai-hint`, { ai_hint: value });
        onSaved && onSaved(data);
        setDirty(false);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Falha ao guardar contexto");
      } finally { setSaving(false); }
    }, 800);
    return () => clearTimeout(handle);
  }, [value, dirty, lead?.id, onSaved]);

  const insertExample = (ex) => {
    const trimmed = (value || "").trim();
    const next = trimmed ? `${trimmed}\n${ex}` : ex;
    setValue(next);
    setDirty(true);
  };

  return (
    <Section
      title={
        <span className="inline-flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-foreground" />
          Contexto para a IA
          {value.trim() && !dirty && (
            <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[hsl(var(--success-bg))] text-[#059669] border border-[hsl(var(--success))]/40 font-semibold">
              Activo
            </span>
          )}
        </span>
      }
      action={
        <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-2" data-testid="ai-hint-status">
          {saving ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> A guardar…</>
          ) : dirty ? (
            <>Alterações por guardar</>
          ) : value.trim() ? (
            <><CheckCircle2 className="w-3.5 h-3.5 text-[#059669]" /> Guardado</>
          ) : (
            <>Vazio</>
          )}
        </div>
      }
    >
      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
        Instruções livres que a IA vai receber como <b>autoritárias</b> sempre que gerar conteúdo
        para este lead (proposta, plano de cuidados, emails, resumo Zoho). Use para dar contexto que
        os dados brutos não capturam — tom, factos que o gestor descobriu na chamada, restrições
        específicas, etc.
      </p>
      <textarea
        data-testid="lead-ai-hint-textarea"
        value={value}
        onChange={(e) => { setValue(e.target.value); setDirty(true); }}
        placeholder="Ex.: Cliente insiste em comunicação por WhatsApp; a filha (Ana) é a decisora efectiva; sensível ao preço mas valoriza estabilidade..."
        rows={4}
        maxLength={1500}
        className="w-full bg-card border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-[#C4B99B] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#3B82F6]/30 resize-y min-h-[100px] font-normal leading-relaxed"
      />
      <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] text-muted-foreground">
          {value.length}/1500 · guarda automaticamente após 0.8s de inatividade
        </div>
        {value.trim() && (
          <button
            type="button"
            onClick={() => { setValue(""); setDirty(true); }}
            className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--danger))] hover:text-[#6E1616] font-semibold"
            data-testid="lead-ai-hint-clear"
          >
            Limpar
          </button>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-border/60">
        <div className="text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold mb-2">
          Exemplos rápidos (clique para adicionar)
        </div>
        <div className="flex flex-wrap gap-2">
          {AI_HINT_EXAMPLES.map((ex, i) => (
            <button
              key={i}
              type="button"
              onClick={() => insertExample(ex)}
              className="text-[11px] text-left bg-muted/40 hover:bg-muted border border-[#EBE0C4] rounded-full px-3 py-1.5 text-[#7B5B08] transition-colors"
              data-testid={`ai-hint-example-${i}`}
            >
              <Plus className="w-3 h-3 inline mr-1" />{ex.length > 60 ? ex.slice(0, 57) + "…" : ex}
            </button>
          ))}
        </div>
      </div>
    </Section>
  );
}
